/**
 * ====================================================================================================================
 * HAND HELD LEGEND: custom.js
 * ====================================================================================================================
 *
 * Everything our theme adds on top of Focal's own JavaScript (theme.js stays as Focal ships it). Loaded with "defer"
 * in the <head> of layout/theme.liquid, so the page is parsed when this runs (modules start right away, no
 * DOMContentLoaded). The house style for this file is in docs/THEME-STYLE.md.
 *
 * How it's organized: a few shared helpers (HHL, right below), then one self-contained module per feature, each in
 * its own function and each opened by a comment block saying what it does and where its markup and styles live
 * (styles: assets/custom.css, under the same name). The modules, in order:
 *
 *   Site-wide       color mode toggle, Doran back-in-stock bridge, docked cart, newsletter popup,
 *                   pinned panels' bottom edge, LCD backdrop, section accent color, resize state, pop-up cards,
 *                   in-page glide, page-wide facts, preview bar, quantity boxes
 *   Product cards   quick add, photo cycle, photo glow color, white/black photo blending, card links to variants
 *   Collections     pager, open in place, filter bar and window, per-row switch, item count, builder tile
 *   Product page    Hulk volume discounts, gallery height, buy box, guides & manuals, sticky add to cart, review stars,
 *                   fits per variant, variant status, description fold, pairs well with, photo crops, reviews fold
 *   Add to cart     button feedback and the "Added" tab
 *   Cart            shipping protection (Navidium), order note above the keyboard, checkout notes sheet, build cards,
 *                   store analytics (cart events for Shopify's Customer Events: hhlTrack)
 *   Search          suggestions engine, bottom-bar search (phones), header search (desktop), in-place results,
 *                   search pager and in-stock count
 *   Menus & bars    menu sheet, country picker in the menu sheet, announcement bar, account label, home links
 *                   on the home page (back to the top)
 *   Account         account sheet, signed in or out, newsletter consent, which form was sent, rewards page
 *   Policies        policy module, policy center and its sections
 *
 * Focal's events this file relies on (fired on document.documentElement unless noted):
 *   cart:refresh        re-render the cart (detail.cart optional; detail.openMiniCart opens the drawer)
 *   cart:updated        the cart changed (detail.cart)
 *   variant:added       a product form added an item (on the form)
 *   variant:changed     a product form picked another variant (detail.variant)
 *   cart-notification:show   show the "added to cart" message (detail.status "success" or "error")
 * Our own events:
 *   hhl:page-swapped    a collection opened in place (on #main)
 *   hhl:sheet-open      a sheet opened (detail: 'cart', 'menu', 'search', 'desk-search', 'searching', 'account', or
 *                       'home' when a home link closes them all),
 *                       so the others close
 *   hhl:cart-shown      on wide screens, the cart in its column was shown instead of opening a drawer (on #mini-cart;
 *                       detail 'docked' or 'peek')
 *   hhl:pairs-changed   a "pairs well with" card changed its pick (on the card)
 *   hhl:cart-redrawn    the theme just replaced the cart drawer's inside (on #mini-cart, from hhl-cart-motion.js), before
 *                       the new layout is measured or painted
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * HHL: SHARED HELPERS
 * ------------------------------------------------------------------------------------------------------------
 * Small helpers the modules share, so each of these is done one way everywhere: prices, store links, the cart,
 * screen-size queries, writing text only when it changes, opening the cart, and the theme's motion speed and easing.
 */
var HHL = (function () {
  // The shop's current currency (the country picker's), and its root path (it differs per market language)
  var currency = function () {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  };

  var root = function () {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  };

  var cartUrl = function () {
    return (window.themeVariables && themeVariables.routes && themeVariables.routes.cartUrl) || root() + 'cart';
  };

  // A price in cents, in the shop's currency and the page's language
  var money = function (cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {style: 'currency', currency: currency()}).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // The cart as JSON (never from the browser cache), and Focal's re-render of the cart drawer, count and page
  var fetchCart = function () {
    return fetch(cartUrl() + '.js', {cache: 'no-store'}).then(function (response) { return response.json(); });
  };

  var refreshCart = function (detail) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true, detail: detail}));
  };

  // The country list is in the page once (<template id="hhl-country-choices">, layout/theme.liquid) and copied into
  // every picker ([data-hhl-country-choices]) the first time one may be opened ("Country lists" below)
  var countriesFilled = false;
  var fillCountries = function () {
    var template = document.getElementById('hhl-country-choices');
    if (countriesFilled || !template) return;
    countriesFilled = true;
    document.querySelectorAll('[data-hhl-country-choices]').forEach(function (list) {
      if (!list.firstElementChild) list.appendChild(template.content.cloneNode(true));
    });
  };

  // Navidium's shipping protection item (it's shown as a line above Checkout, never as a cart item)
  var isProtectionItem = function (item) {
    return /navidium/i.test(item.handle || '') || /shipping protection/i.test(item.product_title || '');
  };

  // Writes text only when it changes (several modules watch the page for changes; this keeps them from looping)
  var setText = function (element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  };

  // The breakpoints the theme's layout changes at (custom.css uses the same ones)
  var mq = {
    phone: window.matchMedia('(max-width: 740px)'),         // phones: bottom sheets, compact cards and cart
    tablet: window.matchMedia('(max-width: 999px)'),        // below the desktop header: bottom-bar search
    sheetMenu: window.matchMedia('(max-width: 1199px)'),    // below the desktop sidebar: the menu is a sheet
    docked: window.matchMedia('(min-width: 1600px)'),       // the cart docks beside the page
    hover: window.matchMedia('(hover: hover)'),             // a real pointer (touch screens keep taps "hovered")
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
  };

  // How fast the theme moves (Theme settings > Motion, read from --hhl-motion: 1 is normal) and its standard easing
  // (the same curve as --hhl-ease), for animations run from script
  var motion = function () {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hhl-motion')) || 1;
  };

  var ease = 'cubic-bezier(.2, 0, 0, 1)';

  // Runs fn when a media query starts or stops matching (older Safari only has addListener)
  var onChange = function (query, fn) {
    if (query.addEventListener) {
      query.addEventListener('change', fn);
    } else if (query.addListener) {
      query.addListener(fn);
    }
  };

  // Opens the cart drawer (where the cart docks beside the page, that gives it a short highlight instead)
  var openCart = function () {
    var drawer = document.getElementById('mini-cart');
    if (!drawer) return false;
    drawer.open = true;
    return true;
  };

  // Bottom sheets (phones: the menu, the cart, the account sheet): drag the head (the handle strip) down to close.
  // While dragging, the sheet gets the class is-dragging and --hhl-sheet-drag (how far down), which its CSS follows;
  // past 90px it closes (close()), otherwise it eases back. Buttons and fields in the head still work as usual.
  // when (optional): a media query; the drag only works while it matches (the menu is a side panel from 1000px)
  var sheetDrag = function (head, sheet, close, when) {
    if (!head || head.hhlDrag) return;
    head.hhlDrag = true;
    var startY = null;
    head.addEventListener('pointerdown', function (event) {
      if (event.button > 0 || (when && !when.matches)) return;
      var origin = event.composedPath ? event.composedPath()[0] : event.target;
      if (origin.closest && origin.closest('button, input, a')) return;
      event.preventDefault(); // no text selection while dragging
      startY = event.clientY;
      sheet.classList.add('is-dragging');
      try { head.setPointerCapture(event.pointerId); } catch (e) {}
    });
    head.addEventListener('pointermove', function (event) {
      if (startY === null) return;
      sheet.style.setProperty('--hhl-sheet-drag', Math.max(0, event.clientY - startY) + 'px');
    });
    var end = function (event) {
      if (startY === null) return;
      var moved = event.clientY - startY;
      startY = null;
      sheet.classList.remove('is-dragging');
      if (moved > 90) {
        close();
        // Let it leave from where it was dropped, then forget the offset
        setTimeout(function () { sheet.style.removeProperty('--hhl-sheet-drag'); }, 450);
      } else {
        sheet.style.removeProperty('--hhl-sheet-drag');
      }
    };
    head.addEventListener('pointerup', end);
    head.addEventListener('pointercancel', end);
  };

  return {
    currency: currency, root: root, cartUrl: cartUrl, money: money, fetchCart: fetchCart, refreshCart: refreshCart,
    isProtectionItem: isProtectionItem, setText: setText, fillCountries: fillCountries, mq: mq, onChange: onChange, openCart: openCart,
    sheetDrag: sheetDrag, motion: motion, ease: ease
  };
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * HHL.keyboardLift: FOLLOWING THE ON-SCREEN KEYBOARD
 * ------------------------------------------------------------------------------------------------------------
 * Phones' on-screen keyboards cover the bottom of the page without resizing it, so parts pinned to the bottom (the
 * bottom-bar search, the cart's order note box) must rise above it while they're in use. This measures how far the
 * keyboard covers a part's bottom edge and reports it, steadily:
 *   - Chromium browsers (Android Chrome, Samsung Internet, Brave) report the keyboard's own position, which is exact
 *     (their visible-area size can be tens of pixels short): while in use, the keyboard is set to overlay the page and
 *     the part sits on its reported top edge. Other browsers (iOS Safari) use the visible area.
 *   - The part's resting edge comes from its layout box, which its lift doesn't move, so measuring mid-slide is right.
 *   - Changes of a few pixels are ignored, and a drop (the keyboard briefly reported short or gone as it animates or
 *     focus moves) only counts if it's still there a moment later.
 *   - The keyboard slides in over a few hundred milliseconds and some browsers report it once, so it measures again as
 *     it settles. Nothing runs while no part is in use.
 * The part's own CSS slides it to the reported height (on the graphics layer) and keeps a strip of its background
 * below it to cover any small mismatch.
 *
 * Usage: var lift = HHL.keyboardLift({bottom: function () { return px; }, apply: function (px) { ... }});
 *        lift.start() when the part comes into use (before focusing its field), lift.stop() when it's done.
 */
HHL.keyboardLift = function (options) {
  var viewport = window.visualViewport,
    keyboard = 'virtualKeyboard' in navigator ? navigator.virtualKeyboard : null,
    active = false, lift = 0, overlaid = false, dropTimer = null, settleTimers = [];

  var set = function (value) {
    lift = value;
    options.apply(value);
  };

  var measure = function (confirmed) {
    if (!active) return;

    var restingBottom = options.bottom(), covered = 0;

    if (keyboard && keyboard.overlaysContent) {
      var keys = keyboard.boundingRect;
      covered = keys.height > 0 ? Math.max(0, restingBottom - keys.y) : 0;
    } else if (viewport) {
      covered = Math.max(0, restingBottom - (viewport.offsetTop + viewport.height));
    }

    covered = Math.round(covered < 4 ? 0 : covered);

    if (covered === lift || (covered > 0 && lift > 0 && Math.abs(covered - lift) < 6)) {
      clearTimeout(dropTimer);
      dropTimer = null;
      return;
    }

    if (covered < lift && confirmed !== true) {
      if (!dropTimer) dropTimer = setTimeout(function () { dropTimer = null; measure(true); }, 260);
      return;
    }

    clearTimeout(dropTimer);
    dropTimer = null;
    set(covered);
  };

  var settle = function () {
    if (!active) return;
    requestAnimationFrame(function () { requestAnimationFrame(function () { measure(); }); });
    settleTimers.forEach(clearTimeout);
    settleTimers = [150, 350, 700].map(function (delay) { return setTimeout(function () { measure(); }, delay); });
  };

  if (keyboard) keyboard.addEventListener('geometrychange', settle);
  if (viewport) {
    viewport.addEventListener('resize', settle);
    viewport.addEventListener('scroll', settle);
  }
  window.addEventListener('resize', settle);

  return {
    start: function () {
      active = true;
      if (keyboard && !keyboard.overlaysContent) {
        keyboard.overlaysContent = true;
        overlaid = true;
      }
      settle();
    },

    stop: function () {
      active = false;
      clearTimeout(dropTimer);
      dropTimer = null;
      settleTimers.forEach(clearTimeout);
      if (overlaid && keyboard) {
        keyboard.overlaysContent = false;
        overlaid = false;
      }
      set(0);
    }
  };
};

/**
 * ------------------------------------------------------------------------------------------------------------
 * HAND HELD LEGEND: COLOR MODE TOGGLE (DARK MODE)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The color mode itself is applied by the inline script located in the <head> of "theme.liquid", which exposes
 * window.HHLTheme (it only exists when dark mode is enabled in the theme settings). This code handles the
 * header toggle, which cycles the visitor preference: system -> light -> dark.
 */
(function () {
  var HHLTheme = window.HHLTheme;

  if (!HHLTheme) {
    return; // Dark mode is disabled
  }

  var root = document.documentElement;

  function currentMode() {
    var mode = root.getAttribute('data-theme-pref');

    return HHLTheme.modes.indexOf(mode) !== -1 ? mode : HHLTheme.get();
  }

  // Keep the accessible label and the tooltip of each toggle in sync with the current mode
  function updateToggles() {
    var mode = currentMode();

    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-theme-toggle]'));
    (window.HHLShadowRoots || []).forEach(function (root) {
      toggles = toggles.concat(Array.prototype.slice.call(root.querySelectorAll('[data-theme-toggle]')));
    });

    toggles.forEach(function (toggle) {
      var label = toggle.getAttribute('data-label-' + mode),
        tooltipElement = toggle.querySelector('[data-tooltip]');

      if (label) {
        toggle.setAttribute('aria-label', label);
      }

      if (tooltipElement && tooltipElement.getAttribute('data-tooltip-' + mode)) {
        tooltipElement.setAttribute('data-tooltip', tooltipElement.getAttribute('data-tooltip-' + mode));
      }
    });
  }

  document.addEventListener('click', function (event) {
    // composedPath: the toggle may be inside the phone account sheet (a shadow root)
    var origin = event.composedPath ? event.composedPath()[0] : event.target;
    var toggle = origin instanceof Element ? origin.closest('[data-theme-toggle]') : null;

    if (!toggle) {
      return;
    }

    var modes = HHLTheme.modes;

    HHLTheme.set(modes[(modes.indexOf(currentMode()) + 1) % modes.length]);
    updateToggles();
  });

  // Follow the device setting while the preference is "system"
  if (window.matchMedia) {
    var mediaQuery = window.matchMedia('(prefers-color-scheme: dark)'),
      onSystemChange = function () {
        if (currentMode() === 'system') {
          HHLTheme.apply('system');
        }
      };

    HHL.onChange(mediaQuery, onSystemChange);
  }

  // Keep the other tabs in sync
  window.addEventListener('storage', function (event) {
    if (event.key === HHLTheme.key || event.key === null) {
      HHLTheme.apply(HHLTheme.get());
      updateToggles();
    }
  });

  // The header can be re-rendered by the theme editor
  document.addEventListener('shopify:section:load', updateToggles);

  updateToggles();
})();

/**
 * Country lists: the pickers' country lists are filled from the page's one copy (HHL.fillCountries) on the shopper's
 * first interaction (a tap, a key, focus moving), which always comes before a picker can open, or once the page is
 * idle after loading, whichever is first.
 */
(function () {
  var fill = function () {
    ['pointerdown', 'keydown', 'focusin'].forEach(function (type) { document.removeEventListener(type, fill, true); });
    HHL.fillCountries();
  };
  ['pointerdown', 'keydown', 'focusin'].forEach(function (type) { document.addEventListener(type, fill, true); });
  window.addEventListener('load', function () {
    (window.requestIdleCallback || function (callback) { setTimeout(callback, 2000); })(fill, {timeout: 4000});
  }, {once: true});
})();

/**
 * Doran Back in Stock: tell the app when the shopper picks another variant. Does nothing if the app is not loaded.
 */
document.addEventListener('variant:changed', function (event) {
  if (!window.$bpDoranInit || !event.detail.variant) {
    return;
  }

  document.dispatchEvent(new CustomEvent('DoranBpFormChange', {
    detail: {variantId: event.detail.variant.id, productId: window.$bpDoranInit.productId}
  }));
});

/**
 * Docked cart: from 1600px the mini-cart is shown open beside the page (see "Docked cart" in custom.css), but only
 * while it has items: an empty cart stays out of the way and slides in when something is added. While docked,
 * requests to open it as a drawer (cart icon, add to cart) give it a short highlight instead, so the page is never
 * locked behind an overlay.
 */
(function () {
  var cart = document.getElementById('mini-cart'),
    main = document.querySelector('[data-cart-dock]');

  if (!cart || !main) {
    return;
  }

  var wide = HHL.mq.docked,
    docked = {get matches() { return wide.matches && main.classList.contains('main--with-cart'); }},
    proto = Object.getPrototypeOf(cart),
    descriptor = null;

  while (proto && !descriptor) {
    descriptor = Object.getOwnPropertyDescriptor(proto, 'open');
    proto = Object.getPrototypeOf(proto);
  }

  if (!descriptor) {
    return;
  }

  Object.defineProperty(cart, 'open', {
    configurable: true,
    get: function () {
      return descriptor.get.call(this);
    },
    set: function (value) {
      // Wide screens: the cart lives in its column. With items it is already shown (a highlight instead); with an
      // empty cart the icon shows or hides it there (peek) rather than opening an overlay drawer
      if (wide.matches) {
        if (docked.matches) {
          if (value) {
            cart.classList.remove('is-nudged');
            void cart.offsetWidth;
            cart.classList.add('is-nudged');
            cart.dispatchEvent(new CustomEvent('hhl:cart-shown', {detail: 'docked'}));
          }
        } else {
          cart.classList.toggle('is-peek', !!value && !cart.classList.contains('is-peek'));
          if (cart.classList.contains('is-peek')) cart.dispatchEvent(new CustomEvent('hhl:cart-shown', {detail: 'peek'}));
        }
        return;
      }

      descriptor.set.call(this, value);
    }
  });

  cart.addEventListener('animationend', function () {
    cart.classList.remove('is-nudged');
  });

  // Growing the window past the breakpoint while the drawer is open: close it so the overlay and scroll lock go away
  var onChange = function () {
    if (docked.matches && cart.hasAttribute('open')) {
      descriptor.set.call(cart, false);
    }
  };

  HHL.onChange(wide, onChange);

  // Dock while the cart has items: follow every cart change (add, remove, quantity)
  var setCount = function (count) {
    var hadItems = main.classList.contains('main--with-cart');
    main.classList.toggle('main--with-cart', count > 0);
    document.body.classList.toggle('hhl-cart-filled', count > 0); // the CSS reads it there (custom.css "Page-wide facts")

    if (count > 0 && !hadItems && cart.hasAttribute('open')) {
      descriptor.set.call(cart, false);
    }

    if (count > 0) {
      cart.classList.remove('is-peek');
    }
  };

  var onCartChange = function (event) {
    var detailCart = event.detail && event.detail.cart;

    if (detailCart && typeof detailCart.item_count === 'number') {
      setCount(detailCart.item_count);
    } else {
      HHL.fetchCart()
        .then(function (data) { setCount(data.item_count); })
        .catch(function () {});
    }
  };

  document.documentElement.addEventListener('cart:updated', onCartChange);
  document.documentElement.addEventListener('cart:refresh', onCartChange);
})();

/**
 * Quick add on product cards (snippets/quick-variants.liquid): the options button opens a dialog with a large
 * preview; choosing an option shows it (hovering does not), and Add to cart adds it (the panel stays open) and refreshes the cart
 * (docked cart, count, notification) with the same events the theme's product form uses.
 */
(function () {
  if (window.customElements.get('quick-variants')) {
    return;
  }

  var addLabel = null;

  class QuickVariants extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.quick-variants__dialog');
      this.image = this.querySelector('[data-quick-variants-image]');
      this.nameEl = this.querySelector('[data-quick-variants-name]');
      this.priceEl = this.querySelector('[data-quick-variants-price]');
      this.addButton = this.querySelector('[data-quick-variants-add]');
      this.addLabelEl = this.querySelector('[data-quick-variants-add-label]');
      this.selected = this.querySelector('.quick-variants__option[aria-pressed="true"]');

      // Set up once (the element can be moved, which connects it again)
      if (this._ready) return;
      this._ready = true;

      this.addEventListener('click', this._onClick.bind(this));

      addLabel = addLabel || (this.selected && this.selected.dataset.available === 'true' ? this.addLabelEl.textContent.trim() : null);

      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('quick-variants-open'));
    }

    open() {
      document.documentElement.classList.add('quick-variants-open');
      this._fitNote();
      this.dialog.showModal();

      if (window.HHLPhotoColor && this.image) {
        HHLPhotoColor.apply(this.dialog, '--preview-glow', this.image.dataset.current || this.image.currentSrc || this.image.src);
      }

      (this.selected || this.addButton).scrollIntoView({block: 'nearest'});
    }

    close() {
      this.dialog.close();
    }

    // HHL (owner 2026-10-26: "Show the note in the add step"): on a "Verified compatible parts" tile with a fit note,
    // the note shows above Add to cart, saying which part it is about ("With the RetroGlow: …"). Not a step to confirm.
    _fitNote() {
      var footer = this.querySelector('.quick-variants__footer');
      if (!footer) return;
      var old = footer.querySelector('[data-quick-variants-fitnote]');
      if (old) old.remove();
      var slide = this.closest('[data-hhl-pairs-slide]');
      var note = slide && Array.prototype.filter.call(slide.querySelectorAll('[data-hhl-tile-note]'), function (n) { return !n.hidden; })[0];
      if (!note) return;
      var copy = note.cloneNode(true);
      copy.classList.remove('hhl-fitnote--chip', 'label', 'is-new');
      copy.removeAttribute('tabindex');
      copy.removeAttribute('data-hhl-tile-note');
      copy.removeAttribute('data-hhl-fitnote');
      copy.setAttribute('data-quick-variants-fitnote', '');
      var tag = copy.querySelector('.hhl-fitnote__tag');
      if (tag) tag.remove();
      var block = slide.closest('[data-hhl-pairs]');
      var page = block && block.getAttribute('data-hhl-page-name');
      var text = copy.querySelector('.hhl-fitnote__text');
      if (page && text) text.textContent = 'With the ' + page + ': ' + text.textContent;
      footer.prepend(copy);
    }

    _onClick(event) {
      if (event.target.closest('.quick-variants__details, .quick-variants__more')) {
        return;
      }

      // Clicks on the dimmed backdrop land on the <dialog> itself
      if (event.target === this.dialog) {
        this.close();
        return;
      }

      if (event.target.closest('[data-quick-variants-toggle]')) {
        event.preventDefault();
        this.open();
      } else if (event.target.closest('[data-quick-variants-close]')) {
        this.close();
      } else if (event.target.closest('[data-quick-variants-add]')) {
        this._add();
      } else {
        var option = event.target.closest('.quick-variants__option');

        if (option) {
          this._select(option);
        }
      }
    }

    _select(option) {
      if (this.selected) {
        this.selected.setAttribute('aria-pressed', 'false');
      }

      this.selected = option;
      option.setAttribute('aria-pressed', 'true');
      this._show(option);

      var available = option.dataset.available === 'true';
      // A choice made: the "Choose a size" ask is done
      this.addButton.removeAttribute('data-choose');
      var prompt = this.querySelector('[data-quick-variants-prompt]');
      if (prompt) prompt.classList.remove('is-shown');
      // Sold out but coming back: "Notify me" (to its page, where the back-in-stock form opens); discontinued: "Sold out"
      var notify = !available && option.dataset.notify;
      if (notify) this.addButton.setAttribute('data-notify', notify); else this.addButton.removeAttribute('data-notify');
      this.addButton.disabled = !available && !notify;
      this.addLabelEl.textContent = available ? (addLabel || 'Add to cart') : notify ? 'Notify me' : option.querySelector('.quick-variants__meta').textContent;
    }

    // Show an option's photo, name and price in the panel header
    _show(option) {
      if (!option) {
        return;
      }

      this.nameEl.textContent = option.dataset.name;
      this.priceEl.textContent = option.dataset.price;

      // The variant's status badge ("Clearance"...), when the product has any (snippets/hhl-variant-status.liquid)
      var status = this.querySelector('[data-hhl-variant-status]');
      if (status && HHL.variantStatus) {
        try { HHL.variantStatus(status, JSON.parse(status.getAttribute('data-hhl-variant-status'))[option.dataset.variantId]); } catch (e) {}
      }

      if (this.image && option.dataset.image && this.image.dataset.current !== option.dataset.image) {
        this._crossFade(option.dataset.image);
      }
    }

    // Load the next photo on top of the current one, fade it in, then drop the old one
    _crossFade(src) {
      var current = this.image,
        next = current.cloneNode(false);

      next.removeAttribute('srcset');
      next.removeAttribute('sizes');
      next.removeAttribute('loading');
      next.dataset.current = src;
      next.classList.add('is-entering');
      this.image = next;

      var reveal = () => {
        if (this.image !== next) {
          next.remove();
          return;
        }

        current.parentNode.appendChild(next);
        requestAnimationFrame(() => next.classList.remove('is-entering'));
        setTimeout(() => {
          this.querySelectorAll('[data-quick-variants-image]').forEach((image) => image !== next && image.remove());
        }, 450);
      };

      next.src = src;
      (next.decode ? next.decode() : Promise.resolve()).then(reveal, reveal);
      window.HHLPhotoColor && HHLPhotoColor.apply(this.dialog, '--preview-glow', src);
    }

    async _add() {
      var option = this.selected;
      var variantId = option && option.dataset.variantId;

      if (this.addButton.hasAttribute('data-notify')) {
        window.location.href = this.addButton.getAttribute('data-notify');
        return;
      }

      // Nothing chosen yet (no filters, several choices): ask for one by the list instead of adding
      if (!variantId && this.addButton.hasAttribute('data-choose')) {
        var prompt = this.querySelector('[data-quick-variants-prompt]');
        if (prompt) {
          HHL.setText(prompt, this.addButton.getAttribute('data-choose'));
          prompt.classList.remove('is-shown');
          prompt.getBoundingClientRect();
          prompt.classList.add('is-shown');
        }
        var list = this.querySelector('.quick-variants__list');
        if (list) list.scrollIntoView({block: 'nearest', behavior: HHL.mq.reducedMotion.matches ? 'auto' : 'smooth'});
        return;
      }

      if (!variantId || this.addButton.disabled || this.addButton.hasAttribute('aria-busy')) {
        return;
      }

      var routes = window.themeVariables.routes,
        settings = window.themeVariables.settings,
        body = new FormData();

      body.append('id', variantId);
      body.append('quantity', '1');
      body.append('sections', 'mini-cart');

      this.addButton.setAttribute('aria-busy', 'true');

      try {
        var response = await fetch(routes.cartAddUrl + '.js', {
          method: 'POST',
          body: body,
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        });
        var json = await response.json();

        if (response.ok && (settings.cartType === 'page' || settings.pageType === 'cart')) {
          window.location.href = HHL.root() + 'cart';
          return;
        }

        if (response.ok) {
          this.dispatchEvent(new CustomEvent('variant:added', {bubbles: true, detail: {variant: json.hasOwnProperty('items') ? json.items[0] : json}}));

          var cart = await HHL.fetchCart();

          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {bubbles: true, detail: {cart: cart}}));
          cart.sections = json.sections;
          HHL.refreshCart({cart: cart, openMiniCart: settings.cartType === 'drawer'});
        } else {
          HHL.refreshCart();
        }

        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {status: response.ok ? 'success' : 'error', error: json.description || ''}
        }));
      } finally {
        this.addButton.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('quick-variants', QuickVariants);
})();

/**
 * Variant photo cycle on product cards (snippets/product-item.liquid): cards whose variants have their own photos
 * cross-fade from the main photo through each variant photo and back, each card on its own random timer so the
 * grid never changes in lockstep. Only cards on screen move; hovering or focusing a card holds the current photo.
 * The variant photos aren't part of the page: the card lists them (data-hhl-cycle: variant, photo address at 800px,
 * width, height) and the images are made here, their address waiting in data-hhl-src / data-hhl-srcset until the page
 * has loaded and the card comes on screen. The cycle skips any not loaded yet.
 */
(function () {
  if (HHL.mq.reducedMotion.matches || !('IntersectionObserver' in window)) {
    return;
  }

  var MIN_DELAY = 2200,
    MAX_DELAY = 4800,
    tracked = new WeakSet();

  var schedule = function (state) {
    clearTimeout(state.timer);
    state.timer = setTimeout(function () {
      advance(state);
    }, MIN_DELAY + Math.random() * (MAX_DELAY - MIN_DELAY));
  };

  var advance = function (state) {
    if (!state.visible || state.held || document.hidden) {
      state.timer = null;
      return;
    }

    // -1 is the card's main photo (no overlay); skip variant photos that haven't loaded yet
    var images = state.images,
      next = state.index;

    for (var tries = 0; tries <= images.length; tries++) {
      next = next + 1 >= images.length ? -1 : next + 1;

      if (next === -1 || (images[next].complete && images[next].naturalWidth > 0)) {
        break;
      }
    }

    if (state.index >= 0) {
      images[state.index].classList.remove('is-active');
    }

    if (next >= 0) {
      images[next].classList.add('is-active');
    }

    state.index = next;
    schedule(state);
  };

  // The card's variant photos from its list: the same sizes as its main photo, 200 to 800px wide
  var build = function (cycle) {
    var list;
    try { list = JSON.parse(cycle.getAttribute('data-hhl-cycle') || '[]'); } catch (error) { list = []; }
    if (!list.length || cycle.querySelector('img')) return;

    var card = cycle.closest('.product-item'),
      primary = card && card.querySelector('.product-item__primary-image'),
      sizes = primary && primary.getAttribute('sizes');

    list.forEach(function (entry) {
      var url = String(entry[1]).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, ''),
        join = url.indexOf('?') === -1 ? '?' : '&',
        image = document.createElement('img');

      image.className = 'product-item__cycle-image';
      image.alt = '';
      image.loading = 'lazy';
      image.width = entry[2];
      image.height = entry[3];
      if (sizes) image.sizes = sizes;
      image.setAttribute('data-variant-id', entry[0]);
      image.dataset.hhlSrc = url + join + 'width=800';
      image.dataset.hhlSrcset = [200, 300, 400, 500, 600, 700, 800].map(function (width) {
        return url + join + 'width=' + width + ' ' + width + 'w';
      }).join(', ');
      cycle.appendChild(image);
    });
  };

  // The card's variant photos get their real address (after the page's own load, so they never compete with it)
  var pageLoaded = document.readyState === 'complete';
  var wake = function (state) {
    if (state.woken || !pageLoaded) return;
    state.woken = true;
    state.images.forEach(function (image) {
      if (image.dataset.hhlSrcset) image.srcset = image.dataset.hhlSrcset;
      if (image.dataset.hhlSrc) image.src = image.dataset.hhlSrc;
    });
  };

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var state = entry.target._hhlCycle;
      state.visible = entry.isIntersecting;

      if (state.visible) {
        wake(state);
      }

      if (state.visible && !state.timer) {
        schedule(state);
      }
    });
  }, {threshold: .5});

  if (!pageLoaded) {
    window.addEventListener('load', function () {
      pageLoaded = true;
      document.querySelectorAll('.product-item').forEach(function (card) {
        if (card._hhlCycle && card._hhlCycle.visible) wake(card._hhlCycle);
      });
    }, {once: true});
  }

  var setup = function (root) {
    (root || document).querySelectorAll('[data-variant-cycle]').forEach(function (cycle) {
      if (tracked.has(cycle)) {
        return;
      }

      tracked.add(cycle);
      build(cycle);

      var card = cycle.closest('.product-item') || cycle,
        state = {images: Array.from(cycle.querySelectorAll('img')), index: -1, visible: false, held: false, timer: null};

      if (!state.images.length) {
        return;
      }

      card._hhlCycle = state;

      var hold = function () {
        state.held = true;
        clearTimeout(state.timer);
        state.timer = null;
      };
      var release = function () {
        state.held = false;

        if (state.visible && !state.timer) {
          schedule(state);
        }
      };

      card.addEventListener('mouseenter', hold);
      card.addEventListener('mouseleave', release);
      card.addEventListener('focusin', hold);
      card.addEventListener('focusout', release);

      observer.observe(card);
    });
  };

  setup();

  // Collection filters, "load more" and section re-renders add new cards
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1 && (node.matches('[data-variant-cycle]') || node.querySelector('[data-variant-cycle]'))) {
          setup(node.parentNode || node);
        }
      });
    });
  }).observe(document.body, {childList: true, subtree: true});

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      document.querySelectorAll('.product-item').forEach(function (card) {
        var state = card._hhlCycle;

        if (state && state.visible && !state.held && !state.timer) {
          schedule(state);
        }
      });
    }
  });
})();

/**
 * Photo glow: reads a product photo's main color from a tiny copy of it (Shopify's image CDN allows this) and
 * exposes it as "r, g, b" in --preview-glow in the quick add panel. White and
 * near-black pixels (backgrounds, shadows) are ignored so the glow takes the product's own color.
 */
window.HHLPhotoColor = window.HHLPhotoColor || (function () {
  var cache = {};

  var sampleUrl = function (src) {
    var url = new URL(src, window.location.href);
    url.searchParams.set('width', '48');
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.href;
  };

  var dominant = function (data) {
    var r = 0, g = 0, b = 0, weight = 0;

    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) {
        continue;
      }

      var pr = data[i], pg = data[i + 1], pb = data[i + 2],
        max = Math.max(pr, pg, pb), min = Math.min(pr, pg, pb),
        saturation = max === 0 ? 0 : (max - min) / max;

      // Skip background white, deep shadows and greys
      if (max > 235 && saturation < .12 || max < 35 || saturation < .18) {
        continue;
      }

      var w = saturation * saturation;
      r += pr * w; g += pg * w; b += pb * w; weight += w;
    }

    if (weight < 1) {
      return null;
    }

    // Lift dark colors a little so the glow stays visible on the dark theme
    var cr = r / weight, cg = g / weight, cb = b / weight, peak = Math.max(cr, cg, cb), boost = peak < 150 ? 150 / peak : 1;
    return [cr, cg, cb].map(function (v) { return Math.min(255, Math.round(v * boost)); }).join(', ');
  };

  return {
    get: function (src) {
      if (!src || !window.createImageBitmap) {
        return Promise.resolve(null);
      }

      var key = sampleUrl(src);

      if (!cache[key]) {
        cache[key] = fetch(key, {mode: 'cors'})
          .then(function (response) { return response.ok ? response.blob() : Promise.reject(); })
          .then(function (blob) { return createImageBitmap(blob); })
          .then(function (bitmap) {
            var canvas = document.createElement('canvas');
            canvas.width = 24;
            canvas.height = 24;
            var context = canvas.getContext('2d', {willReadFrequently: true});
            context.drawImage(bitmap, 0, 0, 24, 24);
            return dominant(context.getImageData(0, 0, 24, 24).data);
          })
          .catch(function () { return null; });
      }

      return cache[key];
    },

    apply: function (element, property, src) {
      // Lookups can finish out of order (photos change faster than some load): only the latest request may apply
      element._hhlColorRequests = element._hhlColorRequests || {};
      element._hhlColorRequests[property] = src;

      return this.get(src).then(function (color) {
        if (element._hhlColorRequests[property] !== src) {
          return;
        }

        if (color) {
          element.style.setProperty(property, color);
        } else {
          element.style.removeProperty(property);
        }
      });
    }
  };
})();


/**
 * Hulk Apps volume discounts: the app puts its tiers inside the Add to cart column, so they start indented next to
 * the quantity box. Move them directly under the whole quantity + Add to cart bar (full width), and again whenever
 * the app re-adds them (variant changes).
 */
(function () {
  var place = function () {
    document.querySelectorAll('.product-form__buy-buttons .hulkapps-volumes').forEach(function (volumes) {
      var buyButtons = volumes.closest('.product-form__buy-buttons');

      if (buyButtons && buyButtons.parentNode) {
        buyButtons.after(volumes);
      }
    });
  };

  var form = document.querySelector('.product-form');

  if (!form) {
    return;
  }

  place();
  new MutationObserver(place).observe(form, {childList: true, subtree: true});
})();

/**
 * Collection pages in the browser (sections/main-collection.liquid): the section loads up to 250 products already in
 * stock order, and <hhl-pager> shows them a page at a time. The page is kept in the URL (?pg=2) so it can be shared
 * and survives a reload; filters and sorting reset it to page 1. Pagers in the same section stay in sync.
 */
(function () {
  if (window.customElements.get('hhl-pager')) {
    return;
  }

  var pageFromUrl = function () {
    var page = parseInt(new URL(window.location.href).searchParams.get('pg'), 10);
    return page > 0 ? page : 1;
  };

  class HhlPager extends HTMLElement {
    connectedCallback() {
      var section = this.closest('.shopify-section') || document;
      this.list = section.querySelector('[data-hhl-page-size]');

      if (!this.list) {
        return;
      }

      this.list._hhlPagers = (this.list._hhlPagers || []).filter((pager) => pager.isConnected);
      this.list._hhlPagers.push(this);

      // Clicks are handled once, however often the pager is connected (moved or re-inserted)
      if (!this._ready) {
        this._ready = true;
        this.addEventListener('click', this._onClick.bind(this));
      }

      if (!this.list._hhlPage) {
        this.list._hhlPage = pageFromUrl();
      }

      HhlPager.show(this.list, this.list._hhlPage, false);
      HhlPager.watch(this.list);
    }

    static items(list) {
      return Array.from(list.querySelectorAll('.product-list__inner > .product-item'));
    }

    // The page size in whole grid rows, so no page ends with a half-empty row (24 on a 5-column grid becomes 25)
    static pageSize(list) {
      var size = parseInt(list.getAttribute('data-hhl-page-size'), 10) || 24,
        inner = list.querySelector('.product-list__inner'),
        columns = inner ? getComputedStyle(inner).gridTemplateColumns.split(' ').filter(Boolean).length : 1;

      columns = Math.max(1, columns);
      return Math.max(columns, Math.round(size / columns) * columns);
    }

    static show(list, page, scroll) {
      var items = HhlPager.items(list),
        size = HhlPager.pageSize(list),
        pages = Math.max(1, Math.ceil(items.length / size));

      list._hhlSize = size;

      page = Math.min(Math.max(1, page), pages);
      list._hhlPage = page;

      items.forEach(function (item, index) {
        item.hidden = Math.floor(index / size) + 1 !== page;
      });

      (list._hhlPagers || []).forEach(function (pager) {
        pager.render(page, pages);
      });

      var url = new URL(window.location.href);
      page > 1 ? url.searchParams.set('pg', page) : url.searchParams.delete('pg');
      window.history.replaceState(window.history.state, '', url.toString());

      // From the bottom pager: bring the top of the list into view, with the top page numbers visible just under the
      // (sticky) header. The top pager never moves the page.
      if (scroll) {
        var section = list.closest('.shopify-section') || document,
          anchor = section.querySelector('.product-facet__meta-bar') || list,
          header = document.querySelector('.shopify-section--header'),
          headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0,
          top = anchor.getBoundingClientRect().top + window.scrollY - headerBottom - 12;

        if (anchor.getBoundingClientRect().top < headerBottom || anchor.getBoundingClientRect().top > window.innerHeight) {
          window.scrollTo({top: Math.max(0, top), behavior: 'smooth'});
        }
      }
    }

    render(page, pages) {
      this.hidden = pages < 2;

      if (pages < 2) {
        this.innerHTML = '';
        return;
      }

      // The arrows are the icon snippet's, from the <template> beside the pagers (sections/main-collection.liquid)
      var arrows = document.getElementById('hhl-pager-arrows'),
        icons = arrows ? arrows.content.querySelectorAll('svg') : [],
        back = icons[0] ? icons[0].outerHTML : '',
        forward = icons[1] ? icons[1].outerHTML : '';

      var build = function (numbers) {
        var html = '<nav class="hhl-pager__nav" aria-label="Pages">' +
          '<button type="button" class="hhl-pager__step" data-page="' + (page - 1) + '" aria-label="Previous page"' + (page === 1 ? ' disabled' : '') + '>' + back + '</button>';

        numbers.forEach(function (number) {
          html += number === '…'
            ? '<span class="hhl-pager__gap" aria-hidden="true">…</span>'
            : '<button type="button" class="hhl-pager__page" data-page="' + number + '"' + (number === page ? ' aria-current="page"' : '') + '>' + number + '</button>';
        });

        return html + '<button type="button" class="hhl-pager__step" data-page="' + (page + 1) + '" aria-label="Next page"' + (page === pages ? ' disabled' : '') + '>' + forward + '</button></nav>';
      };

      // Every page number when they fit in the space available
      var all = [];
      for (var i = 1; i <= pages; i++) all.push(i);
      this.innerHTML = build(all);

      if (this.firstElementChild.scrollWidth <= this.clientWidth + 1) {
        return;
      }

      // Otherwise the first, last, current and its neighbors (or, if even that is too wide, just the current page);
      // gaps become an ellipsis
      var condensed = function (reach) {
        var numbers = [];
        for (var j = 1; j <= pages; j++) {
          if (j === 1 || j === pages || Math.abs(j - page) <= reach) {
            numbers.push(j);
          } else if (numbers[numbers.length - 1] !== '…') {
            numbers.push('…');
          }
        }
        return numbers;
      };

      this.innerHTML = build(condensed(1));

      if (this.firstElementChild.scrollWidth > this.clientWidth + 1) {
        this.innerHTML = build(condensed(0));
      }
    }

    _onClick(event) {
      var button = event.target.closest('[data-page]');

      if (!button || button.disabled) {
        return;
      }

      HhlPager.show(this.list, parseInt(button.getAttribute('data-page'), 10), this.classList.contains('hhl-pager--bottom'));
    }
  }

  // The column count changes with the grid's width (window size, filter sidebar): re-split, keeping the first
  // product on screen on the new page
  var resplit = function (list) {
    if (!list._hhlSize || HhlPager.pageSize(list) === list._hhlSize) {
      return;
    }

    var firstIndex = (list._hhlPage - 1) * list._hhlSize;
    HhlPager.show(list, Math.floor(firstIndex / HhlPager.pageSize(list)) + 1, false);
  };

  HhlPager.watch = function (list) {
    if (list._hhlWatched || !window.ResizeObserver) {
      return;
    }

    list._hhlWatched = true;
    new ResizeObserver(function () {
      clearTimeout(list._hhlResizeTimer);
      list._hhlResizeTimer = setTimeout(function () { resplit(list); }, 150);
    }).observe(list);
  };

  // Defined last: defining upgrades the pagers already on the page, which use everything above
  window.customElements.define('hhl-pager', HhlPager);
})();

/**
 * Smooth collection browsing: on a collection page, links to another collection (sidebar, breadcrumbs, menus) swap
 * just the page content in place instead of reloading the whole page, so the header, sidebar and cart stay put.
 * Back/forward work as usual. Anything else (products, pages, cart, modified clicks) loads normally, and any error
 * falls back to a normal page load.
 * Note: Shopify analytics only records full page loads, so collection views reached this way aren't counted as
 * page views.
 */
(function () {
  var main = document.getElementById('main');

  // Not in the theme editor (owner 2026-10-30: saving there jumped to another page): the editor follows the preview
  // only through real page loads, so a page swapped in place left it on the old page, and its next refresh loaded
  // the swapped-in address under a different template
  if (!main || !window.fetch || !window.DOMParser || !document.body.classList.contains('template-collection') || (window.Shopify && Shopify.designMode)) {
    return;
  }

  var isCollectionUrl = function (url) {
    return url.origin === window.location.origin && /^\/collections\/[^/]+\/?$/.test(url.pathname);
  };

  // A page is its path plus its view (a console's explore page is the collection with ?view=explore)
  var pageKey = function (url) {
    return url.pathname + '|' + (url.searchParams.get('view') || '');
  };

  var pending = null,
    shownPath = pageKey(new URL(window.location.href));

  // Back and forward return to where the page was left: each entry keeps its scroll position, restored with the swap
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  var saveScroll = function () {
    window.history.replaceState(Object.assign({}, window.history.state, {hhlSoftNav: true, hhlScroll: window.scrollY}), '');
  };

  // Leaving for another page (a product): keep the spot too, and put it back when Back reloads this page (when the
  // browser keeps the page in memory it comes back as it was anyway)
  window.addEventListener('pagehide', saveScroll);

  var navigation = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0],
    savedY = window.history.state && window.history.state.hhlScroll;

  if (navigation && navigation.type === 'back_forward' && savedY) {
    window.scrollTo(0, savedY);
    window.addEventListener('load', function () { window.scrollTo(0, savedY); }, {once: true});
  }

  var markSidebar = function (pathname) {
    document.querySelectorAll('.desktop-sidebar a[href]').forEach(function (link) {
      var current = new URL(link.href, window.location.href).pathname === pathname;
      link.classList.toggle('is-active', current);
      current ? link.setAttribute('aria-current', 'page') : link.removeAttribute('aria-current');
    });

    // The bottom bar's Home (or Account) stops being the current tab
    document.querySelectorAll('.hhl-tabbar a[aria-current="page"]').forEach(function (link) {
      if (new URL(link.href, window.location.href).pathname !== pathname) link.removeAttribute('aria-current');
    });

    // Only the group holding the page shows its color
    document.querySelectorAll('.desktop-sidebar__item--group').forEach(function (group) {
      group.classList.toggle('is-current-group', !!group.querySelector('a[aria-current="page"]'));
    });
  };

  // Scripts inserted with innerHTML don't run: replace each with a fresh copy so section scripts work as on a load
  var runScripts = function (root) {
    root.querySelectorAll('script').forEach(function (old) {
      var script = document.createElement('script');
      Array.from(old.attributes).forEach(function (attribute) { script.setAttribute(attribute.name, attribute.value); });
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  };

  // Pages are kept once fetched (as the server sent them), and the page being left is fetched in the background, so
  // Back shows the previous page at once instead of waiting for it to load again
  var pages = {};

  var load = function (url, signal) {
    var key = url.href.split('#')[0];

    if (!pages[key]) {
      pages[key] = fetch(key, {headers: {'X-Requested-With': 'hhl-soft-nav'}}).then(function (response) {
        if (!response.ok) throw new Error('page failed to load');
        return response.text();
      });
      pages[key].catch(function () { delete pages[key]; });
    }

    return signal ? Promise.race([pages[key], new Promise(function (resolve, reject) {
      signal.addEventListener('abort', function () { reject(new DOMException('aborted', 'AbortError')); });
    })]) : pages[key];
  };

  var go = async function (url, push, scrollY) {
    if (pending) {
      pending.abort();
    }

    var controller = new AbortController();
    pending = controller;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', {bubbles: true}));
    main.classList.add('is-swapping');

    try {
      var html = await load(url, controller.signal);
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.getElementById('main');

      if (!next || !doc.body.classList.contains('template-collection')) {
        throw new Error('not a collection page');
      }

      if (push) {
        window.history.pushState({hhlSoftNav: true}, '', url.href);
      }

      var swapped = false;

      var swap = function () {
        if (swapped) return;
        swapped = true;
        document.title = doc.title;
        document.body.className = document.body.className.replace(/template-[\w-]+/g, '').trim() + ' template-collection';
        main.className = next.className;
        main.innerHTML = next.innerHTML;
        runScripts(main);
        markSidebar(url.pathname);
        shownPath = pageKey(url);
        window.scrollTo(0, scrollY || 0);
      };

      // Same page transition as full page loads (custom.css), where supported
      // (A transition the browser cancels, e.g. in a background tab, still swaps the page)
      if (document.startViewTransition && !document.hidden && !HHL.mq.reducedMotion.matches) {
        main.classList.remove('is-swapping');
        var transition = document.startViewTransition(swap);
        transition.ready.catch(function () {});
        transition.finished.catch(function () {});
        await transition.updateCallbackDone.catch(function () {});
      }
      swap();
      main.dispatchEvent(new CustomEvent('hhl:page-swapped', {bubbles: true}));
    } catch (error) {
      if (error.name !== 'AbortError') {
        window.location.href = url.href;
      }
      return;
    } finally {
      if (pending === controller) {
        pending = null;
        main.classList.remove('is-swapping');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', {bubbles: true}));
      }
    }
  };

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var link = event.target.closest('a[href]');

    if (!link || link.target && link.target !== '_self' || link.hasAttribute('download') || link.closest('[data-no-soft-nav]')) {
      return;
    }

    var url = new URL(link.href, window.location.href);

    // Same page with a different query (sorting, filters, pages) is handled by the theme itself
    if (!isCollectionUrl(url) || pageKey(url) === pageKey(new URL(window.location.href))) {
      return;
    }

    event.preventDefault();
    saveScroll();
    load(new URL(window.location.href)).catch(function () {});
    go(url, true);
  }, true);

  window.history.replaceState(Object.assign({}, window.history.state, {hhlSoftNav: true}), '');

  window.addEventListener('popstate', function (event) {
    var url = new URL(window.location.href);

    // Only when Back/Forward lands on another collection; filter and sort history on the same one is the theme's
    if (isCollectionUrl(url) && pageKey(url) !== shownPath) {
      go(url, false, event.state && event.state.hhlScroll);
    }
  });
})();

/**
 * LCD backdrop (layout/theme.liquid): a procedural Game Boy Color screen. The canvas has one pixel per LCD cell
 * across three sub-pixel columns (faint red, green and blue stripes, like the GBC's TFT), scaled up with hard edges;
 * CSS draws the gaps between cells. A slowly flowing plasma is drawn in a four-shade palette made from the color of
 * the sidebar group the page belongs to, GBC style: darker shades lean cool, lighter ones warm. Gradients between
 * shades use ordered (Bayer) dithering, the left edge dissolves into scattered pixels, and every cell eases toward its
 * new shade like the slow response of a handheld LCD (ghosting), and each pixel's color fades rather than jumps. Up to 60 frames a second while on screen (a still
 * frame with reduced motion). A click or tap sends a ripple out from that point; the mouse is otherwise ignored.
 */
(function () {
  var backdrop = document.querySelector('.cart-dock-backdrop');

  if (!backdrop || !window.requestAnimationFrame) {
    return;
  }

  var CELL = 10, SUB = 3,
    canvas = document.createElement('canvas'),
    context = canvas.getContext('2d'),
    reduced = HHL.mq.reducedMotion,
    image = null, columnWave = null, rowWave = null, diagonalWave = null, glow = null, shown = null, cellShift = 0, glowShift = 0,
    color = null, targetColor = null, alphas = null, dark = true, frame = null, ripples = [], last = 0,
    // 4x4 ordered dither thresholds
    BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(function (v) { return (v + 0.5) / 16; }),
    // Sub-pixel tints: each cell's left, middle and right third lean red, green and blue
    TINT = [[1, 0.8, 0.8], [0.84, 1, 0.84], [0.8, 0.84, 1]],
    // A wall-clock time base, so the pattern carries on where it was when the next page loads
    start = performance.now() - (Date.now() % 1e8);

  canvas.className = 'cart-dock-backdrop__lcd';
  backdrop.append(canvas);

  // The backdrop's size, kept by a ResizeObserver: the loop never measures the page itself (a measurement each frame
  // forced a layout every frame, on every page with the docked cart, whatever else the page was doing). Hidden
  // (display: none below 1600px, or the cart has items) it reports 0 x 0.
  var boxWidth = backdrop.clientWidth, boxHeight = backdrop.clientHeight;
  if (window.ResizeObserver) {
    new ResizeObserver(function (entries) {
      var rect = entries[entries.length - 1].contentRect;
      boxWidth = rect.width;
      boxHeight = rect.height;
      wake();
    }).observe(backdrop);
  }

  // Shade opacity (index 0 is an unlit cell) for the current light or dark theme: kept low so the pattern stays a
  // quiet texture behind the page (about half of its first, brighter version)
  var readPalette = function () {
    var rgb = getComputedStyle(document.body).color.match(/\d+/g) || [0, 0, 0];
    dark = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) > 128;
    alphas = dark ? [0, 20, 34, 48, 64] : [0, 14, 23, 34, 46];
  };

  // The color of the sidebar group holding the current page (Theme settings > Group label colors); with no current
  // group (home, product pages), the section accent the sidebar worked out for the page (its data-accent)
  var toRgb = function (value) {
    var probe = document.createElement('canvas').getContext('2d');
    probe.fillStyle = '#000';
    probe.fillStyle = value;
    var hex = probe.fillStyle;

    if (hex.charAt(0) === '#') {
      return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
    }

    return (hex.match(/\d+/g) || [59, 177, 67]).slice(0, 3).map(Number);
  };

  var pickColor = function () {
    // With one brand color the backdrop stays in it too (the sidebar's data-accent)
    var brand = document.documentElement.getAttribute('data-accent-mode') !== 'category',
      current = !brand && document.querySelector('.desktop-sidebar a[aria-current="page"], .desktop-sidebar a.is-active'),
      group = current && current.closest('.desktop-sidebar__item--group'),
      sidebar = document.querySelector('.desktop-sidebar'),
      // No current link (product pages, home): the section accent the sidebar worked out for this page
      value = group ? getComputedStyle(group).getPropertyValue('--group-color').trim() : (sidebar && sidebar.getAttribute('data-accent')) || '';

    targetColor = toRgb(value || '#3bb143');
    color = color || targetColor.slice();
  };

  var hsl = function (r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b), h = 0, s = 0, l = (max + min) / 2, d = max - min;
    if (d) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4) / 6;
    }
    return [h, s, l];
  };

  var rgb = function (h, s, l) {
    var hue = function (p, q, t) {
      t = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
      return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    return [hue(p, q, h + 1 / 3) * 255, hue(p, q, h) * 255, hue(p, q, h - 1 / 3) * 255];
  };

  // Hue h moved part of the way toward target, around the color wheel
  var towards = function (h, target, amount) {
    var d = ((target - h + 1.5) % 1) - 0.5;
    return (h + d * amount + 1) % 1;
  };

  // A GBC-style four-shade ramp from one color: hue drifts toward blue in the shadows and toward yellow in the
  // highlights, saturation peaks in the middle shades
  var ramp = function () {
    var base = hsl(color[0], color[1], color[2]), shades = [null];

    for (var k = 1; k <= 4; k++) {
      var t = (k - 1) / 3,
        h = t < 0.5 ? towards(base[0], 0.64, (0.5 - t) * 0.22) : towards(base[0], 0.15, (t - 0.5) * 0.22),
        s = Math.min(0.85, Math.max(0.35, base[1])) * (0.82 + 0.18 * Math.sin(Math.PI * t)),
        l = dark ? 0.3 + 0.44 * t : 0.24 + 0.36 * t;
      shades.push(rgb(h, s, l));
    }

    return shades;
  };

  var resize = function () {
    // Whole cells, and the canvas drawn at exactly CELL px per cell (SUB canvas pixels across), so its pixels line up
    // with the CSS cell grid. One extra row: the canvas slides up by part of a cell as the page scrolls
    var width = Math.max(1, Math.ceil(boxWidth / CELL)),
      height = Math.max(1, Math.ceil(boxHeight / CELL) + 1);

    if (canvas.width === width * SUB && canvas.height === height && image) {
      return;
    }

    canvas.width = width * SUB;
    canvas.height = height;
    canvas.style.width = width * CELL + 'px';
    canvas.style.height = height * CELL + 'px';
    image = context.createImageData(width * SUB, height);
    columnWave = new Float32Array(width);
    rowWave = new Float32Array(height);
    diagonalWave = new Float32Array(width + height + 1);
    glow = null;
    shown = null;
  };

  // The pattern scrolls with the page at half speed: whole cells shift the pattern, the remainder slides the canvas
  // and the cell grid together, so it moves smoothly rather than a cell at a time
  // Written only when the page has scrolled (a style write every frame restyled the backdrop every frame)
  var lastRemainder = null;
  var applyScroll = function () {
    var offset = window.scrollY * 0.5;
    cellShift = Math.floor(offset / CELL);
    var remainder = (offset - cellShift * CELL).toFixed(2);
    if (remainder === lastRemainder) return;
    lastRemainder = remainder;
    canvas.style.transform = 'translate3d(0, -' + remainder + 'px, 0)';
    backdrop.style.setProperty('--lcd-shift', '-' + remainder + 'px');
  };

  var draw = function (time, elapsed) {
    var t = (time - start) / 1000,
      width = canvas.width / SUB, height = canvas.height, data = image.data, i, x, y, k;

    // Each cell's lit level eases toward the pattern (LCD ghosting), kept aligned as the page scrolls
    // Each cell also keeps the color it shows, which fades toward its new shade instead of jumping (smooth pixels)
    if (!glow) {
      glow = new Float32Array(width * height).fill(-1);
      shown = new Float32Array(width * height * 4).fill(-1);
      glowShift = cellShift;
    } else if (glowShift !== cellShift) {
      var rows = cellShift - glowShift;
      if (Math.abs(rows) >= height) {
        glow.fill(-1);
        shown.fill(-1);
      } else if (rows > 0) {
        glow.copyWithin(0, rows * width);
        shown.copyWithin(0, rows * width * 4);
      } else {
        glow.copyWithin(-rows * width, 0, (height + rows) * width);
        shown.copyWithin(-rows * width * 4, 0, (height + rows) * width * 4);
      }
      glowShift = cellShift;
    }
    var response = reduced.matches ? 1 : 1 - Math.exp(-Math.min(elapsed, 100) / 160),
      fade = reduced.matches ? 1 : 1 - Math.exp(-Math.min(elapsed, 100) / 280);

    // Measured from the right edge, so the pattern stays put when the window (and so the backdrop) is resized
    for (x = 0; x < width; x++) columnWave[x] = Math.sin((width - x) * 0.15 + t * 0.4);
    for (y = 0; y < height; y++) rowWave[y] = Math.sin((y + cellShift) * 0.1 - t * 0.28);
    for (i = 0; i <= width + height; i++) diagonalWave[i] = Math.sin((width + height - i + cellShift) * 0.08 + t * 0.2);

    // Ease toward the section's color
    for (i = 0; i < 3; i++) color[i] += (targetColor[i] - color[i]) * 0.05;
    var shades = ramp();

    ripples = ripples.filter(function (ripple) { return t - ripple.t < 2.2; });

    for (y = 0; y < height; y++) {
      var row = (y + cellShift) & 3;

      for (x = 0; x < width; x++) {
        // Distance to the plasma's focus, a fixed point near the top right of the pattern
        var fx = x - width + 8, fy = y + cellShift - 25,
          index = y * width + x,
          value = (columnWave[x] + rowWave[y] + diagonalWave[x - y + height] + Math.sin(Math.sqrt(fx * fx + fy * fy) * 0.28 - t * 0.6) + 4) / 8;

        for (var r = 0; r < ripples.length; r++) {
          var ripple = ripples[r], age = t - ripple.t,
            dx = x - ripple.x, dy = y + cellShift - ripple.y, distance = Math.sqrt(dx * dx + dy * dy);
          // Ease in then out over the ripple's life (no jolt on click), slow wide rings that fade with distance
          var life = age / 2.2, envelope = life < 1 ? Math.sin(Math.PI * life) * Math.sin(Math.PI * life) : 0;
          value += Math.sin(distance * 0.32 - age * 3.2) * envelope * Math.exp(-distance * 0.07) * 0.18;
        }

        // Slightly fewer lit cells toward the left edge (the CSS fade does most of the fading)
        value *= 0.55 + 0.45 * Math.pow(x / width, 0.7);

        var lit = glow[index] < 0 ? value : glow[index] + (value - glow[index]) * response;
        glow[index] = lit;

        // Four shades plus unlit, dithered in between
        var level = Math.min(4, Math.max(0, Math.floor(lit * 4.4 - 0.3 + BAYER[row * 4 + (x & 3)]))),
          shade = shades[level], alpha = alphas[level], cell = index * 4, out = index * SUB * 4;

        // The shown color eases toward the shade (an unlit cell fades out in its last color)
        if (shown[cell] < 0) {
          shown[cell] = level ? shade[0] : shades[1][0];
          shown[cell + 1] = level ? shade[1] : shades[1][1];
          shown[cell + 2] = level ? shade[2] : shades[1][2];
          shown[cell + 3] = alpha;
        } else {
          if (level) {
            shown[cell] += (shade[0] - shown[cell]) * fade;
            shown[cell + 1] += (shade[1] - shown[cell + 1]) * fade;
            shown[cell + 2] += (shade[2] - shown[cell + 2]) * fade;
          }
          shown[cell + 3] += (alpha - shown[cell + 3]) * fade;
        }

        for (k = 0; k < SUB; k++, out += 4) {
          data[out] = shown[cell] * TINT[k][0];
          data[out + 1] = shown[cell + 1] * TINT[k][1];
          data[out + 2] = shown[cell + 2] * TINT[k][2];
          data[out + 3] = shown[cell + 3];
        }
      }
    }

    context.putImageData(image, 0, 0);
  };

  var visible = function () {
    return !document.hidden && boxWidth > 0 && boxHeight > 0;
  };

  // Taps wait for the next frame, where the backdrop is measured once, before anything is written (measuring on
  // pointerdown forced a layout in the middle of every tap, anywhere on the page)
  var taps = [];

  var loop = function (time) {
    frame = null;

    if (!visible()) {
      taps = [];
      return;
    }

    if (taps.length) {
      var box = backdrop.getBoundingClientRect();
      taps.forEach(function (tap) {
        if (tap.x < box.left || tap.x > box.right || tap.y < box.top || tap.y > box.bottom) return;
        ripples.push({x: (tap.x - box.left) / CELL, y: (tap.y - box.top) / CELL + cellShift, t: (tap.at - start) / 1000});
        if (ripples.length > 4) ripples.shift();
      });
      taps = [];
    }

    // At most about 60 draws a second, also on high refresh rate screens
    if (time - last > 15 || reduced.matches) {
      var elapsed = last ? time - last : 16;
      last = time;
      resize();
      applyScroll();
      draw(time, elapsed);
    }

    if (!reduced.matches) {
      frame = requestAnimationFrame(loop);
    }
  };

  var wake = function () {
    if (!frame && visible()) {
      readPalette();
      pickColor();
      frame = requestAnimationFrame(loop);
    }
  };

  // Collections opened in place (custom.js soft navigation): shift to the new section's color
  document.addEventListener('hhl:page-swapped', function () {
    setTimeout(pickColor, 50);
  });

  // Clicks and taps anywhere over the backdrop send out a ripple (checked against it in the next frame: see "taps")
  document.addEventListener('pointerdown', function (event) {
    if (reduced.matches || !visible()) {
      return;
    }

    taps.push({x: event.clientX, y: event.clientY, at: performance.now()});
    wake();
  }, {passive: true});

  wake();
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('resize', wake);

  // With reduced motion the frame is only redrawn when the page scrolls
  window.addEventListener('scroll', function () {
    if (reduced.matches) {
      wake();
    }
  }, {passive: true});

  // Follow the light/dark toggle
  new MutationObserver(function () { readPalette(); wake(); })
    .observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});
})();

/**
 * While the window is being resized, pause transitions and animations (cart wipe, card lift, fades), so crossing a
 * layout breakpoint snaps cleanly instead of playing animations mid-drag. Restored shortly after resizing stops.
 */
(function () {
  var timer = null, root = document.documentElement;

  window.addEventListener('resize', function () {
    root.classList.add('is-resizing');
    clearTimeout(timer);
    timer = setTimeout(function () {
      root.classList.remove('is-resizing');
    }, 250);
  });
})();

/**
 * Section accent (snippets/desktop-sidebar.liquid sets it on load): when a collection opens in place, buttons and the
 * accent take the new sidebar group's color, with readable text on top.
 */
(function () {
  var apply = function () {
    // One brand color (Theme settings > Desktop sidebar > Button and highlight color): nothing changes between pages
    if (document.documentElement.getAttribute('data-accent-mode') !== 'category') return;

    var current = document.querySelector('.desktop-sidebar a[aria-current="page"], .desktop-sidebar a.is-active'),
      group = (current && current.closest('.desktop-sidebar__item--group')) || document.querySelector('.desktop-sidebar__item--group'),
      value = group ? getComputedStyle(group).getPropertyValue('--group-color').trim() : '';

    if (!value) {
      return;
    }

    var probe = document.createElement('canvas').getContext('2d');
    probe.fillStyle = value;
    var hex = probe.fillStyle;

    if (hex.charAt(0) !== '#') {
      return;
    }

    var r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);

    // Softened the same way as on page load (snippets/desktop-sidebar.liquid)
    if (window.HHLAccent) {
      window.HHLAccent.apply(r, g, b);
    }
  };

  document.addEventListener('hhl:page-swapped', function () {
    setTimeout(apply, 30);
  });
})();

/**
 * Newsletter signup card (sections/newsletter-popup.liquid, styled in custom.css): the theme opens it as a modal that
 * locks page scrolling and traps focus. As a corner card it shouldn't do either.
 */
(function () {
  var popup = document.getElementById('newsletter-popup');

  if (!popup) {
    return;
  }

  Object.defineProperty(popup, 'shouldTrapFocus', {configurable: true, get: function () { return false; }});

  new MutationObserver(function () {
    if (popup.hasAttribute('open')) {
      document.documentElement.classList.remove('lock-all');
    }
  }).observe(popup, {attributes: true, attributeFilter: ['open']});
})();

/**
 * Keep the bottom of the sidebar (and, in browsers without scroll-linked CSS animations, the docked cart's checkout
 * button) on screen: both are pinned panels a full window tall, so at the top of the page (announcement bar above
 * the header) and near the end (footer sliding over) their bottom edge is hidden. Pad their contents up by the hidden
 * amount so the last items always sit at the bottom of the visible part, lined up with the top of the footer. Their
 * outer size never changes, so the page doesn't move.
 */
(function () {
  // The site footer section (the cart drawer has its own <footer> element earlier in the page)
  var footer = document.querySelector('.shopify-section--footer') || document.querySelector('body > footer, #main ~ footer'),
    // The docked cart needs no script where the browser links animations to the scroll (custom.css "Docked cart, top
    // of the page"; at the footer its track carries it); the sidebar still does
    linked = window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()'),
    panels = [linked ? null : document.getElementById('mini-cart'), document.querySelector('.desktop-sidebar__inner')].filter(Boolean),
    pending = false;

  // Values are only written when they change (writing every frame made scrolling stutter)
  var written = {};

  var write = function (target, name, value) {
    var key = (target.id || target.className) + name;
    if (written[key] === value) return;
    written[key] = value;
    target.style.setProperty(name, value);
  };

  var update = function () {
    pending = false;
    var visibleBottom = Math.min(window.innerHeight, footer ? footer.getBoundingClientRect().top : window.innerHeight);

    panels.forEach(function (panel) {
      var rect = panel.getBoundingClientRect();

      if (rect.height === 0 || getComputedStyle(panel).position === 'fixed') {
        if (written[(panel.id || panel.className) + '--hhl-hidden-bottom'] !== '') {
          written[(panel.id || panel.className) + '--hhl-hidden-bottom'] = '';
          panel.style.removeProperty('--hhl-hidden-bottom');
        }
        return;
      }

      var hidden = Math.max(0, Math.round(rect.bottom - visibleBottom));
      write(panel, '--hhl-hidden-bottom', Math.min(hidden, rect.height - 120) + 'px');
    });
  };

  var schedule = function () {
    if (!pending) {
      pending = true;
      requestAnimationFrame(update);
    }
  };

  update();
  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', schedule);
  document.documentElement.addEventListener('cart:refresh', function () { setTimeout(schedule, 400); });
})();

/**
 * Product gallery: while the theme sets up the slider and switches to the selected variant's photo, the gallery can
 * collapse for a moment, making the thumbnails and (on phones) the buy buttons jump up and back. Hold its height at
 * the tallest seen for the current width.
 */
(function () {
  if (!window.ResizeObserver) {
    return;
  }

  document.querySelectorAll('.product__media-list').forEach(function (media) {
    var tallest = 0, width = 0;

    new ResizeObserver(function () {
      var box = media.getBoundingClientRect();

      if (Math.round(box.width) !== width) {
        width = Math.round(box.width);
        tallest = 0;
        media.style.minHeight = '';
        box = media.getBoundingClientRect();
      }

      if (box.height > tallest) {
        tallest = box.height;
        media.style.minHeight = Math.round(tallest) + 'px';
      }
    }).observe(media);
  });
})();

/**
 * Product buy box: where it sticks on desktop (sections/main-product.liquid, assets/hhl-product-layout.css sets
 * --hhl-info-top as its sticky top). Under the header when it fits the screen; when it's taller, by its bottom edge,
 * so it scrolls with the page until Add to cart is in view and then stays there. Worked out again when the buy box
 * changes size, the window does (once a frame at most) or the page crosses the desktop width.
 */
(function () {
  if (!document.querySelector('.hhl-product-layout')) return;

  var info = null, frame = 0;
  var observer = window.ResizeObserver ? new ResizeObserver(function () { place(); }) : null;

  var place = function () {
    frame = 0;
    if (!info) return;
    if (HHL.mq.tablet.matches) {
      info.style.removeProperty('--hhl-info-top');
      return;
    }
    var css = getComputedStyle(document.documentElement);
    var header = (parseInt(css.getPropertyValue('--header-height'), 10) || 0) * (parseInt(css.getPropertyValue('--enable-sticky-header'), 10) || 0);
    var top = header + 24;
    var room = window.innerHeight - top - 24;
    var height = info.offsetHeight;
    info.style.setProperty('--hhl-info-top', (height <= room ? top : window.innerHeight - height - 24) + 'px');
  };

  // The buy box with the description beside it (a product without one has no sticky buy box); again after the
  // theme editor redraws the section
  var bind = function () {
    var next = document.querySelector('.hhl-product-layout--details > .product__info');
    if (next === info) return;
    if (observer && info) observer.unobserve(info);
    info = next;
    if (observer && info) observer.observe(info);
    place();
  };

  window.addEventListener('resize', function () {
    if (info && !frame) frame = requestAnimationFrame(place);
  });
  HHL.onChange(HHL.mq.tablet, place);
  document.addEventListener('shopify:section:load', bind);
  bind();
})();

/**
 * Collection filter bar (sections/main-collection.liquid): the filters open in a window that expands between the bar
 * and the products. A group's button shows just that group's choices; "All filters" shows every group side by side;
 * pressing the open button again closes it. Picks are gathered and the products update once picking pauses. The theme
 * re-renders the whole area, but an open window is kept exactly as it is on screen (scroll, open groups, chips in
 * place): it takes the new state (what's available, counts, what's picked) from the re-rendered copy, which it then
 * replaces, so only the products change. The page stays put.
 */
(function () {
  var current = null; // null: closed, 'all', or a group's param name
  var openGroups = {}; // phones: which groups are open in the all filters view
  var phone = HHL.mq.phone;

  var parts = function () {
    return {
      bar: document.querySelector('.hhl-filter-bar'),
      win: document.getElementById('hhl-filter-window')
    };
  };

  var render = function (animate) {
    var p = parts();

    if (!p.win) {
      return;
    }

    var inner = p.win.firstElementChild;

    p.win.querySelectorAll('.product-facet__filter-item').forEach(function (item) {
      var group = item.getAttribute('data-filter-group');
      // Sorting has its own button: All filters lists only the filters
      item.classList.toggle('is-shown', group === current || (current === 'all' && group !== 'sort'));

      // Phones, all filters: each group is a pill that opens on demand (ones with a pick start open)
      if (!(group in openGroups)) openGroups[group] = !!item.querySelector('[name^="filter."]:checked');
      item.classList.toggle('hhl-open', openGroups[group]);

      // A re-rendered window comes back with every group marked expanded: keep the button's state in step
      var toggle = item.querySelector(':scope > button.collapsible-toggle');
      if (toggle && phone.matches && current === 'all') toggle.setAttribute('aria-expanded', String(openGroups[group]));
    });
    p.win.setAttribute('data-mode', current === 'all' ? 'all' : 'group');

    if (p.bar) {
      p.bar.querySelectorAll('.hhl-filter-bar__pill').forEach(function (pill) {
        pill.setAttribute('aria-expanded', pill.getAttribute('data-filter-group') === current ? 'true' : 'false');
      });
    }

    var open = current !== null,
      target = open ? inner.scrollHeight : 0;

    if (!animate) {
      p.win.style.transition = 'none';
      p.win.style.height = open ? 'auto' : '0px';
      p.win.classList.toggle('is-open', open);
      p.win.classList.toggle('is-settled', open);
      p.win.offsetHeight;
      p.win.style.transition = '';
      return;
    }

    // Animate from the current height to the new one, then let it size itself. While animating the window clips its
    // contents; once settled open it doesn't, so swatch tooltips can show outside it
    p.win.classList.remove('is-settled');
    p.win.style.height = p.win.getBoundingClientRect().height + 'px';
    p.win.offsetHeight;
    p.win.classList.toggle('is-open', open);
    p.win.style.height = target + 'px';

    clearTimeout(p.win._hhlTimer);
    p.win._hhlTimer = setTimeout(function () {
      if (current !== null) {
        p.win.style.height = 'auto';
        p.win.classList.add('is-settled');
      }
    }, 320);
  };

  document.addEventListener('click', function (event) {
    var pill = event.target.closest('.hhl-filter-bar__pill');
    var toolbarButton = !pill && event.target.closest('[aria-controls="facet-filters"]');

    if (!pill && !toolbarButton) {
      return;
    }

    if (!document.getElementById('hhl-filter-window')) {
      return;
    }

    // The theme's own "Filters" buttons (mobile toolbar) open the window instead of a side drawer
    if (toolbarButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    var group = pill ? pill.getAttribute('data-filter-group') : 'all';
    current = current === group ? null : group;
    render(true);

    if (current !== null && toolbarButton) {
      document.getElementById('hhl-filter-window').scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }, true);

  // Phones, all filters: a group's pill opens and closes just that group (instead of the theme's collapsible)
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('#hhl-filter-window[data-mode="all"] button.collapsible-toggle');

    if (!toggle || !phone.matches) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    var item = toggle.closest('.product-facet__filter-item'),
      group = item.getAttribute('data-filter-group');
    openGroups[group] = !item.classList.contains('hhl-open');
    item.classList.toggle('hhl-open', openGroups[group]);
    toggle.setAttribute('aria-expanded', String(openGroups[group]));
  }, true);

  // Sorting lives in the same window: a choice applies the sort the way the theme's sort menu does (the products
  // reload in place), updates the Sort button's label and closes the window
  document.addEventListener('click', function (event) {
    var choice = event.target.closest('.hhl-sort-choice');

    if (!choice) {
      return;
    }

    var url = new URL(location.href);
    url.searchParams.set('sort_by', choice.getAttribute('data-sort-value'));
    url.searchParams.delete('page');
    // A new order starts again at the first page (the browser-side pager keeps its page in ?pg)
    url.searchParams.delete('pg');

    choice.parentElement.querySelectorAll('.hhl-sort-choice').forEach(function (other) {
      other.setAttribute('aria-checked', String(other === choice));
    });

    var label = document.querySelector('.hhl-filter-bar__sort-label');
    if (label) label.textContent = choice.textContent.trim();

    current = null;
    render(true);
    choice.dispatchEvent(new CustomEvent('facet:criteria-changed', {bubbles: true, detail: {url: url.toString()}}));
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && current !== null && document.getElementById('hhl-filter-window')) {
      current = null;
      render(true);
    }
  });

  // Several picks in a row: the ticks show at once, and the products update once, shortly after the last pick, instead
  // of reloading on every tick. The theme's own handler is skipped; the same update is sent when picking pauses. Picks
  // made while an update is loading are kept: the reload brings back the window as the server sent it, so they are put
  // back onto it (pendingPicks) and sent with the next update
  var applyTimer = null, keepScroll = null, pendingPicks = null, keptWindow = null;

  // After any re-render the theme smoothly scrolls the page to the top of the results. For filter and sort changes
  // that jerked the whole view around, so it's skipped for them (and the page stays where it is); a page number click
  // still scrolls up to the results. The theme's re-render is the only scrollIntoView inside the results area that
  // follows these events.
  var stayPut = false, stayTimer = null;
  var nativeScrollIntoView = Element.prototype.scrollIntoView;

  Element.prototype.scrollIntoView = function () {
    if (stayPut && this.closest && this.closest('product-facet')) return;
    return nativeScrollIntoView.apply(this, arguments);
  };

  document.addEventListener('facet:criteria-changed', function () {
    stayPut = true;
    if (keepScroll === null) keepScroll = window.scrollY;
    clearTimeout(stayTimer);
    // Long enough for the update to load and the theme's scroll call to come and go
    stayTimer = setTimeout(function () { stayPut = false; keepScroll = null; }, 8000);
  }, true);

  document.addEventListener('pagination:page-changed', function () {
    stayPut = false;
    keepScroll = null;
  }, true);

  var windowForm = function () {
    var win = document.getElementById('hhl-filter-window');
    return win && win.querySelector('form');
  };

  var snapshot = function (form) {
    return Array.from(form.querySelectorAll('[name^="filter."]')).map(function (input) {
      return {name: input.name, value: input.value, checked: input.checked, type: input.type};
    });
  };

  // Picks waiting to apply dim the products: a class on #facet-main (custom.css), not a :has() on it
  var syncPending = function () {
    var facet = document.getElementById('facet-main');
    var win = document.getElementById('hhl-filter-window');
    if (facet) facet.classList.toggle('hhl-filter-pending', !!(win && win.classList.contains('is-pending')));
  };

  var restorePicks = function (form) {
    form.querySelectorAll('[name^="filter."]').forEach(function (input) {
      var saved = pendingPicks.find(function (pick) {
        return pick.name === input.name && (pick.type === 'checkbox' || pick.type === 'radio' ? pick.value === input.value : true);
      });
      if (!saved) return;
      if (input.type === 'checkbox' || input.type === 'radio') input.checked = saved.checked;
      else input.value = saved.value;
    });
  };

  document.addEventListener('change', function (event) {
    var input = event.target;

    if (!input.matches || !input.matches('#hhl-filter-window [name^="filter."]') || !input.form) {
      return;
    }

    event.stopImmediatePropagation();
    clearTimeout(applyTimer);
    pendingPicks = snapshot(input.form);
    document.getElementById('hhl-filter-window').classList.add('is-pending');
    syncPending();

    applyTimer = setTimeout(function () {
      var form = windowForm();
      if (!form) return;

      var params = new URLSearchParams(new FormData(form));
      // Search results keep their terms
      var terms = new URLSearchParams(location.search).get('q');
      if (terms && !params.has('q')) params.set('q', terms);

      pendingPicks = null;
      keepScroll = window.scrollY;
      // The open window stays on screen through the update (below)
      keptWindow = current !== null ? document.getElementById('hhl-filter-window') : null;
      form.dispatchEvent(new CustomEvent('facet:criteria-changed', {bubbles: true, detail: {url: location.pathname + '?' + params.toString()}}));
    }, 750);
  }, true);

  // The filter choices in a window, by name and value
  var choices = function (win) {
    var map = new Map();
    win.querySelectorAll('input[name^="filter."]').forEach(function (input) {
      map.set(input.name + '=' + (input.type === 'checkbox' || input.type === 'radio' ? input.value : ''), input);
    });
    return map;
  };

  // The kept window takes the re-rendered one's state: each choice's availability (disabled, and its chip's classes),
  // its count (in its label) and whether it's picked, unless picks were made while the update loaded (those stay).
  // Search results only list the choices their products have (and Shopify's search can return a slightly different
  // set from one request to the next), so the new copy can have fewer or other choices: missing ones stay in place,
  // shown unavailable (as collections show them), and new ones wait for the next full load, so nothing moves while
  // the window is open. Returns false only when the filter groups themselves differ, to use the new window instead
  var chipOf = function (input) {
    return input.closest('.color-swatch, .hhl-look-chip, .block-swatch, .checkbox-container');
  };

  var groupsOf = function (win) {
    return Array.from(win.querySelectorAll('.product-facet__filter-item[data-filter-group]')).map(function (item) {
      return item.getAttribute('data-filter-group');
    }).join('|');
  };

  var adopt = function (kept, fresh) {
    if (groupsOf(kept) !== groupsOf(fresh)) return false;

    var oldChoices = choices(kept), newChoices = choices(fresh);

    oldChoices.forEach(function (mine, key) {
      if (newChoices.has(key) || mine.checked) return;
      mine.disabled = true;
      var myChip = chipOf(mine);
      if (myChip) myChip.classList.add('is-disabled');
    });

    newChoices.forEach(function (input, key) {
      var mine = oldChoices.get(key);
      if (!mine) return;
      mine.disabled = input.disabled;
      if (!pendingPicks) {
        if (input.type === 'checkbox' || input.type === 'radio') mine.checked = input.checked;
        else mine.value = input.value;
      }

      var chip = chipOf(input),
        myChip = chipOf(mine);
      if (chip && myChip && chip.className !== myChip.className) myChip.className = chip.className;

      var label = input.id && fresh.querySelector('label[for="' + CSS.escape(input.id) + '"]'),
        myLabel = mine.id && kept.querySelector('label[for="' + CSS.escape(mine.id) + '"]');
      if (label && myLabel) HHL.setText(myLabel, label.textContent);
    });

    // Each group's current picks, shown in its header on phones ("Red, Glow"): added, updated or removed to match
    kept.querySelectorAll('[data-filter-group] > .collapsible-toggle').forEach(function (toggle) {
      var group = toggle.parentElement.getAttribute('data-filter-group'),
        twin = fresh.querySelector('[data-filter-group="' + CSS.escape(group) + '"] > .collapsible-toggle .collapsible-toggle__selected-value'),
        mine = toggle.querySelector('.collapsible-toggle__selected-value');

      if (twin && mine) {
        HHL.setText(mine, twin.textContent);
      } else if (twin) {
        toggle.insertBefore(twin.cloneNode(true), toggle.querySelector('svg'));
      } else if (mine) {
        mine.remove();
      }
    });

    return true;
  };

  // After the theme re-renders the products (filters changed), restore the window as it was, and keep the page where
  // it was while the window is open (the theme scrolls back up to the filter bar). Watched from #main, which stays put
  // when a collection opens in place (its #facet-main is then a new element)
  var root = document.getElementById('main');

  if (root) {
    new MutationObserver(function (mutations) {
      if (!mutations.some(function (mutation) { return mutation.target.id === 'facet-main'; })) {
        return;
      }

      // The open window stays as it was on screen: the re-rendered copy only lends it its new state
      var fresh = document.getElementById('hhl-filter-window');
      if (keptWindow && fresh && fresh !== keptWindow && current !== null && adopt(keptWindow, fresh)) {
        fresh.replaceWith(keptWindow);
        if (!pendingPicks) keptWindow.classList.remove('is-pending');
      }
      keptWindow = null;

      render(false);
      syncPending();

      // Picks made while that update was loading go back onto the new window
      var form = windowForm();
      if (pendingPicks && form) {
        restorePicks(form);
        document.getElementById('hhl-filter-window').classList.add('is-pending');
        syncPending();
      }

      // The page stays where it was: corrected here, before the browser paints, if the new content moved it
      if (keepScroll !== null) {
        if (Math.abs(window.scrollY - keepScroll) > 1) window.scrollTo({top: keepScroll, behavior: 'instant'});
        keepScroll = null;
      }
    }).observe(root, {childList: true, subtree: true});

    // A collection opened in place starts with the window closed
    root.addEventListener('hhl:page-swapped', function () {
      current = null;
      openGroups = {};
      pendingPicks = null;
      keptWindow = null;
      clearTimeout(applyTimer);
    });
  }
})();

/**
 * Add-to-cart feedback: every add-to-cart button (product page, sticky bar, card hover button, touch icon, quick
 * add) shows the same state while the item is being added: the label lifts away, a sheen sweeps across and three
 * pixel dots pulse. Once added, a check draws in with "Added" for a moment. The loading state lasts at least a
 * short beat so fast responses don't flicker. Works off aria-busy, which the theme and quick add already set.
 */
(function () {
  var SELECTOR = '.product-form__add-button, .product-item__quick-form button[type="submit"], .product-item__cta, .product-item__quick-buy-button, .quick-variants__add';
  var MIN_LOADING = 500, DONE_FOR = 1300;
  var pending = new Set();

  var stateMarkup =
    '<span class="hhl-atc-state" aria-hidden="true">' +
      '<span class="hhl-atc-state__dots"><i></i><i></i><i></i></span>' +
      '<span class="hhl-atc-state__done">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '<span class="hhl-atc-state__label">Added</span>' +
      '</span>' +
    '</span>';

  /*
   * Where the cart can't dock beside the page (narrower screens, pages without it), the "added" confirmation is a small
   * note next to the button that added the item, "Added to cart · View cart", instead of the corner message: it's where
   * the shopper is already looking, and it stays while hovered. The corner message is held back while an add from one
   * of these buttons is under way (custom.css); errors still show there.
   */
  var docked = function () {
    return HHL.mq.docked.matches && !!document.querySelector('[data-cart-dock]');
  };

  // The button last clicked, for adds that go through a hidden button
  var lastClick = null;
  document.addEventListener('click', function (event) {
    var clicked = event.target instanceof Element && event.target.closest('button, a');
    if (clicked) lastClick = clicked;
  }, true);

  var toast = null, toastTimer = null, toastHeld = 0, toastSource = null, toastCard = null;

  // The theme's corner message for this add is shown but kept out of sight while the tab handles it; it's dismissed
  // for good, so it never turns up once the tab goes (closing the quick add panel, clicking away)
  var dismissCorner = function () {
    document.querySelectorAll('cart-notification:not(.cart-notification--drawer):not(.cart-notification--error)').forEach(function (notification) {
      notification.hidden = true;
    });
  };

  var hideToast = function () {
    dismissCorner();
    clearTimeout(toastTimer);
    if (toast) toast.classList.remove('is-shown');
    if (toast && toast._hhlFollow) toast._hhlFollow.disconnect();
    toastSource = toastCard = null;
    document.documentElement.classList.remove('hhl-added-inline');
  };

  var holdCorner = function (on) {
    toastHeld = Math.max(0, toastHeld + (on ? 1 : -1));
    document.documentElement.classList.toggle('hhl-atc-pending', toastHeld > 0);
  };

  // Touch screens keep a tapped button "hovered", so hovering only holds the tab with a real pointer
  var canHover = HHL.mq.hover;

  var holdToast = function () { if (canHover.matches) clearTimeout(toastTimer); };
  var releaseToast = function () {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 2500);
  };

  // The tab is part of the button: it goes as soon as the shopper moves on. Pointing at another product (or leaving the
  // card the button is on), choosing another option, or closing the quick add panel puts it away
  document.addEventListener('pointerover', function (event) {
    if (!toastCard || !(event.target instanceof Element)) return;
    if (toastCard.contains(event.target) || (toast && toast.contains(event.target))) return;
    hideToast();
  });

  document.addEventListener('click', function (event) {
    if (toastSource && event.target instanceof Element && event.target.closest('.quick-variants__option')) hideToast();
  }, true);

  document.addEventListener('variant:changed', function () {
    if (toastSource) hideToast();
  });

  // Where the tab sits: centered under (or over) its button and narrower than it, in the box `host`. Run when it shows
  // and again whenever the button or that box changes size while it shows (owner 2026-10-30: the page settling after
  // an add left it sized for a wider button, hanging off-center)
  var placeToast = function (button, host) {
    var tab = toast.firstChild;
    // Outside the button's own box (cards), the tab can't sit behind the button without also sitting behind the
    // photo, so it starts right at the button's edge, above the photo, and is cut off there (same slide)
    var flush = host !== button.parentElement;
    toast.classList.toggle('is-flush', flush);

    var TUCK = flush ? 0 : 12,
      rect = button.getBoundingClientRect(),
      box = host.getBoundingClientRect(),
      // Narrower than the button, so its top corners stay inside the button's straight bottom edge
      inset = Math.max(12, parseFloat(getComputedStyle(button).borderBottomLeftRadius) || 0),
      width = Math.max(rect.width - inset * 2, 150),
      height = tab.offsetHeight || 44,
      // Room below the button: above the bottom bar, where there is one (phones and tablets)
      tabbar = document.querySelector('.hhl-tabbar'),
      floor = tabbar && tabbar.offsetParent ? Math.min(window.innerHeight, tabbar.getBoundingClientRect().top) : window.innerHeight,
      below = rect.bottom + height - TUCK + 4 < floor,
      x = rect.left - box.left - host.clientLeft + host.scrollLeft,
      y = rect.top - box.top - host.clientTop + host.scrollTop;

    toast.classList.toggle('is-above', !below);
    toast.classList.toggle('is-compact', width < 220);
    toast.style.position = 'absolute';
    toast.style.width = width + 'px';
    toast.style.left = (x + (rect.width - width) / 2) + 'px';
    toast.style.top = (below ? y + rect.height - TUCK : y - height + TUCK) + 'px';

    // An icon button on a card (phones): a small "View cart" tab hangs from the middle of the photo's bottom edge,
    // kept inside its card
    var photo = button.closest('.product-item__image-wrapper'),
      card = button.closest('.product-item');
    toast.classList.toggle('is-icon', rect.width < 60 && !!photo && !!card);
    if (rect.width < 60 && photo && card) {
      toast.style.width = 'auto';
      var tabWidth = toast.offsetWidth,
        photoBox = photo.getBoundingClientRect(),
        cardBox = card.getBoundingClientRect(),
        minLeft = cardBox.left - box.left + 4,
        maxLeft = cardBox.right - box.left - tabWidth - 4;
      toast.style.left = Math.max(minLeft, Math.min(photoBox.left + photoBox.width / 2 - box.left - tabWidth / 2, maxLeft)) + 'px';
      toast.style.top = (photoBox.bottom - box.top - host.clientTop + host.scrollTop) + 'px';
      toast.classList.remove('is-above');
    }
  };

  var showToast = function (button) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'hhl-added-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<div class="hhl-added-toast__tab">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '<span>Added<span class="hhl-added-toast__more"> to cart</span></span>' +
          '<a href="' + HHL.cartUrl() + '" class="hhl-added-toast__link" data-no-instant>View cart</a>' +
        '</div>';
      document.body.appendChild(toast);

      toast.addEventListener('mouseenter', holdToast);
      toast.addEventListener('mouseleave', releaseToast);
      toast.querySelector('a').addEventListener('click', function (event) {
        if (!document.getElementById('mini-cart')) return;
        event.preventDefault();
        var panel = toast.closest('dialog[open]');
        hideToast();
        if (panel) panel.close();
        HHL.openCart();
      });
    }

    // Quick add for a one-option product adds through the panel's hidden button: the tab goes by the button that was
    // actually clicked
    if (!button.offsetWidth && lastClick && lastClick.isConnected && lastClick.offsetWidth) {
      button = lastClick;
    }

    // One add can busy two buttons (a product page's own button and its sticky bar's): the tab stays with the one
    // that was clicked
    if (button !== lastClick && toastSource === lastClick && toast && toast.classList.contains('is-shown')) {
      return;
    }

    // Already out under this button: just keep it out
    if (button === toastSource && toast.classList.contains('is-shown')) {
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        if (toastSource && canHover.matches && toastSource.matches(':hover')) return;
        hideToast();
      }, 4000);
      return;
    }

    if (toastSource && toastSource !== button) {
      toastSource.removeEventListener('mouseenter', holdToast);
      toastSource.removeEventListener('mouseleave', releaseToast);
    }

    toastSource = button;
    // On a card, the area that shows the button (its photo): pointing anywhere outside it and the tab puts the tab away
    toastCard = button.closest('dialog[open]') ? null : (button.closest('.product-item__image-wrapper') || button.closest('.product-item'));
    button.addEventListener('mouseenter', holdToast);
    button.addEventListener('mouseleave', releaseToast);

    // Closing the quick add panel takes the tab away
    var panel = button.closest('dialog[open]');
    if (panel && !panel._hhlToastClose) {
      panel._hhlToastClose = true;
      panel.addEventListener('close', function () { if (toast && panel.contains(toast)) hideToast(); });
    }

    // Hide at once (no slide) while it moves to its new place
    var tab = toast.firstChild;
    toast.style.transition = 'none';
    tab.style.transition = 'none';
    toast.classList.remove('is-shown');

    // The tab goes in the button's box, a layer below the button, with its top edge tucked behind the button's bottom
    // edge, so it slides out from underneath. If that box crops what hangs out of it (a card's photo area), the tab
    // goes in the next box out that doesn't
    var host = button.parentElement, stop = panel || document.body;
    for (var node = host; node && node !== stop; node = node.parentElement) {
      if (getComputedStyle(node).overflow !== 'visible') host = node.parentElement;
    }
    if (host === button.parentElement) {
      if (button.nextSibling !== toast) host.insertBefore(toast, button.nextSibling);
    } else if (toast.parentNode !== host) {
      host.appendChild(toast);
    }
    if (getComputedStyle(host).position === 'static') host.classList.add('hhl-atc-host');
    document.querySelectorAll('.hhl-atc-over').forEach(function (other) { if (other !== button) other.classList.remove('hhl-atc-over'); });
    button.classList.add('hhl-atc-over');

    placeToast(button, host);

    void toast.offsetWidth;
    toast.style.transition = '';
    tab.style.transition = '';
    toast.classList.add('is-shown');

    // It follows its button while it shows: a size change of the button or its box places it again
    if (window.ResizeObserver) {
      if (!toast._hhlFollow) {
        toast._hhlFollow = new ResizeObserver(function () {
          if (toastSource && toastSource.isConnected && toast.classList.contains('is-shown')) placeToast(toastSource, toast.parentNode);
        });
      }
      toast._hhlFollow.disconnect();
      toast._hhlFollow.observe(button);
      toast._hhlFollow.observe(host);
    }
    document.documentElement.classList.add('hhl-added-inline');
    dismissCorner();

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      if (toastSource && canHover.matches && toastSource.matches(':hover')) return;
      hideToast();
    }, 4000);
  };

  var start = function (button) {
    if (!docked()) holdCorner(true);
    if (button._hhlAtc) {
      clearTimeout(button._hhlAtc.timer);
    }

    if (!button.querySelector(':scope > .hhl-atc-state')) {
      button.insertAdjacentHTML('beforeend', stateMarkup);
    }

    button._hhlAtc = {started: performance.now(), added: false};
    button.classList.toggle('hhl-atc--compact', button.offsetWidth < 110);
    button.classList.remove('hhl-atc-done');
    button.classList.add('hhl-atc-loading');
    pending.add(button);
  };

  var finish = function (button) {
    var state = button._hhlAtc;

    if (!state) {
      return;
    }

    var wait = Math.max(MIN_LOADING - (performance.now() - state.started), 60);

    state.timer = setTimeout(function () {
      pending.delete(button);
      button.classList.remove('hhl-atc-loading');

      if (!state.added) {
        button._hhlAtc = null;
        holdCorner(false);
        return;
      }

      // One add busies every button of the form: the page's own and the sticky bar's (it names the form with form="").
      // The tab goes only under the one the shopper pressed when that one took part (owner 2026-10-30: at some widths
      // it hung off-center under the add button, placed for the sticky bar's); any other add (a quick add's hidden
      // button) keeps showing it, as before
      var pressedTookPart = lastClick && lastClick !== button && lastClick._hhlAtc && lastClick._hhlAtc.added;
      if (!docked() && !pressedTookPart) showToast(button);
      holdCorner(false);
      button.classList.add('hhl-atc-done');
      state.timer = setTimeout(function () {
        button.classList.remove('hhl-atc-done');
        button._hhlAtc = null;
      }, DONE_FOR);
    }, wait);
  };

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      var button = mutation.target;

      if (!button.matches || !button.matches(SELECTOR)) {
        return;
      }

      if (button.getAttribute('aria-busy') === 'true') {
        if (!button.classList.contains('hhl-atc-loading')) {
          start(button);
        }
      } else if (button.classList.contains('hhl-atc-loading')) {
        finish(button);
      }
    });
  }).observe(document.documentElement, {subtree: true, attributes: true, attributeFilter: ['aria-busy']});

  // The touch icon on cards isn't a loader button, so the theme never marks it busy: do it on submit
  document.addEventListener('submit', function (event) {
    var button = event.submitter;

    if (button && button.matches && button.matches('.product-item__quick-buy-button') && !button.hasAttribute('aria-busy')) {
      start(button);
      var settle = function () {
        finish(button);
      };
      document.addEventListener('variant:added', settle, {once: true});
      document.addEventListener('cart-notification:show', settle, {once: true});
      setTimeout(settle, 8000);
    }
  }, true);

  // Success: the form (or quick add panel) that added the item holds its button, or names it with form=""
  document.addEventListener('variant:added', function (event) {
    pending.forEach(function (button) {
      if (event.target === button.form || (event.target.contains && event.target.contains(button))) {
        button._hhlAtc.added = true;
      }
    });
  });
})();


/**
 * "View cart" in the added-to-cart message opens the cart drawer instead of loading the cart page.
 */
document.addEventListener('click', function (event) {
  var link = event.target instanceof Element && event.target.closest('.cart-notification__view-cart');

  if (!link || link.closest('.drawer') || !document.getElementById('mini-cart')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  var notification = link.closest('cart-notification');
  if (notification) notification.hidden = true;
  HHL.openCart();
}, true);

/**
 * Shipping protection (Navidium) in the cart: a line just above Checkout (snippets/protection-line.liquid). Protection
 * is on by default and its price is included in the Checkout button's total. Remove opens the disclaimer below the
 * line: "Agree & remove" turns it off, "Keep protection" closes it. Add turns it back on. The choice is remembered in
 * this browser and Checkout follows it (see the checkout sheet below).
 * Navidium's own widget stays in the cart, out of sight (custom.css). Navidium keeps the protection's price and
 * product for the cart in the browser; a protected checkout presses its button when it's drawn, or adds its protection
 * product itself.
 */
var hhlProtection = (function () {
  var KEY = 'hhl-protection';

  var money = HHL.money,
    setText = HHL.setText;

  var isOn = function () {
    try { return localStorage.getItem(KEY) !== 'off'; } catch (error) { return true; }
  };

  var setOn = function (on) {
    try {
      localStorage.setItem(KEY, on ? 'on' : 'off');
      localStorage.setItem('nvd_opted_out', on ? 'false' : 'true');
    } catch (error) {}
    update();
  };

  // Navidium's price for this cart, in cents (it works it out in the browser and keeps it here)
  var price = function () {
    var cents = 0;
    try { cents = parseInt(localStorage.getItem('nvdProtectionPrice'), 10) || 0; } catch (error) {}
    return cents;
  };

  // Navidium's widget for this cart, once it has drawn it (its checkout button adds the protection)
  var widgetFor = function (element) {
    var scope = element.closest('.hhl-cart-summary, .cart__recap, form, section') || document,
      mount = scope.querySelector('.nvd-mini') || document.querySelector('#mini-cart .nvd-mini, .nvd-mini');
    return mount && mount.querySelector('#nvd-with-checkout-btn');
  };

  // Navidium's protection product for this cart (it picks one by the cart's total and keeps it here)
  var variant = function () {
    try { return localStorage.getItem('nvdVariant'); } catch (error) { return null; }
  };

  // Navidium is running: its script is on the page and it has worked out a protection for this cart
  var running = function () {
    return !!document.querySelector('script[src*="navidium"], script[src*="nvd-widget"]') && !!variant() && price() > 0;
  };

  // A build that checks out through the builder app (its labor line says _checkout: draft) must reach checkout through
  // the Checkout button, where the app's cart guard sends it to the build's own checkout at its build price; going
  // straight to /checkout charges shelf prices (launch audit 2026-10-25). The button is marked so the checkout sheet
  // below lets it through. Any other cart goes straight to checkout, as before.
  var hasAppCheckout = function (cart) {
    return !!cart && cart.items.some(function (item) {
      return !!item.properties && item.properties._checkout === 'draft';
    });
  };

  var toCheckout = function (button, cart) {
    if (hasAppCheckout(cart) && button && button.isConnected) {
      button.removeAttribute('aria-busy');
      button.dataset.hhlNotesRead = 'true';
      button.click();
      delete button.dataset.hhlNotesRead;
      return;
    }
    window.location.href = HHL.root() + 'checkout';
  };

  // Protected checkout: Navidium's own button when it's drawn (the app's cart guard takes it over for a build),
  // otherwise add its protection product and go to checkout
  var checkoutProtected = function (element) {
    var widget = widgetFor(element);

    if (widget) {
      widget.disabled = false;
      widget.click();
      return;
    }

    var seen = null;

    HHL.fetchCart()
      .then(function (cart) {
        seen = cart;
        var has = cart.items.some(HHL.isProtectionItem);
        return has ? null : fetch(HHL.cartUrl() + '/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({id: variant(), quantity: 1})
        });
      })
      .catch(function () {})
      .then(function () {
        toCheckout(element, seen);
      });
  };

  var update = function () {
    var on = isOn(), cents = price();

    document.querySelectorAll('[data-hhl-protection]').forEach(function (line) {
      if (running()) {
        if (line.dataset.state !== (on ? 'on' : 'off')) line.dataset.state = on ? 'on' : 'off';
        setText(line.querySelector('[data-hhl-protection-price]'), money(cents));
      } else if (line.dataset.state === 'loading' && !line._hhlTimer) {
        // Give Navidium a few seconds to start; without it, fold the line away
        line._hhlTimer = setTimeout(function () {
          if (!running()) line.dataset.state = 'none';
        }, 6000);
      }

      // The total on the Checkout button (the cart drawer's) includes the protection while it's on. With a build that
      // checks out at its build price, the builder app's cart guard says what the cart costs at that price
      // (data-hhl-build-pay, without protection), so the button matches its "You pay" line (launch audit 2026-10-25)
      var summary = line.closest('.mini-cart__drawer-footer') || line.closest('.hhl-cart-summary, .cart__recap') || line.parentElement,
        totals = summary ? summary.querySelectorAll('.nvd-total') : [],
        subtotal = parseInt(document.documentElement.getAttribute('data-hhl-build-pay') || line.dataset.subtotal, 10);

      if (totals.length && !isNaN(subtotal) && line.dataset.state !== 'loading' && line.dataset.state !== 'none') {
        totals.forEach(function (total) {
          setText(total, money(subtotal + (on ? cents : 0)) + ' ' + HHL.currency());
        });
      }
    });
  };

  var openTerms = function (line, open) {
    var terms = line.querySelector('.hhl-protection__terms');
    line.classList.toggle('is-confirming', open);
    terms.inert = !open;
    line.querySelector('[data-hhl-protection-remove]').setAttribute('aria-expanded', String(open));
    if (open) setTimeout(function () { line.querySelector('[data-hhl-protection-keep]').focus({preventScroll: true}); }, 60);
  };

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null,
      line = target && target.closest('[data-hhl-protection]');

    if (!line) return;

    if (target.closest('[data-hhl-protection-remove]')) {
      openTerms(line, !line.classList.contains('is-confirming'));
    } else if (target.closest('[data-hhl-protection-keep]')) {
      openTerms(line, false);
    } else if (target.closest('[data-hhl-protection-agree]')) {
      openTerms(line, false);
      setOn(false);
    } else if (target.closest('[data-hhl-protection-add]')) {
      setOn(true);
    }
  });

  // The cart re-renders on every change, and Navidium redraws its widget (and its price) after it: keep up with both,
  // and with the builder app's build price for the cart
  document.addEventListener('hhl:build-pay', function () { update(); });
  var pending = null;
  new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(update, 60);
  }).observe(document.body, {childList: true, subtree: true});

  update();

  // Back from checkout with protection still in the cart: Navidium takes it out (without reloading the page, see
  // layout/theme.liquid). Once it's gone, refresh the cart so its count and contents are right. Only checks for a few
  // seconds, and only when the page was drawn with protection in the cart.
  var settleLeftover = function () {
    if (!document.querySelector('[data-hhl-protection][data-leftover]')) return;

    var tries = 0;

    var check = function () {
      HHL.fetchCart()
        .then(function (cart) {
          if (!cart.items.some(HHL.isProtectionItem)) {
            HHL.refreshCart();
          } else if (++tries < 12) {
            setTimeout(check, 700);
          }
        })
        .catch(function () {});
    };

    setTimeout(check, 700);
  };

  settleLeftover();

  return {isOn: isOn, running: running, checkoutProtected: checkoutProtected, toCheckout: toCheckout};
})();

/**
 * Cart order note on phones: the note box sits at the bottom of the cart, so while it's being typed in it rides up
 * above the on-screen keyboard (HHL.keyboardLift; it slides via --hhl-note-lift in custom.css, "Phones: every pixel
 * goes to the items").
 */
(function () {
  var box = null;

  var lift = HHL.keyboardLift({
    // Its resting edge is the cart's bottom edge (on phones the cart sits above the bottom bar)
    bottom: function () {
      var cart = box && (box.closest('#mini-cart') || box.offsetParent);
      return cart ? cart.offsetTop + cart.offsetHeight : window.innerHeight;
    },
    apply: function (value) {
      if (!box) return;
      box.style.setProperty('--hhl-note-lift', value + 'px');
      box.classList.toggle('is-lifted', value > 0);
    }
  });

  document.addEventListener('focusin', function (event) {
    var field = event.target instanceof Element && event.target.closest('.mini-cart__order-note textarea');
    if (!field) return;
    box = field.closest('.mini-cart__order-note');
    lift.start();
  });

  // Done typing (focus left the box, not just moved within it): back down
  document.addEventListener('focusout', function (event) {
    if (!box || !box.contains(event.target)) return;
    var leaving = box;
    setTimeout(function () {
      if (leaving.contains(document.activeElement) || box !== leaving) return;
      lift.stop();
      box = null;
    }, 150);
  });
})();

/**
 * Checkout sheet: the cart hides the notes (international orders, sales tax holidays, final sale) to save room, and
 * Checkout first opens them in a sheet (a centered dialog on desktop). Ticking "I've read these notes" enables
 * "Continue to checkout". Checkout then follows the cart's shipping protection choice (above): protected presses
 * Navidium's button so it adds the protection; unprotected removes any protection line and opens checkout directly.
 * Without Navidium it carries on with the normal checkout (other apps' checkout handlers still run).
 */
(function () {
  var scopeOf = function (button) {
    return button.closest('.hhl-cart-summary, .cart__recap, form, section') || document;
  };

  var notesFor = function (button) {
    return scopeOf(button).querySelector('.checkout-notes') || document.querySelector('.cart-drawer .checkout-notes, #mini-cart .checkout-notes, .cart__tax-note .checkout-notes');
  };

  // Unprotected: take any protection line out of the cart, then go straight to checkout (bypassing the checkout
  // button, which Navidium watches to add protection); a build that checks out through the builder app goes on
  // through the button (hhlProtection.toCheckout)
  var checkoutWithout = function (button) {
    var seen = null;

    return HHL.fetchCart()
      .then(function (cart) {
        seen = cart;
        var updates = {}, any = false;

        cart.items.forEach(function (item) {
          if (HHL.isProtectionItem(item)) {
            updates[item.key] = 0;
            any = true;
          }
        });

        return any ? fetch(HHL.cartUrl() + '/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({updates: updates})
        }) : null;
      })
      .catch(function () {})
      .then(function () {
        hhlProtection.toCheckout(button, seen);
      });
  };

  // Checkout, the way the cart says: with or without protection, or the normal checkout without Navidium
  var proceed = function (button) {
    var navidium = hhlProtection.running();

    button.setAttribute('aria-busy', 'true');

    if (navidium && hhlProtection.isOn()) {
      hhlProtection.checkoutProtected(button);
    } else if (navidium) {
      checkoutWithout(button);
    } else {
      button.removeAttribute('aria-busy');
      button.dataset.hhlNotesRead = 'true';
      button.click();
      delete button.dataset.hhlNotesRead;
    }
  };

  var open = function (button, notes) {
    // Inside the cart drawer, so the drawer's focus trap keeps focus in the sheet
    var host = button.closest('cart-drawer, .drawer, #mini-cart') || document.body,
      dialog = document.createElement('dialog');

    dialog.className = 'hhl-checkout-sheet';
    dialog.setAttribute('aria-labelledby', 'hhl-checkout-sheet-title');
    dialog.innerHTML =
      '<div class="hhl-checkout-sheet__panel">' +
        '<p class="hhl-checkout-sheet__title heading" id="hhl-checkout-sheet-title">Before you check out</p>' +
        '<div class="hhl-checkout-sheet__notes"></div>' +
        '<label class="hhl-checkout-sheet__agree"><input type="checkbox"> <span>I’ve read these notes</span></label>' +
        '<button type="button" class="button button--primary button--full hhl-checkout-sheet__go" disabled>Continue to checkout</button>' +
        '<button type="button" class="link hhl-checkout-sheet__back">Back to cart</button>' +
      '</div>';
    dialog.querySelector('.hhl-checkout-sheet__notes').append(notes.cloneNode(true));
    host.append(dialog);

    var agree = dialog.querySelector('input'), go = dialog.querySelector('.hhl-checkout-sheet__go');

    var close = function () {
      dialog.classList.add('is-closing');
      setTimeout(function () {
        dialog.close();
        dialog.remove();
      }, 200);
    };

    agree.addEventListener('change', function () {
      go.disabled = !agree.checked;
    });

    go.addEventListener('click', function () {
      go.disabled = true;
      go.setAttribute('aria-busy', 'true');
      proceed(button);
      // The normal checkout carries on without this sheet; Navidium and the unprotected checkout leave the page
      if (!hhlProtection.running()) {
        dialog.close();
        dialog.remove();
      }
    });

    dialog.querySelector('.hhl-checkout-sheet__back').addEventListener('click', close);
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });
    // A tap on the dimmed area outside the panel closes it
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    dialog.showModal();
    agree.focus();
  };

  // The builder app's cart guard couldn't go on to checkout and shows why, by the Checkout button: the button is
  // usable again, and the sheet (which would cover the message) closes (launch audit 2026-10-25)
  document.addEventListener('hhl:checkout-error', function () {
    document.querySelectorAll('button[name="checkout"][aria-busy="true"]').forEach(function (button) {
      button.removeAttribute('aria-busy');
    });
    document.querySelectorAll('dialog.hhl-checkout-sheet').forEach(function (dialog) {
      if (dialog.open) dialog.close();
      dialog.remove();
    });
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('button[name="checkout"]');

    if (!button || button.dataset.hhlNotesRead) {
      return;
    }

    var notes = notesFor(button), navidium = hhlProtection.running();

    if (!notes && !navidium) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    notes ? open(button, notes) : proceed(button);
  }, true);
})();

/**
 * Parts that don't fit, in the cart (owner 2026-10-27; snippets/hhl-cart-clash.liquid, custom.css "Parts that don't
 * fit together"): "These are for separate builds" adds the bubble's pairs to the cart attribute _hhl_separate and draws
 * the cart again, which then shows the parts apart (snippets/hhl-cart-order.liquid); the drawer's redraw eases them
 * there (hhl-cart-motion.js), the cart page reloads as it does after every change. Pairs whose parts left the cart are
 * dropped from the attribute on the way. The HHL app's cart guard reads the same attribute and says nothing about them.
 */
(function () {
  var KEY = '_hhl_separate';

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-hhl-clash-apart]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    var adding = (button.getAttribute('data-hhl-clash-apart') || '').split(/\s+/).filter(Boolean);
    HHL.fetchCart()
      .then(function (cart) {
        var here = {};
        (cart.items || []).forEach(function (item) { here[item.product_id + ':' + item.variant_id] = true; });
        var kept = String((cart.attributes || {})[KEY] || '').split(/\s+/).filter(function (pair) {
          return pair && pair.split('+').every(function (part) { return here[part]; });
        });
        adding.forEach(function (pair) { if (kept.indexOf(pair) === -1) kept.push(pair); });
        var attributes = {};
        attributes[KEY] = kept.join(' ');
        return fetch(HHL.cartUrl() + '/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({attributes: attributes})
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart update ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (window.themeVariables && themeVariables.settings && themeVariables.settings.pageType === 'cart') {
          window.location.reload();
          return;
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {bubbles: true, detail: {cart: cart}}));
        HHL.refreshCart({cart: cart});
      })
      .catch(function () { button.disabled = false; });
  });
})();

/**
 * Build cards in the cart (snippets/hhl-build-card.liquid; styles: custom.css "Build cards in the cart"): "Details"
 * on a custom build's line eases open the list of its picks. What is open stays open when the drawer is drawn again
 * (hhl-cart-motion.js fires hhl:cart-redrawn right after the theme replaces it) and on the cart page after its reload
 * (sessionStorage), put back at once with no motion: a redraw is not a change. The build's part lines and the
 * "Parts and prices" toggle inside Details belong to the HHL app's cart guard.
 */
(function () {
  var KEY = 'hhl-build-details';
  var open = {};

  try {
    JSON.parse(sessionStorage.getItem(KEY) || '[]').forEach(function (code) { open[code] = true; });
  } catch (error) {
    open = {};
  }

  var save = function () {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(Object.keys(open)));
    } catch (error) {
      // Storage blocked: the card still opens, it just isn't remembered
    }
  };

  var set = function (toggle, isOpen) {
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', String(isOpen));
    if (panel) panel.classList.toggle('is-open', isOpen);
  };

  var restore = function () {
    document.querySelectorAll('[data-hhl-build-details]').forEach(function (toggle) {
      var want = !!open[toggle.getAttribute('data-hhl-build-details')];
      if ((toggle.getAttribute('aria-expanded') === 'true') === want) return;
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.classList.add('is-instant');
      set(toggle, want);
      if (panel) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { panel.classList.remove('is-instant'); });
        });
      }
    });
  };

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-hhl-build-details]');
    if (!toggle) return;
    var code = toggle.getAttribute('data-hhl-build-details');
    var isOpen = toggle.getAttribute('aria-expanded') !== 'true';
    if (isOpen) open[code] = true;
    else delete open[code];
    save();
    set(toggle, isOpen);
  });

  document.addEventListener('hhl:cart-redrawn', restore);
  document.addEventListener('shopify:section:load', restore);
  restore();
})();

/**
 * Store analytics: events for the team's reports. Each is published to Shopify's Customer Events
 * (Shopify.analytics.publish), where the custom pixel "HHL events" (Settings > Customer events; its code is
 * docs/analytics/hhl-cart-events-pixel.js) forwards every hhl_ event to Google Analytics under the same name, with
 * its details. Each is also sent to Microsoft Clarity (its app embed) as a custom event, by name only: in Clarity
 * they filter recordings and heatmaps (Filters > Smart events) and count on its dashboard.
 * Shopify's own standard events (product_added_to_cart, cart_viewed on the cart page, checkout_started, …) keep
 * firing as usual; these add what the cart drawer, docked cart, protection line, Compare and the gift pages do,
 * which Shopify can't see. Other scripts send theirs through HHL.track (assets/hhl-compare.js, assets/hhl-gifts.js);
 * the builder app sends its own hhl_builder_ events (the app's docs/ANALYTICS.md).
 *
 *   hhl_added_to_cart       an item was added   {source, list, list_name, variant_id, product_title, variant_title,
 *                           quantity, price, gift_card, recipient}
 *                           source (how): product_page, sticky_bar, quick_add, card, compare, form (another
 *                           product form, like the gift page's gift card), other
 *                           list (where, or null): compatible_parts (Verified compatible parts, Compare included),
 *                           pairs_well_with (the app's block), gift_page; list_name: the group's or section's name
 *                           gift_card: a gift card; recipient: sent to someone by email (gift cards only)
 *   hhl_cart_viewed         the cart was shown  {source: drawer, docked, peek or page, item_count}
 *                           (docked: the cart beside the page on wide screens, asked for by the cart icon or an add;
 *                           peek: the empty cart shown there by the cart icon)
 *   hhl_cart_emptied        the last item was removed (protection doesn't count as an item)
 *   hhl_builder_tile_opened a builder's tile in a collection grid was opened  {builder (product handle), collection}
 *   hhl_protection_removed  "Agree & remove" on the protection line
 *   hhl_protection_kept     Remove, then "Keep protection" (changed their mind)
 *   hhl_protection_added    protection turned back on
 *   hhl_checkout_clicked    Checkout pressed    {protection: true, false or null (Navidium not running), item_count,
 *                           subtotal}; the share with protection true is the share that kept it
 *
 *   hhl_compare_opened      Compare opened in Verified compatible parts  {list_name, part_count}  (assets/hhl-compare.js)
 *
 *   hhl_gift_page_viewed    the gift page opened  {from: sidebar, menu_sheet, footer, home_strip, product_hint or other}
 *                           (where the Gifts link was pressed: noted on the click, sent once the page is there, so
 *                           leaving the page can't lose it)
 *   hhl_gift_amount_chosen  a gift card amount  {amount, how: button or typed, in_range, place: gift_page,
 *                           product_page or quick_add}; a typed amount outside the card's range has in_range false
 *   hhl_gift_recipient_on   "Send it to someone by email" switched on   {place: gift_page or product_page}
 *   hhl_gift_recipient_off  switched off again                         {place}
 *   hhl_gift_console_chosen a console on the gift page's "Know what they play?"  {console}  (assets/hhl-gifts.js)
 *
 * Cart abandonment isn't an event of its own: it's carts that saw hhl_added_to_cart or hhl_cart_viewed but no
 * checkout_started, worked out in the analytics tool (a funnel). Compare leading to a sale: hhl_compare_opened, then
 * hhl_added_to_cart with source compare. To watch events as they fire, run
 * localStorage.setItem('hhl-debug-analytics', '1') in the browser console.
 */
var hhlTrack = (function () {
  var queue = [], debug = false;

  try { debug = localStorage.getItem('hhl-debug-analytics') === '1'; } catch (error) {}

  var ready = function () {
    return window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function';
  };

  var send = function (name, data) {
    try { Shopify.analytics.publish(name, data); } catch (error) {}
  };

  // Shopify's pixel loader can arrive after this file: hold events until it's there
  var flush = function () {
    if (!ready()) return;
    queue.splice(0).forEach(function (event) { send(event[0], event[1]); });
  };

  window.addEventListener('load', flush);
  setTimeout(flush, 3000);

  return function (name, data) {
    data = data || {};
    if (debug) console.info('[hhl analytics]', name, data);
    ready() ? send(name, data) : queue.push([name, data]);
    // Microsoft Clarity (its app embed): the same event by name, so recordings can be filtered by it
    if (typeof window.clarity === 'function') {
      try { window.clarity('event', name); } catch (error) {}
    }
  };
})();

HHL.track = hhlTrack;

(function () {
  var cart = document.getElementById('mini-cart');

  // Items in the cart, leaving out Navidium's protection (the count as first drawn, then kept up from cart changes)
  var realCount = function (data) {
    return data.items.reduce(function (sum, item) { return sum + (HHL.isProtectionItem(item) ? 0 : item.quantity); }, 0);
  };

  var counter = document.querySelector('cart-count'),
    count = counter ? parseInt(counter.textContent, 10) : NaN;

  // Added to cart, and where from (the button pressed: the sticky bar's names the product form with form="")
  var submitter = null;

  document.addEventListener('submit', function (event) {
    submitter = event.submitter || null;
  }, true);

  document.addEventListener('variant:added', function (event) {
    var target = event.target instanceof Element ? event.target : null,
      item = (event.detail && event.detail.variant) || {},
      from = submitter && target && (submitter.form === target || target.contains(submitter)) ? submitter : target,
      source = 'other',
      where = from ? listOf(from) : {list: null, name: null};

    if (from) {
      if (from.closest('dialog.hhl-compare')) source = 'compare';
      else if (from.closest('.hhl-sticky-inflow')) source = 'sticky_bar';
      else if (from.closest('quick-variants')) source = 'quick_add';
      else if (from.closest('.product-item')) source = 'card';
      else if (from.closest('.product, .product__info, product-form')) source = 'product_page';
      else if (from.form || from.closest('form')) source = 'form'; // another product form (the gift page's gift card)
    }

    submitter = null;

    hhlTrack('hhl_added_to_cart', {
      source: source,
      list: where.list,
      list_name: where.name,
      variant_id: item.variant_id || item.id || null,
      product_title: item.product_title || null,
      variant_title: item.variant_title || null,
      quantity: item.quantity || 1,
      price: typeof item.price === 'number' ? item.price / 100 : null,
      gift_card: !!item.gift_card,
      recipient: !!(item.gift_card && item.properties && item.properties['Recipient email'])
    });
  });

  // The module an add came from: Verified compatible parts (and its Compare, which opens over the page, so it keeps
  // the group's name), the app's "Pairs well with", or a gift page section (with the console picked there)
  function listOf(el) {
    var compare = el.closest('dialog.hhl-compare');
    if (compare) return {list: 'compatible_parts', name: compare.getAttribute('data-hhl-list') || null};

    var card = el.closest('[data-hhl-pairs-card]');
    if (card) return {list: 'compatible_parts', name: card.getAttribute('data-hhl-pairs-title') || null};

    var module = el.closest('.hhl-pairs__module');
    if (module) {
      var heading = module.querySelector('.hhl-pairs__name');
      return {list: 'pairs_well_with', name: heading ? heading.textContent.trim() : null};
    }

    var gifts = el.closest('.hhl-gifts');
    if (gifts) {
      var tab = gifts.querySelector('.hhl-gift-consoles [role="tab"][aria-selected="true"]');
      var name = gifts.classList.contains('hhl-gifts--card') ? 'gift card'
        : gifts.classList.contains('hhl-gifts--picks') ? 'picks'
        : tab ? 'console: ' + tab.textContent.trim() : null;
      return {list: 'gift_page', name: name};
    }

    return {list: null, name: null};
  }

  // Viewed: the drawer opening, the docked cart asked for, or the cart page
  var viewed = function (source) {
    hhlTrack('hhl_cart_viewed', {source: source, item_count: isNaN(count) ? null : count});
  };

  if (cart) {
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.oldValue === null && cart.hasAttribute('open')) viewed('drawer');
      });
    }).observe(cart, {attributes: true, attributeFilter: ['open'], attributeOldValue: true});

    cart.addEventListener('hhl:cart-shown', function (event) { viewed(event.detail); });
  }

  if (/\/cart\/?$/.test(window.location.pathname)) viewed('page');

  // Emptied: the item count falling to zero
  var onCart = function (data) {
    var now = realCount(data);
    if (count > 0 && now === 0) hhlTrack('hhl_cart_emptied', {});
    count = now;
  };

  var onCartEvent = function (event) {
    var data = event.detail && event.detail.cart;
    data && Array.isArray(data.items) ? onCart(data) : HHL.fetchCart().then(onCart).catch(function () {});
  };

  document.documentElement.addEventListener('cart:updated', onCartEvent);
  document.documentElement.addEventListener('cart:refresh', onCartEvent);

  // Protection: removed, kept after all, or added back
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('[data-hhl-protection]')) return;

    if (target.closest('[data-hhl-protection-agree]')) hhlTrack('hhl_protection_removed', {});
    else if (target.closest('[data-hhl-protection-keep]')) hhlTrack('hhl_protection_kept', {});
    else if (target.closest('[data-hhl-protection-add]')) hhlTrack('hhl_protection_added', {});
  });

  // Checkout pressed, with or without protection. Runs before the checkout notes sheet (which presses Checkout again
  // once read), so a press counts once
  var lastCheckout = 0;

  window.addEventListener('click', function (event) {
    var button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
    if (!button || Date.now() - lastCheckout < 10000) return;

    lastCheckout = Date.now();
    var summary = button.closest('.mini-cart__drawer-footer, .hhl-cart-summary, .cart__recap, form'),
      subtotal = summary && summary.querySelector('[data-subtotal]');

    hhlTrack('hhl_checkout_clicked', {
      protection: hhlProtection.running() ? hhlProtection.isOn() : null,
      item_count: isNaN(count) ? null : count,
      subtotal: subtotal ? parseInt(subtotal.dataset.subtotal, 10) / 100 : null
    });
  }, true);
})();

/* Store analytics: the gift-buyer trial (the events are listed above hhlTrack). A Gifts link leaves the page, which
   can lose an event sent on the click, so the click only notes where it was and the gift page sends it once there. */
(function () {
  var KEY = 'hhl-gift-from';

  document.addEventListener('click', function (event) {
    var link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !/^\/pages\/gifts\/?$/.test(link.pathname) || /^\/pages\/gifts\/?$/.test(window.location.pathname)) return;

    var from = link.closest('.hhl-gift-strip') ? 'home_strip'
      : link.closest('.hhl-gift-hint') ? 'product_hint'
      : link.closest('.desktop-sidebar') ? (HHL.mq.sheetMenu && HHL.mq.sheetMenu.matches ? 'menu_sheet' : 'sidebar')
      : link.closest('.footer, [id$="__footer"]') ? 'footer'
      : 'other';

    try { sessionStorage.setItem(KEY, from); } catch (error) {}
  }, true);

  if (/^\/pages\/gifts\/?$/.test(window.location.pathname)) {
    var from = null;
    try { from = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); } catch (error) {}
    hhlTrack('hhl_gift_page_viewed', {from: from || 'other'});
  }

  // "Send it to someone by email" (snippets/hhl-gift-recipient.liquid)
  document.addEventListener('change', function (event) {
    var box = event.target instanceof Element && event.target.matches('.hhl-gift__checkbox') ? event.target : null;
    if (!box) return;
    hhlTrack(box.checked ? 'hhl_gift_recipient_on' : 'hhl_gift_recipient_off', {
      place: box.closest('.hhl-gifts') ? 'gift_page' : 'product_page'
    });
  });
})();

/**
 * Menu sheet (below 1200px): the sidebar menu (snippets/desktop-sidebar.liquid) slides up from the bottom (from the
 * left from 1000 to 1199px, where it keeps its scroll and open groups from page to page). Opened by
 * Menu in the bottom bar (sections/header.liquid) or, from 1000 to 1199px, the header's menu button (which otherwise
 * opens the theme's old drawer). Closes with the close button, a tap outside, Escape, picking a link, or dragging
 * the handle down.
 */
(function () {
  var sheet = document.getElementById('hhl-menu-sheet'),
    narrow = HHL.mq.sheetMenu;

  if (!sheet) {
    return;
  }

  var inner = sheet.querySelector('.desktop-sidebar__inner'),
    head = sheet.querySelector('.desktop-sidebar__sheet-head'),
    toggles = function () { return document.querySelectorAll('[data-hhl-menu-toggle]'); },
    returnFocus = null;

  var setOpen = function (open) {
    if (open === sheet.classList.contains('is-open')) {
      return;
    }

    sheet.classList.toggle('is-open', open);
    open ? sheet.setAttribute('role', 'dialog') : sheet.removeAttribute('role');
    sheet.toggleAttribute('aria-modal', open);
    toggles().forEach(function (toggle) { toggle.setAttribute('aria-expanded', String(open)); });

    if (open) {
      returnFocus = document.activeElement;
      document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'menu'}));
      // The bottom sheet (below 1000px) opens on the part of the menu holding the current page. The side panel (1000 to
      // 1199px) keeps its own scroll: where it was left on this page, or where it was on the last page
      // (snippets/desktop-sidebar.liquid), else the top
      if (HHL.mq.tablet.matches) {
        var current = sheet.querySelector('.is-current-group') || sheet.querySelector('[aria-current="page"]');
        inner.scrollTop = current ? Math.max(0, current.offsetTop - 80) : 0;
      }
      setTimeout(function () { sheet.querySelector('[data-hhl-menu-close]').focus({preventScroll: true}); }, 50);
    } else {
      sheet.style.removeProperty('--hhl-sheet-drag');
      if (returnFocus && returnFocus.focus) returnFocus.focus({preventScroll: true});
    }
  };

  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    if (target.closest('[data-hhl-menu-toggle]')) {
      setOpen(!sheet.classList.contains('is-open'));
      return;
    }

    // The header's menu button (1000 to 1199px): the sheet instead of the theme's old drawer
    if (narrow.matches && target.closest('[aria-controls="mobile-menu-drawer"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(true);
      return;
    }

    if (!sheet.classList.contains('is-open')) {
      return;
    }

    if (target === sheet || target.closest('[data-hhl-menu-close]') || target.closest('#hhl-menu-sheet a[href]')) {
      setOpen(false);
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && sheet.classList.contains('is-open')) {
      setOpen(false);
    }
  });

  // Only one bottom sheet at a time: the cart or search opening closes the menu
  document.addEventListener('hhl:sheet-open', function (event) {
    if (event.detail !== 'menu') setOpen(false);
  });

  HHL.onChange(narrow, function () {
    if (!narrow.matches) setOpen(false);
  });

  // Drag the handle area down to close (HHL.sheetDrag, shared with the cart and the account sheet); only where the
  // menu is a bottom sheet: from 1000 to 1199px it slides out from the left like the cart drawer
  HHL.sheetDrag(head, sheet, function () { setOpen(false); }, HHL.mq.tablet);
})();

/**
 * Country / currency in the menu sheet (below 1000px the header's picker is hidden): "Ships to …" at the sheet's foot
 * swaps the menu for the country list (snippets/desktop-sidebar.liquid, country-choices.liquid), with a back button
 * and a search field in the sheet's header. Picking a country submits Shopify's localization form, which reloads the
 * page in that country's currency. The sheet closing puts the menu back for next time.
 */
(function () {
  var sheet = document.getElementById('hhl-menu-sheet'),
    page = sheet && sheet.querySelector('[data-hhl-country-page]');

  if (!page) {
    return;
  }

  var inner = sheet.querySelector('.desktop-sidebar__inner'),
    head = sheet.querySelector('.desktop-sidebar__sheet-head'),
    title = sheet.querySelector('[data-hhl-sheet-title]'),
    search = sheet.querySelector('[data-hhl-country-search]'),
    empty = page.querySelector('[data-hhl-country-empty]'),
    separator = null,
    choices = [],
    menuTitle = title.textContent;

  // Case and accents don't matter ("cote" finds Côte d'Ivoire); the currency code and symbol count too
  var plain = function (text) {
    return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  };

  // The list is copied in on first use ("Country lists"), so it's read when the page first opens
  var readChoices = function () {
    if (choices.length) return;
    HHL.fillCountries();
    separator = page.querySelector('.hhl-country-separator');
    choices = Array.prototype.slice.call(page.querySelectorAll('.popover__choice-item'));
    choices.forEach(function (choice) {
      choice._hhlText = plain(choice.textContent.replace(/\s+/g, ' '));
    });
  };

  var filter = function () {
    var query = plain(search.value), shown = 0;
    readChoices();

    choices.forEach(function (choice) {
      var match = !query || choice._hhlText.indexOf(query) !== -1;
      choice.hidden = !match;
      if (match) shown++;
    });

    if (separator) separator.hidden = !!query;
    empty.hidden = shown > 0;
  };

  // A short slide in from the side it comes from (forward from the right, back from the left)
  var slideIn = function (forward) {
    if (HHL.mq.reducedMotion.matches || !inner.animate) return;

    Array.prototype.forEach.call(inner.children, function (part) {
      if (part === head || getComputedStyle(part).display === 'none') return;
      part.animate([
        {opacity: 0, transform: 'translateX(' + (forward ? 24 : -24) + 'px)'},
        {opacity: 1, transform: 'none'}
      ], {duration: 260, easing: HHL.ease});
    });
  };

  var show = function (on, animate) {
    if (on === sheet.classList.contains('is-country')) return;

    if (on) readChoices();
    sheet.classList.toggle('is-country', on);
    page.hidden = !on;
    title.textContent = on ? page.getAttribute('data-title') : menuTitle;
    search.tabIndex = on ? 0 : -1;
    inner.scrollTop = 0;

    if (!on) {
      search.value = '';
      filter();
    }

    if (animate !== false) slideIn(on);
  };

  sheet.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-hhl-country-open]')) {
      show(true);
    } else if (target.closest('[data-hhl-country-back]')) {
      show(false);
    }
  });

  search.addEventListener('input', filter);

  // Enter picks the first match (the field isn't part of the form, so it would otherwise do nothing)
  search.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    var first = choices.filter(function (choice) { return !choice.hidden; })[0];
    if (first) first.click();
  });

  // Once the sheet has slid shut, the menu is back for next time
  new MutationObserver(function () {
    if (!sheet.classList.contains('is-open') && sheet.classList.contains('is-country')) {
      setTimeout(function () {
        if (!sheet.classList.contains('is-open')) show(false, false);
      }, 350);
    }
  }).observe(sheet, {attributes: true, attributeFilter: ['class']});
})();

/**
 * Search suggestions shared by the bottom bar search (phones) and the header search (desktop): fetches Shopify's
 * suggestions for a query and fills a results element (products, search shortcuts, suggestions, categories, pages).
 */
function hhlSearchSuggest(form, results) {
  var hint = results.innerHTML, request = 0;

  var escape = function (value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c];
    });
  };

  // Suggestions give prices as amounts ("12.99"), not cents
  var money = function (value) {
    var amount = parseFloat(value);
    return isNaN(amount) ? '' : HHL.money(Math.round(amount * 100));
  };

  var sized = function (url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  };

  // "words | title | url" per line (Theme settings > Search shortcuts)
  var shortcuts = (form.getAttribute('data-shortcuts') || '').split(/\r?\n/).map(function (line) {
    var parts = line.split('|').map(function (part) { return part.trim(); });
    return parts.length === 3 && parts[2] ? {words: parts[0].toLowerCase().split(/\s*,\s*/).filter(Boolean), title: parts[1], url: parts[2]} : null;
  }).filter(Boolean);

  var shortcutsFor = function (query) {
    var q = query.toLowerCase().trim();
    if (q.length < 3) return [];
    return shortcuts.filter(function (shortcut) {
      return shortcut.words.some(function (word) { return word.indexOf(q) === 0 || q.split(/\s+/).indexOf(word) > -1; });
    }).slice(0, 2);
  };

  // A console in the categories lands on its Explore page (the console list: sections/header.liquid, snippets/explore-url.liquid)
  var explore = [];
  try { explore = JSON.parse((document.querySelector('[data-hhl-explore]') || {}).textContent || '[]'); } catch (error) { explore = []; }
  var landing = function (url) {
    var path = String(url || '').split('?')[0].split('#')[0];
    return explore.indexOf(path) > -1 ? path + '?view=explore' : url;
  };

  var chips = function (heading, items, label) {
    return items.length ? '<div><p class="hhl-search-sheet__heading">' + heading + '</p><div class="hhl-search-sheet__chips">' +
      items.map(function (item) {
        return '<a class="hhl-search-sheet__chip" href="' + escape(item.url) + '">' + label(item) + '</a>';
      }).join('') + '</div></div>' : '';
  };

  // Products first, then search suggestions, categories, and pages and guides (e.g. "rewards" finds the rewards page)
  var render = function (query, data) {
    var found = (data && data.resources && data.resources.results) || {},
      products = (found.products || []).slice(0, 6),
      queries = (found.queries || []).filter(function (item) { return item.text.toLowerCase() !== query.toLowerCase(); }).slice(0, 4),
      // Leave out the loyalty app's placeholder collections
      collections = (found.collections || []).filter(function (item) { return !/no-reward/i.test(item.title + ' ' + item.handle); }).slice(0, 3),
      pages = (found.pages || []).concat(found.articles || []).slice(0, 4),
      html = '',
      hits = shortcutsFor(query);

    // Search shortcuts (Theme settings): a word like "rewards" jumps straight to its page
    if (hits.length) {
      html += '<ul class="hhl-search-sheet__list" role="list">' + hits.map(function (hit) {
        return '<li><a class="hhl-search-sheet__shortcut" href="' + escape(hit.url) + '">' + escape(hit.title) + '<span aria-hidden="true">→</span></a></li>';
      }).join('') + '</ul>';
    }

    if (products.length) {
      html += '<div><p class="hhl-search-sheet__heading">Products</p><ul class="hhl-search-sheet__list" role="list">' +
        products.map(function (item) {
          var image = item.featured_image && item.featured_image.url;
          return '<li><a class="hhl-search-sheet__item' + (item.available === false ? ' hhl-search-sheet__item--sold-out' : '') + '" href="' + escape(item.url) + '">' +
            '<span class="hhl-search-sheet__media">' + (image ? '<img src="' + escape(sized(image, 160)) + '" alt="" width="56" height="56" loading="lazy">' : '') + '</span>' +
            '<span><span class="hhl-search-sheet__title">' + escape(item.title) + '</span>' +
            '<span class="hhl-search-sheet__price">' + (item.available === false ? 'Sold out' : escape(money(item.price))) + '</span></span></a></li>';
        }).join('') + '</ul></div>';
    }

    html += chips('Suggestions', queries, function (item) { return item.styled_text || escape(item.text); });
    html += chips('Categories', collections.map(function (item) { return Object.assign({}, item, {url: landing(item.url)}); }), function (item) { return escape(item.title); });
    html += chips('Pages &amp; guides', pages, function (item) { return escape(item.title); });

    html += html
      ? '<a class="button button--primary button--full hhl-search-sheet__all" href="' + escape(resultsUrl(query)) + '">See all results</a>'
      : '<p class="hhl-search-sheet__hint">No results for “' + escape(query) + '”. Try a console or part name.</p>';

    results.innerHTML = html;
  };

  // The full results page for a query, with the form's other fields (type=product)
  var resultsUrl = function (query) {
    var params = new URLSearchParams();
    Array.prototype.forEach.call(form.elements, function (field) {
      if (field.name && field.type === 'hidden') params.set(field.name, field.value);
    });
    params.set('q', query);
    return form.action + '?' + params.toString();
  };

  var search = function (query) {
    var id = ++request;
    query = String(query || '').trim();

    if (!query) {
      results.innerHTML = hint;
      return;
    }

    var url = HHL.root() + 'search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product,query,collection,page,article&resources[limit]=6&resources[options][unavailable_products]=last';

    fetch(url).then(function (response) { return response.json(); }).then(function (data) {
      if (id === request) render(query, data);
    }).catch(function () {});
  };

  return {search: search, reset: function () { request++; results.innerHTML = hint; }};
}

/**
 * Phones' bottom sheets, one at a time: the cart, the menu and search each slide up from the bottom bar, and opening
 * one closes the others. Each sheet announces itself with the hhl:sheet-open event (detail: 'cart', 'menu' or
 * 'search'); this joins the cart drawer in.
 */
(function () {
  var miniCart = document.getElementById('mini-cart');
  if (!miniCart) return;

  document.addEventListener('openable-element:open', function (event) {
    if (event.target === miniCart) document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'cart'}));
  });

  document.addEventListener('hhl:sheet-open', function (event) {
    if (event.detail !== 'cart' && miniCart.hasAttribute('open')) miniCart.open = false;
  });

  // Drag its head down to close, like the menu (HHL.sheetDrag); the head is redrawn with the cart, so it's looked up
  // again each time the cart opens
  var narrow = HHL.mq.tablet;
  document.addEventListener('openable-element:open', function (event) {
    if (event.target !== miniCart || !narrow.matches) return;
    HHL.sheetDrag(miniCart.querySelector('.drawer__header'), miniCart, function () { miniCart.open = false; });
  });
})();

/**
 * Bottom bar's cart badge (sections/header.liquid): hidden at zero (marked empty in the page itself, so an empty cart
 * never flashes a "0" first). The theme keeps the count's text up to date.
 */
document.querySelectorAll('.hhl-tabbar__count').forEach(function (count) {
  var sync = function () { count.toggleAttribute('data-empty', count.textContent.trim() === '0'); };
  sync();
  new MutationObserver(sync).observe(count, {childList: true, characterData: true, subtree: true});
});

/**
 * Bottom bar search (below 1000px, sections/header.liquid): tapping the pill widens it across the bar into a search
 * field, and results (Shopify's search suggestions: products, queries, collections, pages and blog posts) rise from behind the bar as you
 * type. Enter or the magnifying glass opens the full results page. The bar rides up with the on-screen keyboard.
 * Closes with the close button, a tap outside, or Escape.
 */
(function () {
  var bar = document.querySelector('.hhl-tabbar'),
    form = document.querySelector('[data-hhl-search]'),
    sheet = document.getElementById('hhl-search-sheet');

  if (!bar || !form || !sheet) {
    return;
  }

  var input = form.querySelector('input[name="q"]'),
    cancel = bar.querySelector('[data-hhl-search-close]'),
    clear = form.querySelector('[data-hhl-search-clear]'),
    results = sheet.querySelector('[data-hhl-search-results]'),
    engine = hhlSearchSuggest(form, results),
    timer = null,
    searching = function () { return bar.classList.contains('is-searching'); };

  // While searching, the bar (and the results' bottom edge) ride up above the on-screen keyboard (HHL.keyboardLift; the
  // bar slides via --hhl-keyboard in custom.css). The page itself isn't resized, so full-height parts don't jump.
  var keyboardLift = HHL.keyboardLift({
    bottom: function () { return bar.offsetTop + bar.offsetHeight; },
    apply: function (lift) {
      document.documentElement.style.setProperty('--hhl-keyboard', lift + 'px');
      // On the keyboard the bar no longer needs room for the phone's gesture bar (custom.css)
      document.documentElement.classList.toggle('hhl-keyboard-up', lift > 0);
    }
  });

  var open = function () {
    if (searching()) return;
    document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'search'}));
    bar.classList.add('is-searching');
    sheet.classList.add('is-open');
    input.tabIndex = 0;
    cancel.tabIndex = 0;
    keyboardLift.start();
    input.focus({preventScroll: true});
    if (input.value.trim()) search();
  };

  var search = function () { engine.search(input.value); };

  var close = function () {
    if (!searching()) return;
    bar.classList.remove('is-searching');
    sheet.classList.remove('is-open');
    input.tabIndex = -1;
    cancel.tabIndex = -1;
    input.blur();
    keyboardLift.stop();
  };

  input.addEventListener('input', function () {
    clear.hidden = !input.value;
    clearTimeout(timer);
    timer = setTimeout(search, 160);
  });

  cancel.addEventListener('click', close);

  // Clear only empties the field (search stays open)
  clear.addEventListener('click', function (event) {
    event.preventDefault();
    event.stopPropagation();
    input.value = '';
    clear.hidden = true;
    engine.reset();
    input.focus();
  });

  form.addEventListener('click', function (event) {

    // Collapsed, the whole pill (magnifying glass included) opens search
    if (!searching()) {
      event.preventDefault();
      open();
    }
  });

  form.addEventListener('submit', function (event) {
    if (!input.value.trim()) {
      event.preventDefault();
      input.focus();
    }
  });

  // A tap on the dimmed page above the results closes search
  sheet.addEventListener('click', function (event) {
    if (event.target === sheet) close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && searching()) close();
  });

  document.addEventListener('hhl:sheet-open', function (event) {
    if (event.detail !== 'search') close();
  });

  // Focus coming back to the field (after Clear, say) brings the keyboard back: measure again
  input.addEventListener('focus', function () {
    if (searching()) keyboardLift.start();
  });
})();

/**
 * Header search (1000px and up, sections/header.liquid): the pill in the middle of the header. Focusing it widens it,
 * and typing drops a results panel down from it (the same results as the phone search); Enter opens the full results
 * page. Closes with Escape, a click elsewhere, or when another sheet (the cart) opens.
 */
(function () {
  var root = document.querySelector('[data-hhl-desk-search]');

  if (!root) {
    return;
  }

  var form = root.querySelector('form'),
    input = form.querySelector('input[name="q"]'),
    clear = root.querySelector('[data-hhl-desk-search-clear]'),
    results = root.querySelector('[data-hhl-desk-search-results]'),
    engine = hhlSearchSuggest(form, results),
    timer = null;

  var isOpen = function () { return root.classList.contains('is-open'); };

  var open = function () {
    if (isOpen()) return;
    document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'desk-search'}));
    root.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  };

  var close = function () {
    if (!isOpen()) return;
    root.classList.remove('is-open');
    input.setAttribute('aria-expanded', 'false');
  };

  var sync = function () {
    root.classList.toggle('has-query', !!input.value);
  };

  sync();

  // Focusing shows the results for what's in the field (on the search page, the current search)
  input.addEventListener('focus', function () {
    root.classList.add('is-focused');
    if (input.value.trim()) {
      open();
      engine.search(input.value);
    }
  });

  input.addEventListener('blur', function () {
    root.classList.remove('is-focused');
  });

  input.addEventListener('input', function () {
    sync();
    clearTimeout(timer);

    if (!input.value.trim()) {
      engine.reset();
      close();
      return;
    }

    open();
    timer = setTimeout(function () { engine.search(input.value); }, 160);
  });

  clear.addEventListener('click', function (event) {
    event.preventDefault();
    input.value = '';
    sync();
    engine.reset();
    close();
    input.focus();
  });

  form.addEventListener('submit', function (event) {
    if (!input.value.trim()) event.preventDefault();
  });

  document.addEventListener('pointerdown', function (event) {
    if (isOpen() && !root.contains(event.target)) close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && (isOpen() || document.activeElement === input)) {
      close();
      input.blur();
    }

    // "/" jumps to the search field, as on many shops and docs sites
    if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !HHL.mq.tablet.matches) {
      var target = event.target;
      if (target === input || (target.closest && target.closest('input, textarea, select, [contenteditable="true"]'))) return;
      event.preventDefault();
      input.focus();
    }
  });

  document.addEventListener('hhl:sheet-open', function (event) {
    if (event.detail !== 'desk-search') close();
  });
})();

/**
 * Searching: the moment a search is sent (the header search or the bottom bar's), a progress bar runs along the top
 * and the page dims, so it's clear the results are on their way while the next page loads.
 */
document.addEventListener('submit', function (event) {
  var form = event.target;

  if (!(form instanceof HTMLFormElement) || !form.matches('.hhl-desk-search__form, [data-hhl-search]') || event.defaultPrevented) {
    return;
  }

  var input = form.querySelector('input[name="q"]');
  if (!input || !input.value.trim()) return;

  document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'searching'}));
  document.documentElement.classList.add('hhl-searching');
});

document.addEventListener('click', function (event) {
  var link = event.target instanceof Element && event.target.closest('.hhl-search-sheet__all');
  if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    document.documentElement.classList.add('hhl-searching');
  }
});

// Coming back to this page (the browser's back button keeps it as it was): undo the searching state
window.addEventListener('pageshow', function () {
  document.documentElement.classList.remove('hhl-searching');
});

/**
 * Searches without reloading the page: sending a search (the header search, the bottom bar's, or "See all results")
 * fetches only the results and swaps them into the page; the header, menu, cart and the theme's scripts stay as they
 * are. On the search page only the results section is fetched (Shopify's section rendering); from other pages the
 * search page's main area is. The address bar shows the search, so Back, Forward, sharing and refreshing behave as
 * usual, and the page the search started from comes back as it was.
 * Recent searches are kept in memory (a few minutes), so going back to one or searching it again is instant, and a
 * search is fetched ahead while the shopper pauses typing, so Enter usually has it ready.
 */
(function () {
  var main = document.getElementById('main');

  // Not in the theme editor, for the same reason as collection pages above: searches there load the page normally
  if (!main || !window.history || !window.fetch || !window.DOMParser || (window.Shopify && Shopify.designMode)) {
    return;
  }

  var SECTION_KEY = 'hhl-search-section',
    FRESH = 3 * 60 * 1000,
    cache = new Map(),
    pages = new Map(),
    loading = null;

  var searchPath = function () {
    return HHL.root() + 'search';
  };

  var isSearchUrl = function (url) {
    return new URL(url, window.location.href).pathname.replace(/\/$/, '') === searchPath().replace(/\/$/, '');
  };

  // The search page's results section (its id comes from the page; remembered for searches from other pages)
  var currentSection = function () {
    return main.querySelector('.shopify-section--main-search');
  };

  var sectionId = function () {
    var section = currentSection(), id = section && section.id.replace('shopify-section-', '');
    try {
      if (id) localStorage.setItem(SECTION_KEY, id);
      return id || localStorage.getItem(SECTION_KEY);
    } catch (error) {
      return id;
    }
  };

  var normalize = function (url) {
    var parsed = new URL(url, window.location.href);
    ['section_id', 'pg'].forEach(function (name) { parsed.searchParams.delete(name); });
    return parsed.pathname + parsed.search;
  };

  // The search's results, fetched once and kept in memory for a few minutes
  var load = function (url) {
    var key = normalize(url), hit = cache.get(key);

    if (hit && Date.now() - hit.time < FRESH) {
      return hit.promise;
    }

    // Just the results section once its id is known (from any search page seen in this browser); the first time, the
    // whole page
    var id = sectionId(), request = new URL(key, window.location.href);
    if (id) request.searchParams.set('section_id', id);

    var promise = fetch(request.toString(), {credentials: 'same-origin'})
      .then(function (response) { return response.ok ? response.text() : Promise.reject(response.status); })
      .then(function (html) { return {html: html, section: !!id}; });

    promise.catch(function () { cache.delete(key); });
    cache.set(key, {time: Date.now(), promise: promise});

    // Keep the memory small: the 20 latest searches
    if (cache.size > 20) cache.delete(cache.keys().next().value);

    return promise;
  };

  var queryOf = function (url) {
    return new URL(url, window.location.href).searchParams.get('q') || '';
  };

  // The search fields show the search on screen (without opening their suggestions), and their panels close
  var syncFields = function (query) {
    document.querySelectorAll('.hhl-desk-search__input, .hhl-tabbar__search-input').forEach(function (input) {
      input.value = query;
      input.blur();
    });
    document.querySelectorAll('.hhl-desk-search').forEach(function (box) { box.classList.toggle('has-query', !!query); });
    document.querySelectorAll('[data-hhl-search-clear]').forEach(function (clear) { clear.hidden = !query; });
    document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'searching'}));
  };

  // The "you are here" marks in the menu (snippets/desktop-sidebar.liquid) and the bottom bar (Home, Account; in
  // sections/header.liquid): none on a search
  var menuMarks = function () {
    return Array.from(document.querySelectorAll('.desktop-sidebar .is-active, .desktop-sidebar [aria-current="page"], .desktop-sidebar .is-current-group, .hhl-tabbar [aria-current="page"]')).map(function (element) {
      return {element: element, active: element.classList.contains('is-active'), current: element.getAttribute('aria-current'), group: element.classList.contains('is-current-group')};
    });
  };

  var clearMenuMarks = function () {
    menuMarks().forEach(function (mark) {
      mark.element.classList.remove('is-active', 'is-current-group');
      mark.element.removeAttribute('aria-current');
    });
  };

  var restoreMenuMarks = function (marks) {
    clearMenuMarks();
    (marks || []).forEach(function (mark) {
      mark.element.classList.toggle('is-active', mark.active);
      mark.element.classList.toggle('is-current-group', mark.group);
      if (mark.current) mark.element.setAttribute('aria-current', mark.current);
    });
  };

  // Remember the page a search started from, so Back brings it back as it was
  var keepPage = function () {
    if (isSearchUrl(window.location.href)) return;
    pages.set(normalize(window.location.href), {
      nodes: Array.from(main.childNodes),
      bodyClass: document.body.className,
      title: document.title,
      scroll: window.scrollY,
      marks: menuMarks()
    });
  };

  var show = function (url, result) {
    var doc = new DOMParser().parseFromString(result.html, 'text/html'),
      query = queryOf(url);

    if (result.section && currentSection()) {
      var fresh = doc.querySelector('.shopify-section--main-search') || doc.body.firstElementChild;
      if (!fresh) throw new Error('No results section');
      currentSection().replaceWith(document.adoptNode(fresh));
    } else {
      var freshMain = doc.getElementById('main');

      if (!freshMain) {
        // The results section into the main area, with the phone filter bar (kept ready in layout/theme.liquid) or the
        // one already on the page (collections)
        var section = doc.querySelector('.shopify-section--main-search') || doc.body.firstElementChild,
          toolbar = document.getElementById('mobile-facet-toolbar'),
          template = document.getElementById('hhl-facet-toolbar');
        if (!section) throw new Error('No results');
        if (!toolbar && template) toolbar = template.content.firstElementChild.cloneNode(true);
        main.replaceChildren.apply(main, (toolbar ? [toolbar] : []).concat(document.adoptNode(section)));
      } else {
        Array.from(freshMain.querySelectorAll('script')).forEach(function (script) { script.remove(); });
        main.replaceChildren.apply(main, Array.from(freshMain.childNodes).map(function (node) { return document.adoptNode(node); }));
      }

      document.body.className = document.body.className.replace(/\btemplate-[\w-]+/g, '').trim() + ' template-search';
    }

    document.title = (doc.title && doc.title.trim()) || ('Search: ' + query);
    clearMenuMarks();
    syncFields(query);
    sectionId();
  };

  var go = function (url, push) {
    var key = normalize(url), token = {};

    loading = token;
    document.documentElement.classList.add('hhl-searching');

    // The pager's count of products in stock, alongside the results
    if (window.hhlSearchStockCount) window.hhlSearchStockCount(key).catch(function () {});

    return load(url).then(function (result) {
      if (loading !== token) return;

      if (push) {
        keepPage();
        window.history.pushState({hhlSearch: key}, '', key);
      }

      show(key, result);
      window.scrollTo({top: 0});
    }).catch(function () {
      if (loading === token) window.location.href = url;
    }).then(function () {
      if (loading === token) {
        loading = null;
        document.documentElement.classList.remove('hhl-searching');
      }
    });
  };

  var urlFor = function (form) {
    var url = new URL(form.action, window.location.href);
    new FormData(form).forEach(function (value, name) { url.searchParams.set(name, value); });
    return url.pathname + url.search;
  };

  // Sending a search: in place
  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches('.hhl-desk-search__form, [data-hhl-search]') || event.defaultPrevented) {
      return;
    }

    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;

    event.preventDefault();
    go(urlFor(form), true);
  });

  document.addEventListener('click', function (event) {
    var link = event.target instanceof Element && event.target.closest('.hhl-search-sheet__all');

    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    go(link.getAttribute('href'), true);
  });

  // Back and Forward: searches from memory, and the page a search started from as it was
  window.addEventListener('popstate', function () {
    var key = normalize(window.location.href);

    if (isSearchUrl(key)) {
      go(key, false);
      return;
    }

    var page = pages.get(key);

    if (!page) {
      window.location.reload();
      return;
    }

    loading = null;
    document.documentElement.classList.remove('hhl-searching');
    main.replaceChildren.apply(main, page.nodes);
    document.body.className = page.bodyClass;
    document.title = page.title;
    restoreMenuMarks(page.marks);
    window.scrollTo({top: page.scroll});
    syncFields('');
  });

  // The page as first loaded is a history entry too, so Back to it can be told apart
  if (!window.history.state) {
    window.history.replaceState({hhlSearch: isSearchUrl(window.location.href) ? normalize(window.location.href) : null}, '');
  }

  // Ahead of Enter: while the shopper pauses typing, fetch that search into memory
  var aheadTimer = null;

  document.addEventListener('input', function (event) {
    var input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.matches('.hhl-desk-search__input, .hhl-tabbar__search-input') || !event.isTrusted) {
      return;
    }

    clearTimeout(aheadTimer);
    var query = input.value.trim();
    if (query.length < 3) return;

    aheadTimer = setTimeout(function () {
      load(urlFor(input.form));
    }, 600);
  });

  // Keep the search page as first loaded in memory, so Back to it from a later search is instant
  if (isSearchUrl(window.location.href) && currentSection() && sectionId()) {
    cache.set(normalize(window.location.href), {time: Date.now(), promise: Promise.resolve({html: currentSection().outerHTML, section: true})});
  }
})();

/**
 * Search page numbers (snippets/hhl-server-pager.liquid). Shopify lists sold-out products last, so the numbers end at
 * the last page with products in stock: the search is counted again filtered to in stock (the "hhl-count" view,
 * templates/search.hhl-count.liquid, which prints just the number; kept in memory per search), and "Sold out" leads to
 * the page where sold-out products begin. The pages after the last one in stock show their numbers once you're on one.
 * The numbers are fitted to the space: all of them when they fit, otherwise the first, the last and the current
 * page's neighbors, with "…" between. The pager fades in once it's counted, so the numbers never jump.
 */
(function () {
  var counts = new Map(),
    watched = new WeakSet();

  var countKey = function (url) {
    var parsed = new URL(url, window.location.href);
    ['page', 'section_id', 'view', 'pg'].forEach(function (name) { parsed.searchParams.delete(name); });
    return parsed.pathname + parsed.search;
  };

  // The number of products in stock for a search (its filters included)
  var inStock = function (url) {
    var key = countKey(url);

    if (!counts.has(key)) {
      var request = new URL(key, window.location.href);
      request.searchParams.set('filter.v.availability', '1');
      request.searchParams.set('view', 'hhl-count');

      var promise = fetch(request.toString(), {credentials: 'same-origin'})
        .then(function (response) { return response.ok ? response.text() : Promise.reject(response.status); })
        .then(function (text) {
          var count = parseInt(text.trim(), 10);
          return isNaN(count) ? Promise.reject('no count') : count;
        });

      promise.catch(function () { counts.delete(key); });
      counts.set(key, promise);
    }

    return counts.get(key);
  };

  // In-place searches fetch the count alongside the results (custom.js, "Searches without reloading the page")
  window.hhlSearchStockCount = inStock;

  var fit = function (pager) {
    var current = parseInt(pager.getAttribute('data-current'), 10) || 1,
      pages = parseInt(pager.getAttribute('data-pages'), 10) || 1,
      stockLast = parseInt(pager.getAttribute('data-stock-last'), 10) || pages,
      last = current <= stockLast ? stockLast : pages,
      nav = pager.querySelector('.hhl-pager__nav'),
      numbers = Array.from(pager.querySelectorAll('[data-number]'));

    if (!nav) return;

    var show = function (reach) {
      pager.querySelectorAll('.hhl-pager__gap[data-fit]').forEach(function (gap) { gap.remove(); });

      var previous = 0;
      numbers.forEach(function (number) {
        var n = parseInt(number.getAttribute('data-number'), 10),
          visible = n <= last && (reach === Infinity || n === 1 || n === last || Math.abs(n - current) <= reach);

        number.hidden = !visible;

        if (visible) {
          if (previous && n > previous + 1) {
            var gap = document.createElement('span');
            gap.className = 'hhl-pager__gap';
            gap.setAttribute('data-fit', '');
            gap.setAttribute('aria-hidden', 'true');
            gap.textContent = '…';
            number.before(gap);
          }
          previous = n;
        }
      });

      return nav.scrollWidth <= pager.clientWidth + 1;
    };

    [Infinity, 3, 2, 1, 0].some(show);
  };

  var setUp = function (pager) {
    if (watched.has(pager)) return;
    watched.add(pager);

    var settle = function () {
      fit(pager);
      pager.classList.remove('is-counting');

      if (window.ResizeObserver) {
        var width = 0;
        var observer = new ResizeObserver(function (entries) {
          // A newer search replaced this pager: stop watching
          if (!pager.isConnected) {
            observer.disconnect();
            return;
          }

          var next = Math.round(entries[0].contentRect.width);
          if (next !== width) {
            width = next;
            fit(pager);
          }
        });
        observer.observe(pager.parentElement || pager);
      }
    };

    if (!pager.hasAttribute('data-count-in-stock')) {
      settle();
      return;
    }

    pager.classList.add('is-counting');

    inStock(window.location.href).then(function (count) {
      var pages = parseInt(pager.getAttribute('data-pages'), 10) || 1,
        size = parseInt(pager.getAttribute('data-page-size'), 10) || 24,
        current = parseInt(pager.getAttribute('data-current'), 10) || 1,
        stockLast = Math.min(pages, Math.max(1, Math.ceil(count / size))),
        soldOutStart = Math.min(pages, Math.floor(count / size) + 1),
        soldOut = pager.querySelector('.hhl-pager__soldout');

      pager.setAttribute('data-stock-last', stockLast);

      if (soldOut) {
        var url = new URL(window.location.href);
        url.searchParams.set('page', soldOutStart);
        soldOut.setAttribute('data-page', soldOutStart);
        soldOut.setAttribute('href', url.pathname + url.search);
        soldOut.hidden = !(stockLast < pages && current <= stockLast);
      }
    }).catch(function () {}).then(settle);
  };

  var setUpAll = function () {
    document.querySelectorAll('.hhl-pager--server').forEach(setUp);
  };

  // On load, and whenever results are swapped in (filters, pages, searches): only new pagers (not the "…" this adds)
  var queued = false;
  new MutationObserver(function (records) {
    var arrived = records.some(function (record) {
      return Array.prototype.some.call(record.addedNodes, function (node) {
        return node.nodeType === 1 && (node.matches('.hhl-pager--server') || !!node.querySelector('.hhl-pager--server'));
      });
    });
    if (queued || !arrived) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      setUpAll();
    });
  }).observe(document.body, {childList: true, subtree: true});

  setUpAll();
})();

/**
 * "Search again" / "Start searching" on the search page (sections/main-search.liquid): opens the header search on
 * desktop, or the bottom bar's search on phones and tablets.
 */
document.addEventListener('click', function (event) {
  var trigger = event.target instanceof Element && event.target.closest('[data-hhl-open-search]');

  if (!trigger) {
    return;
  }

  var desk = document.querySelector('.hhl-desk-search__input');

  if (desk && desk.offsetParent) {
    desk.focus();
    desk.select();
  } else {
    var pill = document.querySelector('[data-hhl-search]');
    if (pill) pill.click();
  }
});

/**
 * Phones: products per row (2 or 3) or a compact list, from the switch beside the item count
 * (sections/main-collection.liquid). 3 per row by default; the choice is remembered on this device and applied before
 * the page draws (layout/theme.liquid).
 */
(function () {
  var KEY = 'hhl-grid-density', root = document.documentElement, CHOICES = ['2', '3', 'list'], density = '3';

  try {
    density = CHOICES.indexOf(localStorage.getItem(KEY)) > -1 ? localStorage.getItem(KEY) : '3';
  } catch (error) {}

  var apply = function () {
    CHOICES.forEach(function (choice) { root.classList.toggle('hhl-grid-' + choice, choice === density); });
    // Desktop has no 2 per row: a saved 2 shows as the grid
    var shown = density === '2' && !HHL.mq.phone.matches ? '3' : density;
    document.querySelectorAll('[data-hhl-density]').forEach(function (option) {
      option.setAttribute('aria-pressed', String(option.getAttribute('data-hhl-density') === shown));
    });
  };

  apply();

  document.addEventListener('click', function (event) {
    var option = event.target instanceof Element ? event.target.closest('[data-hhl-density]') : null;

    if (!option) {
      return;
    }

    density = option.getAttribute('data-hhl-density');

    try {
      localStorage.setItem(KEY, density);
    } catch (error) {}

    apply();
    // The pager fills whole rows: let it re-measure the grid
    window.dispatchEvent(new Event('resize'));
  });

  // A new page of results (the pager, filters, a search or collection opened in place) brings a fresh switch, marked
  // with the default: mark the chosen layout on it before it's drawn
  var main = document.getElementById('main');

  if (main && window.MutationObserver) {
    new MutationObserver(function () {
      var shown = density === '2' && !HHL.mq.phone.matches ? '3' : density,
        stale = main.querySelector('[data-hhl-density][aria-pressed="true"]:not([data-hhl-density="' + shown + '"])');
      if (stale) apply();
    }).observe(main, {childList: true, subtree: true});
  }
})();

/**
 * Phones and tablets: the notice bar shows one line with a chevron; a tap slides the whole message open (and closed).
 */
(function () {
  var bar = document.querySelector('.announcement-bar');

  if (!bar) {
    return;
  }

  var toggle = function (message) {
    // Animate to the message's real height
    bar.style.setProperty('--hhl-notice-height', message.scrollHeight + 'px');
    var expanded = bar.classList.toggle('is-expanded');
    message.setAttribute('aria-expanded', String(expanded));
  };

  bar.querySelectorAll('.announcement-bar__message p').forEach(function (message) {
    message.setAttribute('role', 'button');
    message.setAttribute('tabindex', '0');
    message.setAttribute('aria-expanded', 'false');
    message.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle(message);
      }
    });
  });

  bar.addEventListener('click', function (event) {
    var message = event.target.closest('.announcement-bar__message p');

    if (!message || event.target.closest('a, button') || !HHL.mq.tablet.matches) {
      return;
    }

    toggle(message);
  });
})();

/**
 * Review stars go to the reviews: a card's stars open the product at #reviews, and on a product page the stars by
 * the title scroll down to the Judge.me reviews. Judge.me draws its widget after the page loads, so arriving with
 * #reviews waits (a few seconds at most) for it before scrolling.
 */
(function () {
  var target = function () {
    return document.querySelector('.jdgm-widget.jdgm-review-widget, #judgeme_product_reviews');
  };

  var go = function (smooth) {
    var reviews = target();
    if (reviews) reviews.scrollIntoView({behavior: smooth ? 'smooth' : 'auto', block: 'start'});
    return !!reviews;
  };

  document.addEventListener('click', function (event) {
    var badge = event.target instanceof Element ? event.target.closest('.product-meta__reviews-badge') : null;

    if (badge && go(true)) {
      event.preventDefault();
    }
  });

  if (location.hash === '#reviews') {
    var tries = 0;
    var wait = setInterval(function () {
      // Once the widget has its content (not just its empty shell)
      var reviews = target();
      if ((reviews && reviews.offsetHeight > 80) || ++tries > 60) {
        clearInterval(wait);
        go(true);
      }
    }, 100);
  }
})();

/**
 * Collection header: the item count sits right after the title, small; when the title leaves no room for it, it is
 * hidden rather than squeezed (sections/main-collection.liquid and main-search.liquid, custom.css)
 */
(function () {
  var fit = function () {
    var count = document.querySelector(':is(.template-collection, .template-search) .product-facet__meta-bar-item--count');

    if (!count) {
      return;
    }

    count.classList.remove('is-cramped');
    count.classList.toggle('is-cramped', count.scrollWidth > count.clientWidth + 1);
  };

  fit();
  window.addEventListener('resize', fit);
  document.addEventListener('hhl:page-swapped', fit);

  var main = document.getElementById('facet-main');
  if (main) new MutationObserver(fit).observe(main, {childList: true});
})();

/**
 * Product guides: the User Guide / Install Guide buttons (the "Metafield Buttons" and "Iframe Button" blocks, and the
 * guide buttons in the builder templates) come from several templates and sit in a band of their own. They are
 * gathered into one "Guides & manuals" group under the buy box, the width of the Add to cart button (custom.css).
 */
(function () {
  var form = document.querySelector('.product__info .product-form');

  if (!form) {
    return;
  }

  var sources = document.querySelectorAll('[class^="ai-metafield-buttons-wrapper-"], [class^="ai-iframe-button-container-"], .button-container:has(.popup--button)');

  if (!sources.length) {
    return;
  }

  var group = document.createElement('div');
  group.className = 'hhl-guides';
  group.innerHTML = '<p class="hhl-guides__title">Guides &amp; manuals</p><div class="hhl-guides__list"></div>';
  var list = group.lastElementChild;

  sources.forEach(function (source) {
    var section = source.closest('.shopify-section');
    list.append(source);

    // The band they came from, if nothing else is left in it
    if (section && !section.contains(form) && !section.innerText.trim() && !section.querySelector('img, video, iframe:not([class^="ai-iframe-"])')) {
      section.hidden = true;
    }
  });

  // Install guides get the wrench icon, everything else the book
  list.querySelectorAll('a, button').forEach(function (button) {
    if (/close/i.test(button.getAttribute('aria-label') || '') || button.closest('[class^="ai-iframe-modal-"]')) return;
    button.classList.add('hhl-guide');
    button.setAttribute('data-guide', /install/i.test(button.textContent) ? 'install' : 'manual');
  });

  form.append(group);
})();

/**
 * Product sticky add to cart: instead of a fixed bar moved up by script when the footer arrives (which trailed the
 * scroll by a frame), the bar becomes sticky at the end of the page content: it rides at the bottom of the window and
 * comes to rest on top of the footer on its own, in step with scrolling (custom.css). It keeps its own small space at
 * the end of the content, so resting there it never covers the last items.
 */
(function () {
  var bar = document.querySelector('product-sticky-form.product-sticky-form'),
    main = document.getElementById('main');

  if (!bar || !main) {
    return;
  }

  // The theme hides the bar while the footer is on screen; here it rests on the footer instead, so that check is
  // switched off (the bar still shows only once the main Add to cart button has scrolled away)
  var prepare = function (element) {
    Object.defineProperty(element, '_isFooterVisible', {configurable: true, get: function () { return false; }, set: function () {}});
    element.classList.add('hhl-sticky-inner');
  };

  prepare(bar);

  // The theme re-renders the bar's wrapper (by id) when the variant changes: move and pin the wrapper, not the bar
  var wrap = bar.closest('[id^="product-sticky-form-"]') || bar;

  main.append(wrap);
  wrap.classList.add('hhl-sticky-inflow');

  // Sold out: the bar's "Notify me" opens Doran's back-in-stock form, as the button under the options does
  wrap.addEventListener('click', function (event) {
    if (!(event.target instanceof Element) || !event.target.closest('[data-hhl-sticky-notify]')) return;
    var notify = main.querySelector('.product-form .dr-back-in-stock-inline-button') || document.querySelector('.dr-back-in-stock-inline-button');
    if (notify) notify.click();
  });

  // A variant change re-renders the bar: the theme swaps in a new wrapper (where this one is now, in the main area) or
  // a new bar inside it. Set the new one up the same way, or it stays hidden behind the footer check
  // The theme shows the bar once the main buy form has scrolled away, watching the form it found when the bar was
  // made. A variant change can replace that form too, leaving the bar watching one that's gone (so it never shows):
  // whenever the form isn't the one watched, watch the current one
  var watchForm = function (sticky) {
    var container = document.getElementById('MainPaymentContainer'),
      form = container && container.closest('form');

    if (!form || sticky._hhlForm === form || typeof sticky._setupVisibilityObservers !== 'function') return;
    if (sticky.intersectionObserver) sticky.intersectionObserver.disconnect();
    sticky._hhlForm = form;
    sticky._setupVisibilityObservers();
  };

  var pending = null;

  new MutationObserver(function () {
    main.querySelectorAll(':scope > [id^="product-sticky-form-"]').forEach(function (fresh) {
      fresh.classList.add('hhl-sticky-inflow');
    });
    main.querySelectorAll('.hhl-sticky-inflow product-sticky-form:not(.hhl-sticky-inner)').forEach(prepare);

    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(function () {
      main.querySelectorAll('.hhl-sticky-inflow product-sticky-form').forEach(watchForm);
    });
  }).observe(main, {childList: true, subtree: true});
})();

/**
 * Signed in or out (in Shopify's account sheet on this page, or in another tab): the header is asked for again and
 * everything that depends on who's signed in is redrawn in place: the bottom bar's account slot and the mail panel
 * (every [data-hhl-session] with an id). Runs when the account sheet closes, when the tab is shown again (at most
 * every 10 seconds), when Back restores the page, and when asked (HHLSession.refresh(), after the newsletter opt-out).
 */
(function () {
  var refreshing = false, last = 0;

  var refresh = function (force) {
    var marker = document.querySelector('[data-hhl-session][id], [data-hhl-account-label]');
    var section = marker && marker.closest('.shopify-section');
    if (!section || refreshing) return;
    if (!force && Date.now() - last < 10000) return;
    refreshing = true;
    last = Date.now();
    var id = section.id.replace('shopify-section-', '');

    fetch(location.pathname + '?sections=' + encodeURIComponent(id), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (sections) {
        if (!sections || !sections[id]) return;
        var fresh = new DOMParser().parseFromString(sections[id], 'text/html');

        var label = document.querySelector('[data-hhl-account-label]'), freshLabel = fresh.querySelector('[data-hhl-account-label]');
        if (label && freshLabel) label.textContent = freshLabel.textContent.trim();

        document.querySelectorAll('[data-hhl-session][id]').forEach(function (part) {
          var next = fresh.getElementById(part.id);
          if (!next) return;
          // The bottom bar's account slot, same person still signed in (or still out): only its newsletter copy in
          // the account sheet changes. Rebuilding the slot would close the sheet the customer is using.
          var account = part.querySelector('shopify-account');
          var nextAccount = next.querySelector('shopify-account');
          if (account && nextAccount && account.hasAttribute('data-hhl-signed-in') === nextAccount.hasAttribute('data-hhl-signed-in')) {
            var shown = account.hhlExtra && account.hhlExtra.shadowRoot && account.hhlExtra.shadowRoot.querySelector('[data-hhl-account-extra]');
            var nextExtra = next.querySelector('[data-hhl-account-extra]');
            if (shown && nextExtra && shown.innerHTML !== nextExtra.innerHTML) shown.innerHTML = nextExtra.innerHTML;
            return;
          }
          if (next.innerHTML !== part.innerHTML) part.innerHTML = next.innerHTML;
        });

      })
      .catch(function () {})
      .finally(function () { refreshing = false; });
  };

  window.HHLSession = { refresh: function () { refresh(true); } };

  // Signing in (or out) in Shopify's account sheet happens on this page: when the sheet closes, redraw what depends
  // on who's signed in (the mail panel's newsletter options, the bottom bar's label)
  document.addEventListener('close', function (event) {
    if (event.target && event.target.tagName === 'SHOPIFY-ACCOUNT') refresh(true);
  }, true);

  // The bottom bar's Account tab: a tap anywhere on it (avatar or icon, the word "Account", the space around them)
  // opens the account sheet through the component's own open call, caught before it reaches Shopify's button inside.
  // Shopify's button isn't relied on here: on real Android phones (Chrome, Brave) a tap on it went off to the account
  // pages instead of opening the sheet. The handler sits on the tab itself (it stays when its contents are redrawn),
  // and custom.css gives the tab the pointer (Safari only sends taps to things that look clickable). A tap while the
  // sheet is open is the page's (HHLAccountSheet: it closes it before this runs).
  var accountTab = document.getElementById('hhl-tabbar-account');
  if (accountTab) {
    accountTab.addEventListener('click', function (event) {
      var account = accountTab.querySelector('shopify-account');
      if (!account || typeof account.showModal !== 'function') return;
      // Taps inside the open sheet (its links, the sign-in, the newsletter) are the sheet's own
      var dialog = account.shadowRoot && account.shadowRoot.querySelector('dialog');
      if (dialog && event.composedPath && event.composedPath().indexOf(dialog) !== -1) return;
      event.preventDefault();
      event.stopPropagation();
      account.showModal();
    }, true);
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh(true);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refresh(false);
  });
})();

/**
 * Newsletter for signed-in customers (snippets/hhl-mail-panel.liquid: the header's mail panel and the phone account
 * sheet): "Sign me up" and "Unsubscribe" ask the HHL app (its store proxy, theme setting "HHL app proxy") to change
 * the account's email consent, which a theme can't do; the app knows who's asking from Shopify's signed proxy request.
 * The answer shows right away in the panel, then the panels are redrawn for the new state (HHLSession.refresh). If the
 * app can't do it, the panel says what to do instead.
 */
(function () {
  var words = {
    subscribe: { busy: 'Signing you up…', done: "You're subscribed. Thanks!", fail: "That didn't go through. Please try again in a moment." },
    unsubscribe: { busy: 'Unsubscribing…', done: "You're unsubscribed. You can sign up again any time.", fail: "That didn't go through. You can also use the unsubscribe link at the bottom of any of our emails." }
  };

  document.addEventListener('click', function (event) {
    // composedPath: the button may be inside the phone account sheet (a shadow root)
    var origin = event.composedPath ? event.composedPath()[0] : event.target;
    var button = origin.closest && origin.closest('[data-hhl-consent-button]');
    if (!button || button.disabled) return;
    var box = button.closest('[data-hhl-consent]');
    var note = box.querySelector('[data-hhl-consent-note]');
    var hint = box.querySelector('[data-hhl-consent-hint]');
    var action = button.getAttribute('data-action');
    var text = words[action];
    var label = button.textContent;
    button.disabled = true;
    button.textContent = text.busy;
    note.hidden = true;
    fetch(box.getAttribute('data-proxy') + '/newsletter', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: 'action=' + action
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (answer) {
        if (!answer || !answer.ok) throw new Error('consent');
        button.hidden = true;
        if (hint) hint.hidden = true;
        // Unsubscribed: "You're on the list" above goes at once, not when the panel is redrawn
        var listed = action === 'unsubscribe' && box.closest('.hhl-panel') && box.closest('.hhl-panel').querySelector('.hhl-panel__head .hhl-panel__done');
        if (listed) listed.hidden = true;
        note.textContent = text.done;
        note.hidden = false;
        // Then every panel shows the new state (the mail panel, the phone sheet's copy)
        setTimeout(function () { if (window.HHLSession) HHLSession.refresh(); }, 1600);
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = label;
        note.textContent = text.fail;
        note.hidden = false;
      });
  });
})();

/**
 * Product cards open the color they are showing: when a card's photo cycle (or a filter) shows a variant's photo, a
 * click opens the product with that variant chosen. The link's address is updated just before it's followed (also
 * for middle and modified clicks): the shown variant is added, or removed when the card shows its main photo.
 * On a collection page the product also opens within that collection (/collections/<collection>/products/<product>),
 * so its breadcrumb shows the way the customer came (snippets/product-breadcrumb.liquid), not the product's deepest
 * place in the menu.
 */
(function () {
  var shownVariant = function (card) {
    var active = card.querySelector('.product-item__cycle-image.is-active[data-variant-id]') ||
      card.querySelector('.product-item__primary-image[data-variant-id]:not([hidden])');
    return active && active.getAttribute('data-variant-id');
  };

  var point = function (event) {
    var link = event.target.closest && event.target.closest('.product-item a[href*="/products/"]');
    if (!link) return;

    // The main photo (no variant of its own) opens the product as usual
    var variant = shownVariant(link.closest('.product-item')),
      url = new URL(link.href, location.href);
    variant ? url.searchParams.set('variant', variant) : url.searchParams.delete('variant');

    // The collection is read at click time: collection pages swap in place (Smooth collection browsing above)
    var here = location.pathname.match(/^(.*\/collections\/[^/]+)\/?$/),
      product = url.pathname.match(/\/products\/[^/]+$/);
    if (here && product && link.closest('#main') && !/\/collections\/(all|vendors|types)$/.test(here[1])) {
      url.pathname = here[1] + product[0];
    }

    link.href = url.pathname + url.search + url.hash;
  };

  document.addEventListener('mousedown', point, true);
  document.addEventListener('touchstart', point, {capture: true, passive: true});
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter') point(event);
  }, true);
})();

/**
 * Quantity boxes on product pages: the minus is disabled at the smallest quantity and the plus at the most that can
 * be added (the max snippets/product-form.liquid works out from stock), kept in step with every change, including
 * when picking another variant re-renders the box.
 */
(function () {
  var sync = function (selector) {
    var input = selector.querySelector('input'),
      buttons = selector.querySelectorAll('button.quantity-selector__button');
    if (!input || buttons.length < 2) return;

    var value = parseInt(input.value) || 0,
      min = parseInt(input.min) || 1,
      max = parseInt(input.max);
    buttons[0].disabled = value <= min;
    buttons[buttons.length - 1].disabled = !isNaN(max) && value >= max;
  };

  var QuantitySelector = window.customElements.get('quantity-selector');
  if (!QuantitySelector) return;

  var connected = QuantitySelector.prototype.connectedCallback;
  QuantitySelector.prototype.connectedCallback = function () {
    connected.call(this);
    var selector = this;
    // A re-rendered box gets its previous quantity back just after it's added (theme.js)
    setTimeout(function () { sync(selector); });
  };

  ['change', 'input'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var selector = event.target.closest && event.target.closest('quantity-selector');
      if (selector) sync(selector);
    });
  });

  document.querySelectorAll('quantity-selector').forEach(sync);
})();

/* ==========================================================================
   Description fold (sections/product-content.liquid, custom.css "Product description fold")
   A product's description starts folded to a preview; clicking it (not a link in it) or "Read more" opens it,
   clicking again or "Show less" folds it back, easing the height. A description shorter than the preview isn't
   folded. While the HHL app's "Pairs well with" block shows parts, the description's own "PAIRS WELL WITH:"
   heading and its list of links are hidden (the block replaces them).
   ========================================================================== */
(function () {
  // A description's PAIRS WELL WITH section drawn hidden because "Verified compatible parts" would show
  // (snippets/hhl-pairs-shown.liquid): shown again where the page has no such block (its section is off on the template)
  var keepPairsSection = function () {
    if (document.querySelector('[data-hhl-pairs]')) return;
    document.querySelectorAll('.hhl-desc-pairs--replaced').forEach(function (el) { el.classList.remove('hhl-desc-pairs--replaced'); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', keepPairsSection);
  else keepPairsSection();

  var HEADING = /^\s*pairs\s+well\s+with\s*:?\s*$/i;

  /** Only links (or nothing) in it: the hand-written list under the heading. */
  var linksOnly = function (el) {
    var text = el.textContent.replace(/\s+/g, '');
    var links = Array.prototype.map.call(el.querySelectorAll('a'), function (a) { return a.textContent; }).join('').replace(/\s+/g, '');
    return text === links;
  };

  var retirePairsList = function (body) {
    if (!document.querySelector('[data-hhl-pairs]')) return;
    body.querySelectorAll('h1, h2, h3, h4, h5, h6, p, strong, b').forEach(function (el) {
      if (!HEADING.test(el.textContent)) return;
      var head = el.closest('h1, h2, h3, h4, h5, h6, p') || el;
      if (!body.contains(head) || head.classList.contains('hhl-desc__retired')) return;
      head.classList.add('hhl-desc__retired');
      var next = head.nextElementSibling;
      while (next && (/^(UL|OL)$/.test(next.tagName) || ((next.tagName === 'P' || next.tagName === 'DIV') && linksOnly(next)))) {
        next.classList.add('hhl-desc__retired');
        next = next.nextElementSibling;
      }
    });
  };

  var setUp = function (box) {
    if (box.dataset.hhlReady) return;
    box.dataset.hhlReady = '1';
    var body = box.querySelector('[data-hhl-desc-body]');
    var toggle = box.querySelector('[data-hhl-desc-toggle]');
    if (!body || !toggle) return;
    retirePairsList(body);

    var preview = function () {
      return parseFloat(getComputedStyle(box).getPropertyValue('--hhl-desc-preview')) * parseFloat(getComputedStyle(document.documentElement).fontSize) || 240;
    };
    var measure = function () {
      if (!body.offsetParent || box.classList.contains('is-open')) return; // hidden tab: measured when shown
      var short = body.scrollHeight <= preview() + 64;
      box.classList.toggle('is-short', short);
      box.classList.toggle('is-folded', !short);
      toggle.hidden = short;
    };

    var open = function (yes) {
      if (box.classList.contains('is-short')) return;
      var from = body.getBoundingClientRect().height;
      box.classList.toggle('is-open', yes);
      toggle.textContent = yes ? 'Show less' : 'Read more';
      toggle.setAttribute('aria-expanded', String(yes));
      // Ease between the two heights: pin the starting height, then go to the target on the next frame.
      var to = yes ? body.scrollHeight : preview();
      body.style.maxHeight = from + 'px';
      requestAnimationFrame(function () {
        body.style.maxHeight = to + 'px';
      });
      var done = function (event) {
        if (event && event.propertyName !== 'max-height') return;
        body.removeEventListener('transitionend', done);
        body.style.maxHeight = '';
      };
      body.addEventListener('transitionend', done);
      if (!yes) {
        // Folding a long description: bring its top back into view.
        var top = box.getBoundingClientRect().top;
        if (top < 0) window.scrollBy({ top: top - 120, behavior: 'smooth' });
      }
    };

    toggle.addEventListener('click', function () { open(!box.classList.contains('is-open')); });
    body.addEventListener('click', function (event) {
      if (box.classList.contains('is-short')) return;
      if (event.target.closest('a, button, input, select, textarea, video, iframe, label, summary')) return;
      if (window.getSelection && String(window.getSelection()).length) return; // selecting text, not folding
      open(!box.classList.contains('is-open'));
    });

    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(body);
  };

  var all = function () { document.querySelectorAll('[data-hhl-desc]').forEach(setUp); };
  all();
  document.addEventListener('shopify:section:load', all);
})();


/**
 * Product image viewer (PhotoSwipe): a click on the dimmed page around the panel, or on the panel outside the photo,
 * closes it, like any other popup. The photo itself still zooms on click.
 */
(function () {
  var close = function (target) {
    var viewer = target.closest && target.closest('product-image-zoom');
    if (viewer && viewer.photoSwipeInstance) viewer.photoSwipeInstance.close();
  };

  document.addEventListener('click', function (event) {
    if (event.target.classList && event.target.classList.contains('pswp__bg')) close(event.target);
  });

  // PhotoSwipe reports taps inside the panel as pswpTap (it swallows the clicks there)
  document.addEventListener('pswpTap', function (event) {
    var target = event.target;
    if (!target.closest || target.closest('.pswp__img, .pswp__ui')) return;
    close(target);
  });
})();

/* ==========================================================================
   Pairs well with (snippets/hhl-pairs.liquid, custom.css "Pairs well with")
   Each card's row of tiles scrolls sideways; the arrows show only when there is more than fits, page by a
   card's width, and loop around (past the last tile back to the first, and the other way). Tiles the page couldn't
   draw on load (Shopify looks up 20 products per page) are fetched as the row nears them, as the site's own tile
   (sections/hhl-pair-tile.liquid), and fade in.
   ========================================================================== */
(function () {
  var root = HHL.root();

  var loadSlot = function (slot) {
    if (slot.dataset.loading) return;
    slot.dataset.loading = '1';
    var handle = slot.getAttribute('data-hhl-pairs-slot');
    // A version's placeholder asks for that version's tile; a group of versions' asks the HHL app's proxy, which renders
    // the theme's own tile for them (snippets/hhl-pair-tile-body.liquid), else (no proxy set) its first version's
    var version = slot.getAttribute('data-hhl-pairs-variant');
    var versions = slot.getAttribute('data-hhl-pairs-versions');
    var block = slot.closest('[data-hhl-pairs]');
    var proxy = block && block.getAttribute('data-hhl-proxy');
    var url = versions && proxy
      ? proxy.replace(/\/$/, '') + '/pair-tile?h=' + encodeURIComponent(handle) + '&v=' + encodeURIComponent(versions)
      : root + 'products/' + encodeURIComponent(handle) + '?section_id=hhl-pair-tile' + (version ? '&variant=' + encodeURIComponent(version) : '');
    fetch(url)
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var tile = doc.querySelector('.product-item');
        var slide = slot.parentElement;
        if (!tile) {
          // Sold out, discontinued or hidden: the part leaves the card (and an empty card leaves the page).
          var card = slide.closest('[data-hhl-pairs-card]');
          slide.remove();
          if (card) {
            var left = card.querySelectorAll('[data-hhl-pairs-slide]').length;
            var count = card.querySelector('[data-hhl-pairs-count]');
            if (count) count.textContent = String(left);
            if (!left) card.remove();
            card.dispatchEvent(new CustomEvent('hhl:pairs-changed'));
          }
          var block = document.querySelector('[data-hhl-pairs]');
          if (block && !block.querySelector('[data-hhl-pairs-card]')) block.remove();
          return;
        }
        slot.replaceWith(document.importNode(tile, true));
        // The part's strip waited beside the placeholder: it goes under the tile's photo, as on drawn tiles
        var info = slide.querySelector('.product-item__info');
        var strip = Array.prototype.filter.call(slide.children, function (el) { return el.hasAttribute('data-hhl-strip'); })[0];
        if (info && strip) info.before(strip);
        var pid = doc.querySelector('[data-hhl-pid]');
        if (pid) slide.setAttribute('data-product-id', pid.getAttribute('data-hhl-pid'));
        // Compare's data for this part came with the tile (assets/hhl-compare.js reads it from the slide)
        var spec = doc.querySelector('[data-hhl-spec]');
        if (spec) slide.appendChild(document.importNode(spec, true));
        var crop = doc.querySelector('[data-hhl-crop]');
        if (crop) { slide.appendChild(document.importNode(crop, true)); if (window.HHLCrop) HHLCrop.slide(slide); }
      })
      .catch(function () { delete slot.dataset.loading; });
  };

  var setUp = function (card) {
    if (card.dataset.hhlReady) return;
    card.dataset.hhlReady = '1';
    var track = card.querySelector('[data-hhl-pairs-track]');
    var arrows = card.querySelector('[data-hhl-pairs-arrows]');
    if (!track || !arrows) return;

    var update = function () {
      arrows.hidden = track.scrollWidth <= track.clientWidth + 2;
    };
    var go = function (dir) {
      var max = track.scrollWidth - track.clientWidth;
      var at = Math.abs(track.scrollLeft);
      var rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      if (dir > 0 && at >= max - 4) track.scrollTo({ left: 0 });               // past the end: back to the start
      else if (dir < 0 && at <= 4) track.scrollTo({ left: max * rtl });         // before the start: to the end
      else track.scrollBy({ left: dir * track.clientWidth * rtl });
    };
    card.querySelector('[data-hhl-pairs-prev]').addEventListener('click', function () { go(-1); });
    card.querySelector('[data-hhl-pairs-next]').addEventListener('click', function () { go(1); });

    var slots = card.querySelectorAll('[data-hhl-pairs-slot]');
    if (slots.length) {
      if ('IntersectionObserver' in window) {
        // A row's next page and the card coming on screen both count as "near".
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            io.unobserve(e.target);
            loadSlot(e.target);
          });
        }, { root: track, rootMargin: '0px 100% 0px 100%' });
        slots.forEach(function (slot) { io.observe(slot); });
      } else {
        slots.forEach(loadSlot);
      }
    }

    update();
    card.addEventListener('hhl:pairs-changed', update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
  };

  var all = function () { document.querySelectorAll('[data-hhl-pairs-card]').forEach(setUp); };
  all();
  document.addEventListener('shopify:section:load', all);
})();

/* ==========================================================================
   Pairs and the cart (snippets/hhl-pairs.liquid, custom.css "Pairs and the cart"; owner 2026-10-20)
   With parts in the cart, "Verified compatible parts" checks its parts against them (the HHL app's
   {proxy}/compat?items=…&check=…): in each card the parts that fit go first ("Fits your IPS kit"), parts that fit in
   some versions next, then the rest as before (parts that would take a cart part's spot say "Instead of your …"),
   parts already in the cart say "In your cart", and parts that don't fit go last, dimmed, with the reason. Nothing is
   hidden. Adding one that doesn't fit asks first, on the tile. A line on top says what the parts are checked against;
   its × stops checking for the visit (sessionStorage) and puts the list back as drawn. The app allows 120 asks per
   10 minutes per visitor, so one ask covers all the parts, and the answer is kept per cart (sessionStorage). Any
   failure leaves the list as drawn, silently.
   ONE line per tile (owner 2026-10-26): the worst of the cart's line and the tile's own fit note with this page's
   product (snippets/hhl-tile-note.liquid), on "Fits < Fits with note < Only some versions < Not recommended <
   Doesn't fit". A part that fits a cart part with a note says the note ("Fits your Button PCB" with why, amber; "Not
   recommended with your …", orange). Tiles' notes for some versions of this page's product follow the picker.
   ========================================================================== */
(function () {
  var block = document.querySelector('[data-hhl-pairs]');
  if (!block) return;
  var line = block.querySelector('[data-hhl-pairs-cart]');
  var proxy = block.getAttribute('data-hhl-proxy');

  var OFF = 'hhl-pairs-cart-off';
  var KEPT = 'hhl-pairs-cart';
  var grid = block.querySelector('.hhl-pairs__grid');
  var lineText = line && line.querySelector('[data-hhl-pairs-cart-text]');
  var reduced = function () { return HHL.mq.reducedMotion.matches; };
  var read = function (key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } };
  var write = function (key, value) { try { sessionStorage.setItem(key, value); } catch (e) {} };
  var isOff = function () { return read(OFF) === '1'; };

  var slides = function () { return Array.prototype.slice.call(block.querySelectorAll('[data-hhl-pairs-slide]')); };
  // As drawn, so the list can be put back
  slides().forEach(function (slide, i) { slide.setAttribute('data-hhl-order', i); });

  // The cart parts by the app's names (the Parts Catalog's short names, "Q5 IPS"): the first, and "+1" for more
  var names = function (list) {
    var seen = [];
    list.forEach(function (w) { var n = w.name || 'part'; if (seen.indexOf(n) === -1) seen.push(n); });
    if (!seen.length) return '';
    return seen.length === 1 ? seen[0] : seen[0] + ' +' + (seen.length - 1);
  };

  // Slots not drawn yet have no product id on the page: their product's .js has it
  var withIds = function () {
    return Promise.all(slides().filter(function (s) { return !s.getAttribute('data-product-id'); }).map(function (slide) {
      return fetch(HHL.root() + 'products/' + encodeURIComponent(slide.getAttribute('data-handle')) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p && p.id) slide.setAttribute('data-product-id', p.id); })
        .catch(function () {});
    }));
  };

  // Runs change() with nothing jumping: the tiles that move slide from where they were (FLIP), and each card eases
  // to its new height (the labels fade in with CSS). Tiles that keep their place stay still; nothing is redrawn.
  var easeCards = function (change) {
    if (reduced() || !grid.animate) { change(); return; }
    var cards = Array.prototype.slice.call(block.querySelectorAll('[data-hhl-pairs-card]'));
    var tiles = slides();
    var before = tiles.map(function (s) { return s.getBoundingClientRect(); });
    var heights = cards.map(function (c) { return c.getBoundingClientRect().height; });
    change();
    var speed = HHL.motion();
    var timing = {duration: 350 * speed, easing: HHL.ease};
    tiles.forEach(function (slide, i) {
      var now = slide.getBoundingClientRect();
      var dx = before[i].left - now.left;
      if (Math.abs(dx) > 1) slide.animate([{transform: 'translateX(' + dx + 'px)'}, {transform: 'none'}], timing);
    });
    cards.forEach(function (card, i) {
      var to = card.getBoundingClientRect().height;
      if (Math.abs(to - heights[i]) > 1) card.animate([{height: heights[i] + 'px', overflow: 'hidden'}, {height: to + 'px', overflow: 'hidden'}], timing);
    });
  };

  var CHECK = '<svg class="hhl-cartfit__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  // A heads-up's "i" and "Not recommended"'s triangle, as snippets/hhl-fit-note.liquid draws them
  var HEADS = '<svg class="hhl-cartfit__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM8.9 4.7a.9.9 0 1 1-1.8 0 .9.9 0 0 1 1.8 0zM7.25 7h1.5v4.8h-1.5z"/></svg>';
  var ADVISE = '<svg class="hhl-cartfit__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8 1.5l7 12.5H1zM7.25 5.6h1.5v4.6h-1.5zm0 5.6h1.5v1.5h-1.5z"/></svg>';
  // "Doesn't Fit": a cross (owner 2026-10-26: every strip says its kind with its icon)
  var CROSS = '<svg class="hhl-cartfit__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  // alt: it fills the same spot as a cart part (another screen when a screen is in the cart)
  // cart: the part is in the cart already
  // A tile's part as the app is asked about it: "productId", or "productId:variantId" for a version's tile
  var keyOf = function (slide) {
    var v = slide.getAttribute('data-variant-id');
    return slide.getAttribute('data-product-id') + (v ? ':' + v : '');
  };
  // ...and with its card's group ("123:456@Shells"), so the app leaves out notes about another group's parts, as the
  // card itself does (owner 2026-10-26: the shells said their included buttons' LED note with the LED kit in the cart)
  var askOf = function (slide) {
    var card = slide.closest('[data-hhl-pairs-card]');
    var type = card ? (card.getAttribute('data-hhl-pairs-type') || '').replace(/[,@]/g, ' ').trim() : '';
    return keyOf(slide) + (type ? '@' + type : '');
  };

  // ONE line per tile (owner 2026-10-26: "whatever is the highest level on our scale: 1) Fits 2) Fits with note
  // 3) Fits not recommended 4) Does not fit"): the worst of the cart check's line and the tile's own note with this
  // page's product. "Only some versions fit" sits between a note and "Not recommended"; "Instead of your …" and "In
  // your cart" are at the Fits level. A tie goes to the tile's own note (it is about this page's product).
  var LEVEL = {fits: 0, alt: 0, cart: 0, note: 1, some: 2, advised: 3, no: 4};
  // The order in a card with the cart in mind: fits first, then with a note, some versions, the rest as drawn, not
  // recommended, and doesn't fit last
  var RANK = {fits: 0, note: 1, some: 2, alt: 3, cart: 3, na: 3, advised: 4, no: 5};
  var STATES = ['fits', 'note', 'some', 'alt', 'cart', 'advised', 'no'];

  // The page's selected version: snippets/hhl-tile-note.liquid drew each tile's note for it
  var pageVariant = block.getAttribute('data-hhl-page-variant') || '';
  var pageVariants = (block.getAttribute('data-hhl-page-variants') || '').split(',');

  // A tile's own note for the selected version: the first drawn for it ("Not recommended" ones are drawn first)
  var ownNote = function (slide) {
    var notes = slide.querySelectorAll('[data-hhl-tile-note]');
    for (var i = 0; i < notes.length; i++) {
      var a = notes[i].getAttribute('data-hhl-note-a');
      if (!a || a.indexOf('|' + pageVariant + '|') !== -1) return notes[i];
    }
    return null;
  };

  // The cart check's line for a tile (the app's answer for it): kind, words, why and the cart parts; null: none
  var cartLine = function (check) {
    var state = check && RANK[check.state] !== undefined ? check.state : 'na';
    if (state === 'na') return null;
    // Fits, with a note (the app's `note`): the note's level and words
    if (state === 'fits' && check.note && check.note.t) {
      var advised = !!check.note.r;
      var name = check.note.name || 'part';
      // Its page, when the note has one (the app's `u`; owner 2026-10-25): the note's words open it
      var url = typeof check.note.u === 'string' && check.note.u.indexOf('https://') === 0 ? check.note.u : '';
      return {kind: advised ? 'advised' : 'note', who: name, reason: check.note.t, url: url,
        words: (advised ? 'Not recommended with your ' : 'Fits your ') + name};
    }
    // The overall state wins: a screen that fits the shell but takes the screen's spot is "alt"
    var against = (check.with || []).filter(function (w) { return state === 'some' ? w.state !== 'fits' : w.state === state; });
    if (!against.length) against = (check.with || []).filter(function (w) { return w.state !== 'na'; });
    var who = names(against);
    var reason = state === 'no' ? (against.map(function (w) { return w.reason; }).filter(Boolean)[0] || '') : '';
    var words = state === 'fits' ? 'Fits your ' + who : state === 'some' ? 'Only some versions fit your ' + who :
      state === 'alt' ? 'Instead of your ' + who : state === 'cart' ? 'In your cart' : 'Doesn’t fit your ' + who;
    return {kind: state, who: who, words: words, reason: reason};
  };
  var lineKey = function (l) { return l.kind + l.words + l.reason + (l.url || ''); };
  // What a tile's strip says before it is opened (owner 2026-10-26): the kind of fit; the words come when it opens
  var TAG = {fits: 'Fit Verified', alt: 'Fit Verified', cart: 'Fit Verified', note: 'Fit Warning', some: 'Fit Warning',
    advised: 'Not Advised', no: 'Doesn’t Fit'};

  // What each tile shows (answer: the app's checks by product id; none = as drawn, its own note only)
  var plan = function (answer) {
    return slides().map(function (slide) {
      var own = ownNote(slide);
      var ownKind = own ? (own.classList.contains('hhl-fitnote--advised') ? 'advised' : 'note') : '';
      var cart = answer ? cartLine(answer[askOf(slide)] || answer[keyOf(slide)]) : null;
      // The card's order goes by how the part stands with the cart, then plain before a part with its own note
      // (owner 2026-10-26: with buttons in the cart every set is "Instead of your Buttons", and the sets with a note
      // jumped ahead of the plain ones because the note was the line shown)
      var rank = answer ? Math.max(RANK[cart ? cart.kind : 'na'], ownKind ? RANK[ownKind] : 0) + (ownKind ? .5 : 0) : 0;
      if (cart && own && LEVEL[cart.kind] <= LEVEL[ownKind]) cart = null;
      return {slide: slide, note: cart ? null : own, line: cart, kind: cart ? cart.kind : ownKind, rank: rank};
    });
  };

  // Shows each tile's one line and puts each card's parts in order
  var apply = function (plans, answer) {
    plans.forEach(function (p) {
      var slide = p.slide;
      slide.querySelectorAll('[data-hhl-tile-note]').forEach(function (n) {
        var on = n === p.note;
        if (on && n.hidden) { n.hidden = false; n.classList.add('is-new'); }
        else if (!on && !n.hidden) { n.hidden = true; n.classList.remove('is-new'); }
      });
      STATES.forEach(function (s) { slide.classList.toggle('is-cart-' + s, !!answer && s === p.kind); });
      slide.hhlRank = p.rank;
      slide.hhlCartWith = p.line ? p.line.who : '';
      var label = slide.querySelector('.hhl-cartfit');
      if (!p.line) { if (label) label.remove(); return; }
      if (label && label.getAttribute('data-words') === lineKey(p.line)) return;
      if (!label) {
        label = document.createElement('p');
        label.tabIndex = 0; // tapped, it opens to all its words (custom.css "The strip under a tile's photo")
        var strip = slide.querySelector('[data-hhl-strip]');
        var tile = slide.querySelector('.product-item, .hhl-pairs__slot');
        if (strip) strip.appendChild(label); else if (tile) tile.after(label); else slide.prepend(label);
      }
      var kind = p.line.kind;
      // In a tile's strip: its kind first ("Fit Verified"), the words in a fold that opens (custom.css)
      var inStrip = !!(label.parentElement && label.parentElement.hasAttribute('data-hhl-strip'));
      label.className = 'hhl-cartfit is-new hhl-cartfit--' + kind;
      label.setAttribute('data-words', lineKey(p.line));
      label.innerHTML = ({fits: CHECK, alt: CHECK, cart: CHECK, note: HEADS, some: HEADS, advised: ADVISE, no: CROSS}[kind] || '') +
        '<span class="hhl-cartfit__body">' + (inStrip ? '<span class="hhl-cartfit__tag"></span><span class="hhl-strip__more"><span class="hhl-strip__more-inner">' : '') +
        '<span class="hhl-cartfit__text"></span>' + (p.line.reason ? (p.line.url ? '<a class="hhl-fitnote__link" target="_blank" rel="noopener noreferrer">' +
          '<span class="hhl-cartfit__why"></span><span class="hhl-fitnote__go" aria-hidden="true">&#8599;</span><span class="hhl-fitnote__sr"> (opens in a new tab)</span></a>' :
          '<span class="hhl-cartfit__why"></span>') : '') + (inStrip ? '</span></span>' : '') + '</span>';
      var tag = label.querySelector('.hhl-cartfit__tag');
      if (tag) tag.textContent = TAG[kind] || '';
      label.querySelector('.hhl-cartfit__text').textContent = p.line.words;
      if (p.line.reason) label.querySelector('.hhl-cartfit__why').textContent = p.line.reason;
      if (p.line.url) label.querySelector('.hhl-fitnote__link').href = p.line.url;
    });
    block.querySelectorAll('[data-hhl-pairs-track]').forEach(function (track) {
      var list = Array.prototype.slice.call(track.querySelectorAll('[data-hhl-pairs-slide]'));
      var left = track.scrollLeft;
      list.sort(function (a, b) {
        return ((a.hhlRank || 0) - (b.hhlRank || 0)) || (a.getAttribute('data-hhl-order') - b.getAttribute('data-hhl-order'));
      });
      // The new order is shown with CSS order, not by moving the tiles: a tile taken out of the page and put back is
      // set up again by the theme (and its photos redrawn), which read as every tile reloading. As drawn: no order.
      list.forEach(function (slide, i) {
        if (answer) slide.style.order = String(i); else slide.style.removeProperty('order');
      });
      // A snapping row would follow the tile it rested on to its new place: it stays where it was, so the parts that
      // now lead are the ones in view
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.removeProperty('scroll-behavior');
    });
    // New lines fade in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        block.querySelectorAll('.hhl-cartfit.is-new, [data-hhl-tile-note].is-new').forEach(function (l) { l.classList.remove('is-new'); });
      });
    });
  };

  // Lines that go away fade first, then the cards ease to their new size and the new lines fade in (nothing pops)
  var current = null; // the answer the tiles show (null: as drawn)
  var turn = 0;
  var render = function (answer) {
    current = answer;
    var mine = ++turn;
    var plans = plan(answer);
    var leaving = [];
    plans.forEach(function (p) {
      p.slide.querySelectorAll('[data-hhl-tile-note]').forEach(function (n) { if (!n.hidden && n !== p.note) leaving.push(n); });
      var label = p.slide.querySelector('.hhl-cartfit');
      if (label && (!p.line || label.getAttribute('data-words') !== lineKey(p.line))) leaving.push(label);
    });
    var go = function () { if (mine === turn) easeCards(function () { apply(plans, answer); }); };
    if (!leaving.length || reduced()) { go(); return; }
    leaving.forEach(function (el) { el.classList.add('is-new'); });
    setTimeout(go, 150 * HHL.motion());
  };

  // Another version of this page's product picked: its tiles' notes follow (only tiles with notes for some versions)
  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    // Only this product's own versions (a quick-add window of another product fires this too)
    if (!variant || pageVariants.indexOf(String(variant.id)) === -1 || String(variant.id) === pageVariant) return;
    pageVariant = String(variant.id);
    block.setAttribute('data-hhl-page-variant', pageVariant); // Compare reads it (assets/hhl-compare.js)
    if (block.querySelector('[data-hhl-note-a]')) render(current);
  });

  // The rest is about the cart: only with the app's address
  if (!line || !proxy || !window.fetch) return;
  proxy = proxy.replace(/\/$/, '');

  var asked = ''; // the last cart asked about (one ask at a time)
  var shown = ''; // what the cards show now: the cart and answer they were arranged for ('' = as drawn)
  var show = function (key, answer, against) {
    if (key === shown) return;
    var wasPlain = !shown;
    shown = key;
    closeConfirm();
    if (!answer && wasPlain) return;
    render(answer);
    if (answer) HHL.setText(lineText, 'Checking against: your ' + against);
    line.classList.toggle('is-on', !!answer);
  };
  var plain = function () { asked = ''; show('', null); };

  var check = function () {
    if (isOff()) { plain(); return; }
    HHL.fetchCart().then(function (cart) {
      var items = [];
      var inCart = [];
      (cart.items || []).forEach(function (item) {
        if (HHL.isProtectionItem(item)) return;
        inCart.push(String(item.product_id), item.product_id + ':' + item.variant_id);
        var id = item.product_id + ':' + item.variant_id;
        if (items.indexOf(id) === -1) items.push(id);
      });
      if (!items.length) { plain(); return; }
      items.sort();
      return withIds().then(function () {
        var ids = [];
        slides().forEach(function (s) { var p = s.getAttribute('data-product-id') && askOf(s); if (p && ids.indexOf(p) === -1) ids.push(p); });
        if (!ids.length) { plain(); return; }
        ids.sort(); // the same ask whatever order the cards are in
        var key = items.join(',') + '|' + ids.join(',') + '|' + (window.HHL_COMPAT || '');
        if (key === asked) return;
        asked = key;
        var kept = null;
        try { kept = JSON.parse(read(KEPT) || 'null'); } catch (e) {}
        var answered = kept && kept.key === key ? Promise.resolve(kept.body) :
          fetch(proxy + '/compat?items=' + items.join(',') + '&check=' + ids.map(encodeURIComponent).join(',') + '&v=' + encodeURIComponent(window.HHL_COMPAT || ''), {headers: {Accept: 'application/json'}})
            .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
            .then(function (body) { write(KEPT, JSON.stringify({key: key, body: body})); return body; });
        return answered.then(function (body) {
          if (key !== asked || isOff()) return;
          var answer = {};
          var cartParts = [];
          var any = false;
          (body && body.checks || []).forEach(function (c) {
            // A part that is in the cart itself just says so (the app checks it against itself too)
            var key = c.v ? c.p + ':' + c.v : String(c.p);
            if (inCart.indexOf(key) !== -1) c = {p: c.p, v: c.v, c: c.c, state: 'cart', with: []};
            answer[c.c ? key + '@' + c.c : key] = c;
            if (c.state === 'na') return;
            any = true;
            (c.with || []).forEach(function (w) { if (w.state !== 'na') cartParts.push(w); });
          });
          if (!any) { plain(); return; }
          show(key, answer, names(cartParts));
        });
      });
    }).catch(function () { asked = ''; plain(); });
  };

  /* Adding a part that doesn't fit asks first, on its tile */
  var confirmBox = null;
  var closeConfirm = function (refocus) {
    if (!confirmBox) return;
    var box = confirmBox;
    confirmBox = null;
    box.classList.remove('is-open');
    if (refocus && box.hhlTrigger && box.hhlTrigger.isConnected) box.hhlTrigger.focus();
    setTimeout(function () { box.remove(); }, reduced() ? 0 : 250 * HHL.motion());
  };
  var count = 0;
  var openConfirm = function (slide, trigger) {
    closeConfirm();
    var id = 'hhl-cartfit-ask-' + (++count);
    var box = document.createElement('div');
    box.className = 'hhl-pairs__confirm';
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-labelledby', id);
    box.innerHTML = '<p class="hhl-pairs__confirm-text" id="' + id + '"></p>' +
      '<button type="button" class="button button--primary button--small" data-hhl-confirm-add>Add anyway</button>' +
      '<button type="button" class="button button--outline button--small" data-hhl-confirm-cancel>Cancel</button>';
    var who = slide.hhlCartWith || 'part';
    box.firstChild.textContent = 'This doesn’t fit the ' + who.replace(/ \+(\d+)$/, ' and $1 more') + ' in your cart. Add anyway?';
    box.hhlTrigger = trigger;
    box.addEventListener('click', function (event) {
      if (event.target.closest('[data-hhl-confirm-add]')) {
        closeConfirm();
        slide.hhlAllow = true;
        trigger.click();
      } else if (event.target.closest('[data-hhl-confirm-cancel]')) {
        closeConfirm(true);
      }
    });
    box.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.stopPropagation(); closeConfirm(true); }
    });
    slide.appendChild(box);
    confirmBox = box;
    box.getBoundingClientRect();
    box.classList.add('is-open');
    box.querySelector('[data-hhl-confirm-cancel]').focus({preventScroll: true});
  };

  // The tile's add buttons (one variant: its Add; several: the button that opens the version picker)
  var TRIGGER = '.product-item__quick-form button, [data-quick-variants-toggle]';
  block.addEventListener('click', function (event) {
    var trigger = event.target.closest(TRIGGER);
    var slide = trigger && trigger.closest('.is-cart-no');
    if (!slide) return;
    if (slide.hhlAllow) { slide.hhlAllow = false; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    openConfirm(slide, trigger);
  }, true);
  document.addEventListener('pointerdown', function (event) {
    if (confirmBox && !confirmBox.contains(event.target)) closeConfirm();
  });

  line.querySelector('[data-hhl-pairs-cart-off]').addEventListener('click', function () {
    write(OFF, '1');
    asked = '';
    var heading = block.querySelector('.hhl-pairs__heading');
    plain();
    if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({preventScroll: true}); }
  });

  // The cart changed (an add from these tiles or anywhere, the cart drawer): ask again, once it settles
  var timer;
  var later = function () { clearTimeout(timer); timer = setTimeout(check, 400); };
  ['variant:added', 'cart:updated', 'cart:refresh'].forEach(function (name) { document.addEventListener(name, later); });
  window.addEventListener('pageshow', function (event) { if (event.persisted) later(); });
  check();
})();

/* ==========================================================================
   Fits per variant (snippets/hhl-fits.liquid, snippets/hhl-pairs.liquid, custom.css "Fits per variant")
   A product can be in a product line for some of its versions only (the Game Boy speaker: the 23mm one fits the Game
   Boy Color, the 28mm DMG one doesn't). The page is drawn for the selected variant; when another is picked, "Fits …"
   lists that variant's lines (or goes away) and the compatible-parts cards switch to that variant's. Each box fades
   out, changes, eases to its new height (0 when nothing is left) and fades back in.
   ========================================================================== */
(function () {
  // "Fits …" carries the lines and the product's variants (it is there whenever the product is in a line)
  var fits = document.querySelector('[data-hhl-fits]');
  var pairs = document.querySelector('[data-hhl-pairs]');
  if (!fits) return;

  var lines = [];
  try { lines = JSON.parse(fits.getAttribute('data-hhl-fits-lines')) || []; } catch (e) {}
  var own = fits.getAttribute('data-hhl-fits-variants').split(',');
  var inLine = function (vs, id) { return !vs || !vs.length || vs.indexOf(id) !== -1; };

  // Fades box out, runs change(), eases box's height (and margins) to what it is then, fades back in.
  var ease = function (box, change) {
    if (box.hhlEaseEnd) box.hhlEaseEnd();
    if (HHL.mq.reducedMotion.matches) { change(); return; }
    var laidOut = function () { return getComputedStyle(box).display !== 'none'; };
    var size = function () {
      if (!laidOut()) return {h: 0, t: '0px', b: '0px'};
      var cs = getComputedStyle(box);
      return {h: box.getBoundingClientRect().height, t: cs.marginBlockStart, b: cs.marginBlockEnd};
    };
    var from = size();
    var timer;
    var set = function (s) {
      box.style.height = s.h + 'px';
      box.style.marginBlockStart = s.t;
      box.style.marginBlockEnd = s.b;
    };
    var end = box.hhlEaseEnd = function () {
      clearTimeout(timer);
      box.hhlEaseEnd = null;
      ['height', 'marginBlockStart', 'marginBlockEnd', 'opacity', 'overflow', 'transition'].forEach(function (k) { box.style[k] = ''; });
      box.classList.remove('is-easing');
    };
    box.classList.add('is-easing');
    box.style.overflow = 'hidden';
    set(from);
    box.style.transition = 'opacity var(--hhl-dur-fast) var(--hhl-ease)';
    box.style.opacity = '0';
    timer = setTimeout(function () {
      change();
      // Its new size: as laid out without the easing hold
      box.classList.remove('is-easing');
      ['height', 'marginBlockStart', 'marginBlockEnd', 'overflow'].forEach(function (k) { box.style[k] = ''; });
      var to = size();
      box.classList.add('is-easing');
      box.style.overflow = 'hidden';
      set(from);
      box.getBoundingClientRect();
      box.style.transition = 'height var(--hhl-dur) var(--hhl-ease), margin var(--hhl-dur) var(--hhl-ease), opacity var(--hhl-dur) var(--hhl-ease)';
      set(to);
      box.style.opacity = to.h ? '1' : '0';
      timer = setTimeout(end, 450);
    }, from.h ? 160 : 0);
  };

  var current = null;
  var show = function (id) {
    id = String(id);
    if (id === current) return;
    current = id;

    if (fits.isConnected) {
      var names = [];
      var withParts = false;
      lines.forEach(function (line) {
        if (!inLine(line.vs, id)) return;
        if (names.indexOf(line.l) === -1) names.push(line.l);
        if (line.p) withParts = true;
      });
      var label = fits.querySelector('[data-hhl-fits-names]');
      var link = fits.querySelector('[data-hhl-fits-link]');
      var text = names.join(', ');
      var off = !names.length;
      var same = (off || label.textContent === text) && off === fits.classList.contains('is-off') && (!link || link.hidden !== withParts);
      if (!same) {
        ease(fits, function () {
          if (!off) HHL.setText(label, text);
          if (link) link.hidden = !withParts;
          fits.classList.toggle('is-off', off);
        });
      }
    }

    if (pairs && pairs.isConnected) {
      var cards = pairs.querySelectorAll('[data-hhl-pairs-card]');
      var changed = Array.prototype.filter.call(cards, function (card) {
        var vs = card.getAttribute('data-hhl-pairs-vs');
        return inLine(vs ? vs.split(',') : null, id) === card.classList.contains('is-off');
      });
      if (changed.length) {
        var wrap = pairs.closest('.hhl-product-apps') || pairs;
        ease(wrap, function () {
          changed.forEach(function (card) { card.classList.toggle('is-off'); });
        });
      }
    }
  };

  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    // Only this product's own variants (a quick-add window of another product fires this too)
    if (own.indexOf(String(variant.id)) === -1) return;
    show(variant.id);
  });
  // As drawn: the selected variant (Liquid drew the page for it)
  current = fits.getAttribute('data-hhl-fits-variant');
})();

/* ==========================================================================
   Variant status (snippets/hhl-variant-status.liquid, custom.css "Variant status")
   A small badge with the selected variant's status ("Clearance", "Discontinued": the variant metafield hhl.status).
   The badge carries every variant's status; when another variant is picked it fades to the new words, or eases
   closed when that variant has none. HHL.variantStatus(badge, text) is shared with the quick add.
   ========================================================================== */
(function () {
  var set = function (badge, text) {
    var label = badge.querySelector('.hhl-variant-status__text');
    if (!label) return;
    text = text || '';
    clearTimeout(badge.hhlTimer);
    if (!text) {
      // The old words stay while it eases closed
      badge.classList.remove('is-changing');
      badge.classList.add('is-empty');
      return;
    }
    if (badge.classList.contains('is-empty')) {
      HHL.setText(label, text);
      badge.classList.remove('is-empty');
      return;
    }
    if (label.textContent === text) {
      badge.classList.remove('is-changing');
      return;
    }
    badge.classList.add('is-changing');
    badge.hhlTimer = setTimeout(function () {
      HHL.setText(label, text);
      badge.classList.remove('is-changing');
    }, HHL.mq.reducedMotion.matches ? 0 : 160);
  };
  HHL.variantStatus = set;

  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    document.querySelectorAll('[data-hhl-variant-status]').forEach(function (badge) {
      var map;
      try { map = JSON.parse(badge.getAttribute('data-hhl-variant-status')); } catch (e) { return; }
      // Only the badge of the product this variant belongs to
      if (!map || !Object.prototype.hasOwnProperty.call(map, String(variant.id))) return;
      set(badge, map[String(variant.id)]);
    });
  });
})();



/**
 * Pop-up cards: one small card that eases in next to the button that opens it, for the store information
 * (snippets/store-availability.liquid) and the sticky bar's color chooser (snippets/product-sticky-form.liquid,
 * snippets/hhl-option-chips.liquid). A button [data-hhl-pop-toggle] opens the card its aria-controls names
 * ([data-hhl-pop]); that button again, a click anywhere else, or Escape closes it. In a color chooser, a chip
 * [data-hhl-chip-value] sets the option's own select and closes the card, so the theme updates the product as usual.
 * A card meant to open upward opens downward when its button is high on the screen. Delegated, so it keeps working
 * when picking a variant redraws the bar or the pickup line.
 */
(function () {
  var toggleOf = function (card) { return document.querySelector('[data-hhl-pop-toggle][aria-controls="' + card.id + '"]'); };

  var shut = function (except) {
    document.querySelectorAll('[data-hhl-pop].is-open').forEach(function (card) {
      if (card === except) return;
      card.classList.remove('is-open');
      var toggle = toggleOf(card);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', function (event) {
    var t = event.target;
    var chip = t.closest && t.closest('[data-hhl-chip-value]');
    if (chip) {
      var wrap = chip.closest('.select-wrapper, .hhl-pop-anchor');
      var select = wrap && wrap.querySelector('select[data-option-position]');
      if (select) {
        select.value = chip.getAttribute('data-hhl-chip-value');
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      shut();
      return;
    }
    var toggle = t.closest && t.closest('[data-hhl-pop-toggle]');
    if (toggle) {
      var card = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!card) return;
      var open = !card.classList.contains('is-open');
      shut(card);
      // An upward card opens downward when there is more room below its button (the sticky bar at the top)
      if (open && card.classList.contains('hhl-popcard--up')) {
        var box = toggle.getBoundingClientRect();
        card.classList.toggle('is-down', box.top < window.innerHeight - box.bottom);
      }
      card.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (!(t.closest && t.closest('[data-hhl-pop]'))) shut();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') shut();
  });
})();

/**
 * In-page links to HHL sections ("See verified compatible parts" -> #hhl-pairs, and any other "#hhl-…" link):
 * instead of the browser's instant jump, the page glides there with an ease-in-out (about half a second, longer for
 * long distances, scaled by the theme's "Animation speed"), stopping just under the sticky header. Turning the wheel or
 * touching the screen hands control back at once. With reduced motion it goes straight there.
 */
(function () {
  var running = null;

  var headerOffset = function () {
    var header = document.querySelector('.shopify-section--header, store-header');
    var sticky = header && /sticky|fixed/.test(getComputedStyle(header).position);
    return (sticky ? header.getBoundingClientRect().height : 0) + 24;
  };

  var glide = function (to) {
    var from = window.scrollY, distance = to - from;
    if (Math.abs(distance) < 2) return;
    var speed = HHL.motion();
    var duration = Math.min(900, 380 + Math.abs(distance) * 0.25) * speed;
    var start = null;
    var stop = function () { running = null; window.removeEventListener('wheel', stop); window.removeEventListener('touchstart', stop); };
    var step = function (now) {
      if (running !== step) return;
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      var eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // ease-in-out
      window.scrollTo(0, from + distance * eased);
      if (t < 1) requestAnimationFrame(step); else stop();
    };
    running = step;
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    requestAnimationFrame(step);
  };

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="#hhl-"]');
    if (!link) return;
    var target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    event.preventDefault();
    // The target's own scroll margin wins when it's bigger (e.g. a policy, clear of the policy center's sticky bar)
    var margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    var to = Math.max(0, target.getBoundingClientRect().top + window.scrollY - Math.max(headerOffset(), margin));
    if (HHL.mq.reducedMotion.matches) window.scrollTo(0, to);
    else glide(to);
  });
})();


/**
 * Photo crops (snippets/hhl-crop-json.liquid): staff can set a focus point and zoom for a product photo in the HHL
 * app's Parts Catalog, so a square thumbnail shows the detail. Applied the same way as the app's builder
 * (app/lib/square-crop.ts): the photo covers its square box, centered on the focus, scaled from a point that keeps
 * the focus in view. Used by the compatible parts' tiles (here) and Compare (assets/hhl-compare.js).
 */
(function () {
  /** "…/files/set-black_400x.jpg?v=1&width=400" -> "set-black.jpg" */
  var fileOf = function (url) {
    var path = String(url || '').split('?')[0].split('#')[0];
    return path.slice(path.lastIndexOf('/') + 1).replace(/_\d*x\d*(?=\.[a-z0-9]+$)/i, '').toLowerCase();
  };

  var find = function (map, url) {
    if (!map || !url) return null;
    var want = fileOf(url);
    for (var key in map) if (Object.prototype.hasOwnProperty.call(map, key) && fileOf(key) === want) return map[key];
    return null;
  };

  var clamp = function (n) { return Math.max(0, Math.min(100, n)); };

  var apply = function (img, crop) {
    if (!img || !crop) return;
    var x = Number(crop.x), y = Number(crop.y), zoom = Math.max(1, Math.min(3, Number(crop.zoom) || 1));
    if (isNaN(x) || isNaN(y)) return;
    var ox = zoom > 1 ? clamp((zoom * x - 50) / (zoom - 1)) : x;
    var oy = zoom > 1 ? clamp((zoom * y - 50) / (zoom - 1)) : y;
    img.style.objectFit = 'cover';
    img.style.objectPosition = x + '% ' + y + '%';
    img.style.transformOrigin = ox + '% ' + oy + '%';
    img.style.transform = zoom > 1 ? 'scale(' + zoom + ')' : '';
    img.classList.add('hhl-cropped');
  };

  /** Every photo of one compatible-parts tile that has a crop */
  var slide = function (root) {
    var node = root.querySelector('script[data-hhl-crop]');
    if (!node) return;
    var map = null;
    try { map = JSON.parse(node.textContent); } catch (e) { map = null; }
    if (!map) return;
    root.querySelectorAll('img').forEach(function (img) { apply(img, find(map, img.currentSrc || img.src)); });
  };

  window.HHLCrop = { find: find, apply: apply, slide: slide };

  var all = function () { document.querySelectorAll('[data-hhl-pairs-slide]').forEach(slide); };
  all();
})();


/**
 * Which form was sent (newsletter forms: the mail panel, the footer, the newsletter pop-up). After any newsletter
 * sign-up Shopify marks EVERY newsletter form on the next page as sent, so each would show a thank-you (and the pop-up
 * would open itself). The page comes back as ?customer_posted=true#<form id>: only that form shows its thank-you (or
 * its error) in place of its fields, and opens the panel or pop-up it lives in ([data-hhl-open-on-done]).
 */
(function () {
  var run = function () {
    var id = decodeURIComponent((location.hash || '').slice(1));
    if (!id) return;
    var form = document.getElementById(id);
    // The phone account sheet's newsletter form may already sit inside Shopify's sheet (custom.js "Account sheet")
    (window.HHLShadowRoots || []).forEach(function (root) { form = form || root.getElementById(id); });
    if (!form || form.tagName !== 'FORM') return;
    var done = form.querySelector('[data-hhl-form-done]');
    var error = form.querySelector('[data-hhl-form-error]');
    if (!done && !error) return;
    if (done) {
      done.hidden = false;
      var fields = form.querySelector('[data-hhl-form-fields]');
      if (fields) fields.hidden = true;
    } else {
      error.hidden = false;
    }
    var host = form.getAttribute('data-hhl-open-on-done');
    var target = host && document.getElementById(host);
    if (!target) return;
    if (target.tagName === 'SHOPIFY-ACCOUNT') {
      // Shopify's account sheet: open it the way a tap would, once it's ready (only where it shows: phones)
      customElements.whenDefined('shopify-account').then(function () {
        setTimeout(function () {
          var button = target.shadowRoot && target.shadowRoot.querySelector('button');
          if (button && target.offsetParent) button.click();
        }, 300);
      });
    } else if (target.hasAttribute('data-hhl-pop')) {
      target.classList.add('is-open');
      var toggle = document.querySelector('[data-hhl-pop-toggle][aria-controls="' + host + '"]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    } else if ('open' in target) {
      target.open = true; // the theme's newsletter pop-up
    }
  };
  run();
})();

/**
 * Reviews fold (the Judge.me widget on product pages): the title, the star summary and the customer photos stay in
 * view; the review search, the Reviews / Questions tabs and both lists fold away together behind "Read all N
 * reviews", which eases them open and shut. Following a link to #reviews (the stars under the title, on cards) opens
 * it. The folded parts are only moved into one wrapper inside the widget, so Judge.me keeps working on them.
 */
(function () {
  var reduced = function () { return HHL.mq.reducedMotion.matches; };
  var FOLDED = '.jdgm-row-search, .jdgm-rev-widg__actions, .jdgm-rev-widg__body, .jdgm-quest-widg__body, .jdgm-rev-widg__paginate-spinner-wrapper';

  // Open and shut by animating the height (Web Animations), its length following the distance (a long list takes a
  // little longer, never more than about 0.7 s), with a fade. Shutting while scrolled into the list glides the page
  // back to the button at the same pace, so the page doesn't jump when the list goes.
  var ease = function (fold, opening, button) {
    // Only about a screen of it moves (a long list is mostly off-screen): the visible part eases at a calm pace
    var full = Math.min(fold.scrollHeight, Math.round(window.innerHeight * 1.2));
    var speed = HHL.motion();
    var duration = Math.min(650, 280 + full * 0.35) * speed;
    if (reduced() || typeof fold.animate !== 'function') {
      fold.style.maxHeight = opening ? 'none' : '0px';
      return;
    }
    fold.style.maxHeight = 'none';
    fold.style.overflow = 'hidden';
    var run = fold.animate(
      opening ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }] : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: duration, easing: HHL.ease }
    );
    run.onfinish = function () {
      fold.style.maxHeight = opening ? 'none' : '0px';
      fold.style.overflow = '';
    };
    if (!opening && button) {
      var target = button.getBoundingClientRect().top + window.scrollY - 140;
      if (target < window.scrollY) {
        var from = window.scrollY, start = null;
        var step = function (now) {
          if (start === null) start = now;
          var t = Math.min(1, (now - start) / duration);
          var e = 1 - Math.pow(1 - t, 3);
          window.scrollTo(0, from + (target - from) * e);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }
  };

  var setUp = function (widget) {
    var header = widget.querySelector('.jdgm-rev-widg__header');
    var existing = widget.querySelector('.hhl-rev-fold');
    if (existing) {
      // Parts Judge.me adds later (the review search) join the fold, at its top
      widget.querySelectorAll(FOLDED).forEach(function (part) {
        if (part.parentElement !== existing) existing.insertBefore(part, existing.firstChild);
      });
      return;
    }
    if (!header || !widget.querySelector('.jdgm-rev-widg__body')) return;
    var count = parseInt(widget.getAttribute('data-number-of-reviews'), 10) || widget.querySelectorAll('.jdgm-rev').length;
    if (!count) return;

    var fold = document.createElement('div');
    fold.className = 'hhl-rev-fold';
    widget.querySelectorAll(FOLDED).forEach(function (part) { fold.appendChild(part); });

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'hhl-rev-toggle';
    button.setAttribute('aria-expanded', 'false');
    var label = function (open) { button.textContent = open ? 'Hide reviews' : 'Read all ' + count + ' review' + (count === 1 ? '' : 's'); };
    label(false);

    header.insertAdjacentElement('afterend', button);
    button.insertAdjacentElement('afterend', fold);
    widget.classList.add('hhl-rev-folded');

    var toggle = function (open) {
      if (open === !widget.classList.contains('hhl-rev-folded')) return;
      widget.classList.toggle('hhl-rev-folded', !open);
      button.setAttribute('aria-expanded', String(open));
      label(open);
      ease(fold, open, button);
    };
    button.addEventListener('click', function () { toggle(widget.classList.contains('hhl-rev-folded')); });
    widget.hhlOpenReviews = function () { toggle(true); };
    if (location.hash === '#reviews') toggle(true);
  };

  var scan = function () { document.querySelectorAll('.jdgm-rev-widg').forEach(setUp); };
  // Judge.me draws the widget after the page loads: watch for it
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  scan();

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href$="#reviews"]');
    if (!link) return;
    var widget = document.querySelector('.jdgm-rev-widg');
    if (widget && widget.hhlOpenReviews) widget.hhlOpenReviews();
  });
})();

/**
 * Account sheet (Shopify's <shopify-account>, sections/header.liquid): its inside is closed to the page's CSS, so the
 * theme's look is handed to it here: the heading is a small label like the other pop-up cards' titles, the close
 * button is the theme's icon button, and the account links are the same tinted rows as the mail panel's. The
 * "Email me with news and offers" box is left out (the mail panel has the newsletter); it starts ticked, so it's
 * unticked first and signing in never subscribes anyone.
 */
(function () {
  // A <style> inside each shadow root (the component swaps its own adopted sheets as it redraws, which would drop
  // ours); every rule is !important because adopted sheets come after <style> elements
  var sheet = function (css) {
    return css.replace(/;/g, ' !important;');
  };

  var accountSheet = sheet([
    '.account__header { align-items: center; }',
    '.account__heading { color: rgba(var(--text-color), var(--hhl-alpha-muted)); font-family: var(--text-font-family);',
    '  font-size: var(--hhl-text-xs); font-weight: 600; letter-spacing: var(--hhl-label-tracking); text-transform: uppercase; line-height: 1.4; }',
    '.account__close { width: var(--hhl-icon-btn-size); height: var(--hhl-icon-btn-size); color: rgb(var(--heading-color));',
    '  background: rgb(var(--background)); border-radius: var(--hhl-icon-btn-radius);',
    '  box-shadow: inset 0 0 0 999px rgba(var(--text-color), var(--hhl-tint-hover)); transition: box-shadow var(--hhl-dur-fast) var(--hhl-ease); }',
    '@media (hover: hover) { .account__close:hover { background: rgb(var(--background)); box-shadow: inset 0 0 0 999px rgba(var(--text-color), var(--hhl-tint-strong)); } }',
    '.account__header { padding: 12px 12px 0 16px; }',
    '.account__content { margin-top: 0; padding: 12px 16px 16px; }',
    'button:focus:not(:focus-visible), a:focus:not(:focus-visible) { outline: none; }',
    // No blue tap flash on its own buttons either (it sets its own tap color)
    ':host, *, *::before, *::after { -webkit-tap-highlight-color: transparent; }',
    // Signed in: the avatar is drawn like the theme's other icons, an outline ring in the icon color with the initial
    // inside (no accent fill, no Shop coin ring, shadow, flip or hover growth)
    '.account-button, .account-button__avatar { color: inherit; }',
    '.account-button__avatar, .account-button__avatar:hover { background: transparent; box-shadow: inset 0 0 0 1.8px currentColor; scale: none; }',
    '.account-button__avatar::before, .account-button--shop-customer::after, .account-button__coin-front::before,',
    '  .account-button__coin-front::after, .account-button__coin-back { display: none; }',
    '.account-button--shop-customer, .account-button--shop-customer.animated { animation: none; transform: none; }',
    '.account-button__coin-front { background: transparent; color: currentColor; }',
    // The theme's own loaded bold (a weight it doesn't load would be faked, or swapped in later and shift the letter)
    '.account-button__avatar-initials { color: currentColor; font-family: var(--text-font-family); font-size: 11px;',
    '  font-weight: var(--text-font-bold-weight, 600); }',
    // Centered by the letter itself: the line box is trimmed to the capital's height, so the circle centers the
    // letter's ink (a font's line box sits the capital half a pixel high, which shows in a 24px circle)
    '.account-button__avatar { display: grid; place-items: center; }',
    '.account-button__avatar-initials { display: block; margin: 0; padding: 0; line-height: 1; letter-spacing: 0; text-box: trim-both cap alphabetic; }',
    '.account__shortcuts { display: grid; gap: 6px; background: none; border: 0; border-radius: 0; }',
    '.account__item-wrapper { border: 0; border-radius: 0; overflow: visible; }',
    '.account__item-wrapper::before, .account__item-wrapper::after, .account__item::before, .account__item::after { display: none; }',
    '.account__item { padding: 10px 12px; color: rgb(var(--text-color)); font-size: var(--hhl-text-md); line-height: 1.45;',
    '  background: rgba(var(--text-color), var(--hhl-tint)); border: var(--hhl-border-width) solid rgb(var(--border-color));',
    '  border-radius: var(--hhl-radius-small); transition: background var(--hhl-dur-fast) var(--hhl-ease), border-color var(--hhl-dur-fast) var(--hhl-ease); }',
    '@media (hover: hover) { .account__item:hover { background: rgba(var(--text-color), var(--hhl-tint-hover));',
    '  border-color: rgba(var(--text-color), var(--hhl-alpha-edge-strong)); } }',
    // Each link leads with its icon (Material Symbols, outlined, weight 300), in the accent color like the policy icons
    '.account__item { display: flex; align-items: center; gap: 10px; }',
    '.hhl-account-icon { flex: none; width: 20px; height: 20px; fill: rgb(var(--primary-button-background)); }',
    // Sign out: the last row, quieter than the links (no fill or edge, muted text and icon), the tint only on hover
    '.account__item.hhl-signout { color: rgba(var(--text-color), var(--hhl-alpha-muted)); background: transparent; border-color: transparent; }',
    '.hhl-signout .hhl-account-icon { fill: currentColor; }',
    '@media (hover: hover) { .account__item.hhl-signout:hover { color: rgb(var(--text-color)); background: rgba(var(--text-color), var(--hhl-tint));',
    '  border-color: transparent; } }'
  ].join('\n'));

  // Material Symbols outlined: logout
  var SIGN_OUT_ICON = 'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h280v80H200Zm440-160-55-58 102-102H360v-80h327L585-622l55-58 200 200-200 200Z';

  // The account links' icons (Material Symbols outlined 300: package_2, person, redeem), matched by where they go
  var ICONS = [
    [/\/account\/orders/, 'M457.31-155.92v-311.31L185.39-624.39V-320q0 3.08 1.53 5.77 1.54 2.69 4.62 4.61l265.77 153.7Zm45.38 0 265.77-153.7q3.08-1.92 4.62-4.61 1.53-2.69 1.53-5.77v-305L502.69-467.23v311.31Zm-51.54 48.61-282.3-163q-13.62-7.61-21.23-21.23-7.62-13.61-7.62-28.85v-319.22q0-15.24 7.62-28.85 7.61-13.62 21.23-21.23l282.3-163q13.62-7.62 28.85-7.62 15.23 0 28.85 7.62l282.3 163q13.62 7.61 21.23 21.23 7.62 13.61 7.62 28.85v319.22q0 15.24-7.62 28.85-7.61 13.62-21.23 21.23l-282.3 163q-13.62 7.62-28.85 7.62-15.23 0-28.85-7.62Zm185.93-490 111.23-64.38-262.16-151.69q-3.07-1.54-6.15-1.54-3.08 0-6.15 1.54l-104.31 60.15 267.54 155.92ZM480-505.54l112.08-65.61-268.93-155.16-112.07 64.62L480-505.54Z'],
    [/\/account\/profile/, 'M384.69-530.31q-37.38-37.38-37.38-95.5 0-58.11 37.38-95.3 37.39-37.2 95.31-37.2t95.31 37.2q37.38 37.19 37.38 95.3 0 58.12-37.38 95.5-37.39 37.39-95.31 37.39t-95.31-37.39ZM180-187.69v-75.93q0-32.23 17.08-56.15t44.38-36.77q63.16-28.07 121.77-42.31 58.62-14.23 116.77-14.23t116.46 14.54q58.31 14.54 121.46 42 27.92 12.85 45 36.77T780-263.62v75.93H180Zm45.39-45.39h509.22v-30.54q0-15.61-9.88-29.92-9.88-14.31-25.81-22.46-59-28.69-111.3-40.19-52.3-11.5-107.62-11.5-55.32 0-108.43 11.5-53.11 11.5-111.11 40.19-15.92 8.15-25.5 22.46-9.57 14.31-9.57 29.92v30.54Zm316.99-330.15q24.93-24.92 24.93-62.38 0-37.47-24.93-62.39-24.92-24.92-62.38-24.92T417.62-688q-24.93 24.92-24.93 62.39 0 37.46 24.93 62.38 24.92 24.92 62.38 24.92t62.38-24.92ZM480-625.61Zm0 392.53Z'],
    [/loyalty|rewards/i, 'M145.39-286.23v88.54q0 4.61 3.84 8.46 3.85 3.84 8.46 3.84h644.62q4.61 0 8.46-3.84 3.84-3.85 3.84-8.46v-88.54H145.39Zm12.3-413h137.7q-5-9-8.58-21.54t-3.58-23.7q0-39.65 27.71-67.21 27.72-27.55 66.6-27.55 25.61 0 47.58 13 21.96 13 34.75 33.16l19.22 29.84 20.22-29.84q13.69-20.77 34.65-33.47 20.96-12.69 45.73-12.69 40.31 0 67.92 27.31 27.62 27.31 27.62 67.61 0 10.62-3.08 21.62-3.08 11-8.46 23.46h138.62q23.53 0 40.61 17.08T860-641.54v443.85q0 23.53-17.08 40.61T802.31-140H157.69q-23.53 0-40.61-17.08T100-197.69v-443.85q0-23.53 17.08-40.61t40.61-17.08Zm-12.3 319.46h669.22v-261.77q0-4.61-3.84-8.46-3.85-3.85-8.46-3.85H551l93.07 129.77L608-496.92l-128.77-178-128.77 178-36.07-27.16 93.46-129.77H157.69q-4.61 0-8.46 3.85-3.84 3.85-3.84 8.46v261.77Zm267.76-329.49q14.54-14.49 14.54-35 0-20.51-14.49-35.05-14.49-14.53-35-14.53-20.51 0-35.05 14.49-14.53 14.49-14.53 35 0 20.51 14.49 35.04 14.49 14.54 35 14.54 20.51 0 35.04-14.49Zm166.54 14.49q21.08 0 35.62-14.49 14.53-14.49 14.53-35 0-20.51-14.53-35.05-14.54-14.53-35.62-14.53-19.69 0-34.31 14.49-14.61 14.49-14.61 35 0 20.51 14.61 35.04 14.62 14.54 34.31 14.54Z']
  ];

  // In the header (desktop) the sheet closes by clicking anywhere outside it (or Escape), so it has no close button
  var headerSheet = sheet([
    '.account__close { display: none; }',
    '.account__header { grid-template-rows: auto; padding: 16px 16px 0; min-height: 0; }',
    '.account__header-content { padding: 0; }',
    '.account__heading { min-height: 0; margin: 0; }',
    '.account__content { padding-top: 10px; }'
  ].join(' '));

  // On phones (the bottom bar's account) it's one of the bottom sheets, like the menu and the cart: it rises from
  // behind the bar (the bar stays in reach), with the same head (handle, title, close, a line under it), corners,
  // shadow and drag-to-close. It rests on the bar: the bar is its containing block, so bottom: 100% is the bar's top.
  var barSheet = sheet([
    '.dialog { position: fixed; z-index: -1; inset: auto 0 100% 0; width: min(100%, 600px); max-width: none; height: auto; min-height: 0;',
    '  max-height: calc(100dvh - var(--hhl-tabbar-space, 66px) - var(--hhl-sheet-gap, 40px)); margin: 0 auto; padding: 0;',
    '  border: var(--hhl-border-width) solid rgb(var(--border-color)); border-bottom: 0;',
    '  border-radius: var(--hhl-sheet-radius) var(--hhl-sheet-radius) 0 0; box-shadow: var(--hhl-sheet-shadow);',
    '  animation: none; translate: 0 var(--hhl-sheet-drag, 0px); transition: translate var(--hhl-dur) var(--hhl-ease); }',
    // Its slide in and out is run from script (HHLAccountSheet.sync), not Shopify's CSS animations
    '.dialog.closing { animation: none; }',
    ':host(.is-dragging) .dialog { transition: none; }',
    // The bar's account button: no focus ring left on it after a tap (the open sheet and the tab state show it)
    '.account-button:focus, .account-button:focus-visible { outline: none; box-shadow: none; }',
    '.account__header-region { position: relative; background: rgb(var(--background));',
    '  border-bottom: var(--hhl-border-width) solid rgb(var(--border-color)); touch-action: none; user-select: none; }',
    '.account__header::before { content: ""; position: absolute; top: 6px; left: calc(50% - 20px); width: 40px; height: 4px;',
    '  background: rgba(var(--text-color), .25); border-radius: 2px; }',
    '.account__header { position: relative; min-height: 56px; padding: var(--hhl-sheet-head-pad); }',
    '.account__heading { color: rgb(var(--heading-color)); font-family: var(--heading-font-family); font-weight: var(--heading-font-weight);',
    '  font-size: var(--hhl-sheet-title-size); letter-spacing: var(--hhl-label-tracking); text-transform: uppercase; }',
    '.account__content { padding: 16px; }'
  ].join(' '));

  var loginSheet = sheet('.login-form__marketing-consent { display: none; }');

  var adopt = function (root, css, name) {
    name = name || 'base';
    if (!root || root.querySelector('style[data-hhl="' + name + '"]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-hhl', name);
    style.textContent = css;
    root.appendChild(style);
  };

  // Untick the news and offers box (it starts ticked) before it's hidden
  var untick = function (root) {
    var box = root && root.querySelector('.login-form__marketing-consent-checkbox');
    if (box && box.checked && !box.hhlUnticked) {
      box.hhlUnticked = true;
      // A real click (so the sign-in form's own state follows), kept from reaching the page, where it would read as a
      // click outside and close whatever was just opened
      box.addEventListener('click', function (event) { event.stopPropagation(); }, { once: true });
      box.click();
    }
  };

  // The phone sheet's newsletter and support (snippets/hhl-mail-panel.liquid, sheet: true, waiting hidden next to the
  // component): moved under the account links, in a shadow root of its own that loads the theme's stylesheets, so it
  // looks exactly like the mail panel without the theme's styles reaching Shopify's sheet
  window.HHLShadowRoots = window.HHLShadowRoots || [];
  var stylesheets = function () {
    return Array.prototype.filter.call(document.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      return /\/assets\/(theme|custom|theme-dark)\.css/.test(link.href);
    });
  };

  var addExtra = function (account, root) {
    var extra = account.parentElement && account.parentElement.querySelector(':scope > [data-hhl-account-extra]');
    if (extra) {
      var host = document.createElement('div');
      host.className = 'hhl-account-extra';
      var inner = host.attachShadow({ mode: 'open' });
      stylesheets().forEach(function (link) {
        var copy = document.createElement('link');
        copy.rel = 'stylesheet';
        copy.href = link.href;
        inner.appendChild(copy);
      });
      var style = document.createElement('style');
      // The light/dark button shows the icon of the chosen mode, which the theme keys to <html data-theme-pref>: the
      // host carries a copy, kept in step
      style.textContent = ':host { display: block; margin-top: 16px; text-align: start; }' +
        ':host([data-theme-pref="system"]) .theme-toggle__icon--system, :host([data-theme-pref="light"]) .theme-toggle__icon--light,' +
        ':host([data-theme-pref="dark"]) .theme-toggle__icon--dark { display: block; }';
      inner.appendChild(style);
      var copyPref = function () {
        var pref = document.documentElement.getAttribute('data-theme-pref');
        pref ? host.setAttribute('data-theme-pref', pref) : host.removeAttribute('data-theme-pref');
      };
      copyPref();
      new MutationObserver(copyPref).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme-pref'] });
      extra.hidden = false;
      inner.appendChild(extra);
      // Its footer's country button: the country list lives in the menu sheet, so open that on the list. Opened from
      // here, the list has no back button (it would lead to the menu, not back here), only close (is-country-only)
      inner.addEventListener('click', function (event) {
        if (!(event.target instanceof Element) || !event.target.closest('[data-hhl-country-open]')) return;
        var menuToggle = document.querySelector('[data-hhl-menu-toggle]');
        var menu = document.getElementById('hhl-menu-sheet');
        if (!menuToggle || !menu) return;
        menuToggle.click();
        var country = menu.querySelector('[data-hhl-country-open]');
        if (country) country.click();
        menu.classList.add('is-country-only');
        var watch = new MutationObserver(function () {
          if (menu.classList.contains('is-open')) return;
          watch.disconnect();
          setTimeout(function () { menu.classList.remove('is-country-only'); }, 400);
        });
        watch.observe(menu, { attributes: true, attributeFilter: ['class'] });
      });
      window.HHLShadowRoots.push(inner);
      account.hhlExtra = host;
    }
    var host = account.hhlExtra;
    if (!host || root.contains(host)) return;
    var place = root.querySelector('.account__menu-region') || root.querySelector('.account__content');
    if (place) place.after(host);
  };

  var dress = function (account) {
    var root = account.shadowRoot;
    if (!root) return;
    adopt(root, accountSheet);
    addExtra(account, root);
    // The account pages (Orders, Profile) live on Shopify's own site: they open in a new tab, so the store stays
    // where the customer left it (JOY rewards and other store links open here as usual)
    root.querySelectorAll('a[href]').forEach(function (link) {
      var away;
      try { away = new URL(link.href, location.href).host !== location.host; } catch (e) { away = false; }
      if (away && link.target !== '_blank') {
        link.target = '_blank';
        link.rel = 'noopener';
      }
    });
    // The store menu calls it "JOY rewards" (that name stays on Shopify's own account pages); here it's just Rewards.
    // Each link gets its icon on the left
    root.querySelectorAll('.account__item').forEach(function (link) {
      var text = link.querySelector('.account__item-text');
      if (text && /\bjoy\b/i.test(text.textContent)) text.textContent = 'Rewards';
      if (link.querySelector('.hhl-account-icon')) return;
      var href = link.getAttribute('href') || '';
      var icon = ICONS.filter(function (entry) { return entry[0].test(href); })[0];
      if (!icon) return;
      link.insertAdjacentHTML('afterbegin', '<svg class="hhl-account-icon" viewBox="0 -960 960 960" aria-hidden="true" focusable="false"><path d="' + icon[1] + '"/></svg>');
    });
    // Shopify's sheet has no way to sign out: a Sign out row after the account links (the theme's logout route, from
    // data-hhl-logout in sections/header.liquid). Only on the loaded list, so it never sits under the loading rows, and
    // only signed in: signed out, Shopify lists the same links under its sign-in form. Its signed-in avatar is the
    // live sign (the page's data-hhl-signed-in can be a cached page's); the row goes again if that avatar does
    var logout = account.getAttribute('data-hhl-logout');
    var links = root.querySelector('.account__shortcuts:not(.account__shortcuts--loading)');
    var signedIn = !!root.querySelector('.account-button__avatar');
    var row = root.querySelector('.hhl-signout');
    if (row && !signedIn) row.closest('.account__item-wrapper').remove();
    if (logout && links && signedIn && !links.querySelector('.hhl-signout')) {
      links.insertAdjacentHTML('beforeend', '<div class="account__item-wrapper"><a class="account__item hhl-signout" href="' + logout +
        '" target="_top" data-no-instant><svg class="hhl-account-icon" viewBox="0 -960 960 960" aria-hidden="true" focusable="false"><path d="' +
        SIGN_OUT_ICON + '"/></svg><span class="account__item-text">Sign out</span></a></div>');
    }
    // Signed in, Shopify titles the sheet "Hi, <name>": it just says Account
    var heading = root.querySelector('.account__heading');
    if (heading && /^\s*(hi|hello|hey)\b/i.test(heading.textContent)) heading.textContent = 'Account';
    // Shown once styled: signed in (the page knows: data-hhl-signed-in), once Shopify's avatar is there, so its interim
    // person icon never flashes; it then fades in (custom.css "Account sheet")
    if (!account.hasAttribute('data-hhl-ready')) {
      // If Shopify's avatar never comes (a slow or failed load), show whatever is there after a few seconds
      if (!account.hhlReadyTimer) {
        account.hhlReadyTimer = setTimeout(function () { account.setAttribute('data-hhl-ready', ''); }, 4000);
      }
      var avatarIn = root.querySelector('.account-button__avatar');
      var buttonIn = root.querySelector('.account-button');
      // ...and once the page's fonts are in, so the initial is set in its final font and doesn't move after
      if (account.hasAttribute('data-hhl-signed-in') ? avatarIn : buttonIn) {
        var fontsIn = document.fonts && document.fonts.status !== 'loaded' ? document.fonts.ready : Promise.resolve();
        fontsIn.then(function () { account.setAttribute('data-hhl-ready', ''); });
      }
    }
    // Every account sheet is a dropdown or bottom sheet, not a modal: the rest of the page (and the bottom bar) stays
    // live; custom.js "Account dropdown and bottom sheet" closes it on a click outside, Escape or the account button
    var plain = root.querySelector('dialog');
    if (plain && !plain.hhlPlain) {
      plain.hhlPlain = true;
      plain.showModal = function () { this.show(); };
    }
    if (account.closest('.hhl-tabbar')) {
      adopt(root, barSheet, 'bar');
      var regionHead = root.querySelector('.account__header-region');
      if (regionHead) HHL.sheetDrag(regionHead, account, function () { HHLAccountSheet.shut(account); });
      if (plain && !plain.hhlTracked) {
        plain.hhlTracked = true;
        new MutationObserver(function () { HHLAccountSheet.sync(account); }).observe(plain, { attributes: true, attributeFilter: ['open', 'class'] });
      }
    }
    if (account.closest('store-header')) adopt(root, headerSheet, 'header');
    root.querySelectorAll('shopify-login-form').forEach(function (form) {
      adopt(form.shadowRoot, loginSheet);
      untick(form.shadowRoot);
      if (form.shadowRoot && !form.hhlWatched) {
        form.hhlWatched = true;
        new MutationObserver(function () { untick(form.shadowRoot); }).observe(form.shadowRoot, { childList: true, subtree: true });
      }
    });
    if (!account.hhlWatched) {
      account.hhlWatched = true;
      new MutationObserver(function () { dress(account); }).observe(root, { childList: true, subtree: true, characterData: true });
    }
  };

  var all = function () { document.querySelectorAll('shopify-account').forEach(dress); };
  if (window.customElements) customElements.whenDefined('shopify-account').then(all);
  document.addEventListener('open', function (event) {
    if (event.target && event.target.tagName === 'SHOPIFY-ACCOUNT') dress(event.target);
  }, true);
  // The bottom bar's account slot is redrawn when the session changes: dress any account component that arrives (only
  // then: running on every change anywhere on the page cost every tap in the builder)
  new MutationObserver(function (records) {
    var arrived = records.some(function (record) {
      return Array.prototype.some.call(record.addedNodes, function (node) {
        return node.nodeType === 1 && (node.tagName === 'SHOPIFY-ACCOUNT' || (node.querySelector && node.querySelector('shopify-account')));
      });
    });
    if (arrived) all();
  }).observe(document.body, { childList: true, subtree: true });
})();

/**
 * Header pop-ups open in the same place: the mail panel (a pop-up card) and the account sheet (Shopify's
 * <shopify-account>) both drop to 8px under the header bar, their right edge lined up with their own button's.
 * Measured as each one opens, so the announcement bar and a scrolled, shrunken header are both allowed for.
 */
(function () {
  var headerBottom = function () {
    var header = document.querySelector('store-header');
    return header ? header.getBoundingClientRect().bottom + 8 : null;
  };

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('store-header [data-hhl-pop-toggle]');
    if (toggle) {
      var card = document.getElementById(toggle.getAttribute('aria-controls'));
      var bottom = headerBottom();
      if (card && card.offsetParent && bottom !== null) card.style.setProperty('--hhl-pop-top', Math.round(bottom - card.offsetParent.getBoundingClientRect().top) + 'px');
      return;
    }
    var account = event.target.closest && event.target.closest('store-header shopify-account');
    if (account) {
      var top = headerBottom();
      var box = account.getBoundingClientRect();
      if (top !== null) account.style.setProperty('--shopify-account-dialog-position-top', Math.round(top) + 'px');
      account.style.setProperty('--hhl-pop-right', Math.max(8, Math.round(document.documentElement.clientWidth - box.right)) + 'px');
    }
  }, true);
})();

/**
 * Account dropdown and bottom sheet: the account sheet opens as a plain dropdown (desktop header) or bottom sheet
 * (phones' bottom bar), not a modal (custom.js "Account sheet"), so it closes the way the other pop-ups do: a click
 * or tap anywhere outside it (which then does what it was meant to: opens the mail panel, the cart, a link), Escape,
 * or the account button again. It closes through Shopify's own close button (hidden on desktop), so the sheet eases
 * out and tells the page it closed. On phones it also dims the page like the menu and cart (.hhl-sheet-scrim), marks
 * the Account tab, and is one of the bottom sheets that close each other (hhl:sheet-open).
 */
var HHLAccountSheet = (function () {
  var dialogOf = function (account) {
    return account.shadowRoot && account.shadowRoot.querySelector('dialog');
  };

  var openSheets = function () {
    return Array.prototype.filter.call(document.querySelectorAll('shopify-account'), function (account) {
      var dialog = dialogOf(account);
      return dialog && dialog.open && !dialog.matches(':modal') && !dialog.classList.contains('closing');
    });
  };

  var shut = function (account) {
    var dialog = dialogOf(account);
    if (!dialog || !dialog.open) return;
    var close = account.shadowRoot.querySelector('.account__close');
    if (close) close.click();
    else dialog.close();
  };

  // The page dimmer behind the phone sheet (the same as the menu's and cart's), made once
  var scrim = null;
  var scrimEl = function () {
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'hhl-sheet-scrim';
      scrim.setAttribute('aria-hidden', 'true');
      document.body.appendChild(scrim);
    }
    return scrim;
  };

  // The phone sheet's slide: up from behind the bar as it opens, back down as it closes (Shopify takes it away
  // --timing-default, .24s, after closing starts, so the slide down ends just before). Run with the Web Animations API
  // rather than CSS keyframes, which some phone browsers don't find inside Shopify's component
  var slide = function (dialog, up) {
    if (typeof dialog.animate !== 'function' || HHL.mq.reducedMotion.matches) return;
    var root = getComputedStyle(document.documentElement);
    var speed = HHL.motion();
    var ease = root.getPropertyValue('--hhl-ease').trim() || HHL.ease;
    if (dialog.hhlSlide) dialog.hhlSlide.cancel();
    dialog.hhlSlide = dialog.animate(
      up ? [{ transform: 'translateY(100%)' }, { transform: 'none' }] : [{ transform: 'none' }, { transform: 'translateY(100%)' }],
      { duration: up ? 350 * speed : 220, easing: up ? ease : 'cubic-bezier(.4, 0, 1, 1)', fill: up ? 'none' : 'forwards' }
    );
  };

  // The phone sheet opened or closed: its slide, the dimmer, the Account tab's state, and the other sheets
  var sync = function (account) {
    var dialog = dialogOf(account);
    var showing = !!(dialog && dialog.open && !dialog.classList.contains('closing'));
    var closing = !!(dialog && dialog.open && dialog.classList.contains('closing'));
    if (dialog && showing && !account.hhlAnnounced) slide(dialog, true);
    if (dialog && closing && !account.hhlClosing) slide(dialog, false);
    if (dialog && !dialog.open && dialog.hhlSlide) { dialog.hhlSlide.cancel(); dialog.hhlSlide = null; }
    account.hhlClosing = closing;
    var slot = account.closest('.hhl-tabbar__account');
    if (slot) slot.classList.toggle('is-open', showing);
    var tab = account.querySelector('[slot="signed-out-avatar"]');
    if (tab) tab.setAttribute('aria-expanded', String(showing));
    scrimEl().classList.toggle('is-open', showing);
    if (showing && !account.hhlAnnounced) document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'account'}));
    account.hhlAnnounced = showing;
  };

  document.addEventListener('hhl:sheet-open', function (event) {
    if (event.detail !== 'account') openSheets().forEach(shut);
  });

  // Where a tap counts as the account button itself: the bottom bar's whole account slot (avatar and label)
  var zoneOf = function (account) {
    return account.closest('.hhl-tabbar__account') || account;
  };

  document.addEventListener('pointerdown', function (event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    openSheets().forEach(function (account) {
      if (path.indexOf(dialogOf(account)) !== -1) return;
      // The account button: close, and keep its click from opening the sheet again
      if (path.indexOf(zoneOf(account)) !== -1) {
        account.hhlSkipClick = true;
        // ...but only that tap's click: a tap that moved (no click) mustn't swallow the next one
        setTimeout(function () { account.hhlSkipClick = false; }, 700);
      }
      shut(account);
    });
  }, true);

  document.addEventListener('click', function (event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    Array.prototype.forEach.call(document.querySelectorAll('shopify-account'), function (account) {
      if (!account.hhlSkipClick) return;
      account.hhlSkipClick = false;
      if (path.indexOf(zoneOf(account)) !== -1 && path.indexOf(dialogOf(account)) === -1) {
        event.stopPropagation();
        event.preventDefault();
      }
    });
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') openSheets().forEach(shut);
  });

  // Shopify locks the page's scroll while its sheet is open (overflow: hidden on <html>). For the header's dropdown
  // that's wrong: the page should stay as it is, and a locked <html> breaks the sticky header, which jumps away when
  // the page is scrolled down. While a header dropdown is open, the lock is let go.
  var root = document.documentElement;
  var unlock = function () {
    var headerOpen = openSheets().some(function (account) { return account.closest('store-header'); });
    if (!headerOpen) return;
    ['overflow', 'touch-action', 'scrollbar-gutter'].forEach(function (name) {
      if (root.style.getPropertyValue(name)) root.style.removeProperty(name);
    });
    if (document.body.style.getPropertyValue('overflow')) document.body.style.removeProperty('overflow');
  };
  new MutationObserver(unlock).observe(root, { attributes: true, attributeFilter: ['style'] });
  new MutationObserver(unlock).observe(document.body, { attributes: true, attributeFilter: ['style'] });
  document.addEventListener('open', function () { setTimeout(unlock, 0); }, true);

  return { shut: shut, sync: sync };
})();

/**
 * Rewards page (JOY Loyalty's blocks, templates/page.loyalty-page.json; the look is in custom.css "Rewards page"):
 *   - JOY's buttons become the theme's own buttons (Join: primary, Sign in: outline);
 *   - "Join" and "Sign in" open the account sheet right here (Shopify's in-page sign-in, like the header's account
 *     button) instead of leaving for the account pages; visitors get "Join free" again under the questions;
 *   - every way to earn and every reward shows (on phones JOY keeps some behind "Show more": pressed for them);
 *   - rewards are in order of the points they take, fewest first (JOY lists them as they were made; owner
 *     2026-10-20); a reward whose card names no points goes last;
 *   - each block fades in once JOY has drawn it (no spinners flashing by), with a fallback after a few seconds;
 *   - questions open and close with an eased height, not a jump;
 *   - JOY draws its blocks after the page loads (and again when the customer changes), so this runs on each change.
 */
(function () {
  var section = document.querySelector('.Avada-Joy__Block') && document.querySelector('.Avada-Joy__Block').closest('.shopify-section');
  if (!section) return;

  // The account sheet for the screen in use: the header's (desktop) or the bottom bar's (phones)
  var openAccount = function () {
    var account = Array.prototype.filter.call(document.querySelectorAll('shopify-account'), function (el) {
      return el.offsetParent !== null;
    })[0];
    var button = account && account.shadowRoot && account.shadowRoot.querySelector('button');
    if (!button) return false;
    button.click();
    return true;
  };

  // Visitors: the join button again once they've read it all (under the questions)
  var closing = function () {
    if (document.body.classList.contains('is-customer') || section.querySelector('.hhl-joy-cta')) return;
    var last = section.querySelector('.shopify-block:has(> .Joy-FAQ)');
    var join = section.querySelector('.Joy-Hero-Banner_ActionGroup--item.signup');
    if (!last || !join) return;
    var cta = document.createElement('div');
    cta.className = 'hhl-joy-cta';
    cta.innerHTML = "<p class=\"hhl-joy-cta__text\">Ready to start earning? It's free.</p>";
    var again = join.cloneNode(true);
    cta.appendChild(again);
    last.after(cta);
  };

  // A block is ready once JOY has put its real content in
  var CONTENT = '.Joy-Hero-Banner_Title, .Joy-WayEarn__Item, .Joy-Redeem__Item, .Joy-FAQ__Row, .Avada-Joy__Activity--tableRow, .Avada-Joy__Activity--text';
  var ready = function (all) {
    section.querySelectorAll('.shopify-block').forEach(function (block) {
      if (!block.classList.contains('hhl-joy-ready') && (all || block.querySelector(CONTENT))) block.classList.add('hhl-joy-ready');
    });
  };
  section.classList.add('hhl-joy-fade');
  setTimeout(function () { ready(true); }, 5000);

  // Phones: JOY builds only the first few of each list; "Show more" builds the rest (then reads "Show less")
  var showAll = function () {
    section.querySelectorAll('.Joy-WayEarn__ShowMore button, .Joy-WayRedeem__ShowMore button').forEach(function (button) {
      if (/show more/i.test(button.textContent)) button.click();
    });
  };

  // Members: under the balance, what it gets them now (rewards ready to redeem) and how far the next one is, worked
  // out from the balance in the hero and the costs on the reward cards ("1,500 points")
  var number = function (text) {
    var match = /(\d[\d,]*)\s*points?/i.exec(text || '');
    return match ? parseInt(match[1].replace(/,/g, ''), 10) : null;
  };
  var status = function () {
    if (!document.body.classList.contains('is-customer')) return;
    var content = section.querySelector('.Joy-Hero-Banner_Content');
    var title = section.querySelector('.Joy-Hero-Banner_Title');
    var balance = title && number(title.textContent);
    if (!content || balance === null) return;
    var rewards = Array.prototype.map.call(section.querySelectorAll('.Joy-Redeem__Item'), function (item) {
      var name = item.querySelector('.Joy-Redeem__Item-Title');
      return { name: name ? name.textContent.trim() : '', cost: number(item.textContent) };
    }).filter(function (reward) { return reward.cost; });
    if (!rewards.length) return;
    var ready = rewards.filter(function (reward) { return reward.cost <= balance; }).length;
    var next = rewards.filter(function (reward) { return reward.cost > balance; })
      .sort(function (a, b) { return a.cost - b.cost; })[0];
    var html = '<a class="hhl-joy-status__ready" href="#hhl-rewards">' +
      (ready ? '<strong>' + ready + (ready === 1 ? ' reward' : ' rewards') + '</strong> ready to redeem' : 'Your first reward is close') +
      ' <span aria-hidden="true">&darr;</span></a>';
    if (next) {
      var share = Math.max(4, Math.round(balance / next.cost * 100));
      html += '<p class="hhl-joy-status__next"><span>' + (next.cost - balance).toLocaleString() + ' points to ' +
        next.name.replace(/[<>&]/g, '') + '</span><span class="hhl-joy-status__bar"><span style="width:' + share + '%"></span></span></p>';
    }
    var box = content.querySelector('.hhl-joy-status');
    if (!box) {
      box = document.createElement('div');
      box.className = 'hhl-joy-status';
      content.appendChild(box);
    }
    // Compared with what was written last, not innerHTML (the browser re-spells it, which would redraw forever)
    if (box.hhlHtml !== html) {
      box.hhlHtml = html;
      box.innerHTML = html;
    }
    var redeem = section.querySelector('.shopify-block:has(.Joy-Redeem__List)');
    if (redeem && !redeem.id) redeem.id = 'hhl-rewards';
  };

  // Rewards by points, fewest first. Only cards out of place move, so watching for JOY's changes can't loop
  var sortRewards = function () {
    section.querySelectorAll('.Joy-Redeem__List').forEach(function (list) {
      var items = Array.prototype.slice.call(list.querySelectorAll('.Joy-Redeem__Item'));
      if (items.length < 2) return;
      var parent = items[0].parentElement;
      if (items.some(function (item) { return item.parentElement !== parent; })) return;
      var cost = function (item) { var n = number(item.textContent); return n === null ? Infinity : n; };
      var sorted = items.slice().sort(function (a, b) { return cost(a) - cost(b); });
      if (sorted.every(function (item, index) { return item === items[index]; })) return;
      var anchor = items[items.length - 1].nextSibling;
      sorted.forEach(function (item) { parent.insertBefore(item, anchor); });
    });
  };

  var dress = function () {
    showAll();
    sortRewards();
    ready(false);
    closing();
    status();
    section.querySelectorAll('.Joy-Hero-Banner_ActionGroup--item').forEach(function (link) {
      var join = link.classList.contains('signup');
      link.classList.add('button', join ? 'button--primary' : 'button--outline');
      link.classList.toggle('hhl-joy-join', join);
    });
  };

  // Questions: the answer's height eases open and shut (JOY's own toggle only flips it on and off)
  var reduced = function () { return HHL.mq.reducedMotion.matches; };
  section.addEventListener('click', function (event) {
    var question = event.target.closest && event.target.closest('.Joy-FAQ__Question');
    if (!question) return;
    event.preventDefault();
    event.stopPropagation();
    var row = question.closest('.Joy-FAQ__Row');
    var answer = row && row.querySelector('.Joy-FAQ__Answers');
    if (!answer) return;
    var opening = !row.classList.contains('hhl-open');
    var from = answer.getBoundingClientRect().height;
    row.classList.toggle('hhl-open', opening);
    question.setAttribute('aria-expanded', String(opening));
    var to = opening ? answer.scrollHeight : 0;
    if (reduced() || typeof answer.animate !== 'function') return;
    answer.animate([{ height: from + 'px', opacity: opening ? 0 : 1 }, { height: to + 'px', opacity: opening ? 1 : 0 }],
      { duration: Math.min(420, 220 + to * 0.4), easing: HHL.ease });
  }, true);

  section.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('.Joy-Hero-Banner_ActionGroup--item');
    if (!link || !/\/account\/(login|register)/.test(link.getAttribute('href') || '')) return;
    if (openAccount()) event.preventDefault();
  });

  dress();
  new MutationObserver(dress).observe(section, { childList: true, subtree: true });
})();

/**
 * Theme preview bar: while an unpublished theme is previewed, Shopify pins its own bar ("<theme> · Draft") over the
 * bottom of the screen, right on top of the phones' bottom bar, so a tap on Account or Cart went to Shopify's bar
 * instead. Its height is passed to the CSS (--hhl-preview-bar), which lifts the bottom bar and the sheets above it.
 * Customers never see the preview bar, so for them this does nothing.
 */
(function () {
  var root = document.documentElement;
  var watched = null;
  var measure = function () {
    var bar = document.getElementById('PBarNextFrameWrapper');
    var height = bar && bar.offsetParent !== null ? Math.round(bar.getBoundingClientRect().height) : 0;
    height ? root.style.setProperty('--hhl-preview-bar', height + 'px') : root.style.removeProperty('--hhl-preview-bar');
    if (bar && bar !== watched && window.ResizeObserver) {
      watched = bar;
      new ResizeObserver(measure).observe(bar);
    }
  };
  // Shopify adds the bar after the page loads: look for it a few times, then stop
  var tries = 0;
  var look = function () {
    measure();
    if (!watched && ++tries < 10) setTimeout(look, 500);
  };
  look();
})();

/**
 * Page-wide facts that appear after the page loads, as classes for the CSS (custom.css "Page-wide facts"; not
 * body:has(), which makes every class change anywhere restyle the whole page):
 *   hhl-has-bis (on <body>): Doran's back-in-stock button is on the page (it adds it after load, per variant);
 *   hhl-shopify-banner (on <html>): Shopify's own cookie banner is showing;
 *   hhl-viewer-open (on <html>): the product photo viewer is open.
 */
(function () {
  var root = document.documentElement;

  var bis = function () {
    document.body.classList.toggle('hhl-has-bis', !!document.querySelector('.dr-back-in-stock-inline-button'));
  };
  var banner = function () {
    root.classList.toggle('hhl-shopify-banner', !!document.getElementById('shopify-pc__banner'));
  };
  var check = function () { bis(); banner(); };

  // Apps add these a little after load: look a few times, then on each variant change (the button follows it)
  var tries = 0;
  var look = function () {
    check();
    if (++tries < 12) setTimeout(look, 500);
  };
  look();
  document.addEventListener('variant:changed', function () { setTimeout(bis, 300); }, true);
  // Shopify's banner comes and goes as a child of <body>: watch just that level
  new MutationObserver(banner).observe(document.body, { childList: true });

  // The photo viewer: PhotoSwipe marks its element pswp--open
  document.querySelectorAll('.pswp').forEach(function (viewer) {
    new MutationObserver(function () {
      root.classList.toggle('hhl-viewer-open', !!document.querySelector('.pswp--open'));
    }).observe(viewer, { attributes: true, attributeFilter: ['class'] });
  });
})();

/**
 * Policy module (snippets/hhl-policy.liquid): the full policy opens and shuts with an eased height rather than a jump
 * (it's a <details>, so without this script it still opens). Arriving on a link to a policy (#hhl-policy-refund, e.g.
 * from a short version shown elsewhere, or the policy center's list) opens that policy's full text.
 */
(function () {
  var reduced = function () { return HHL.mq.reducedMotion.matches; };

  var slide = function (details, opening) {
    var body = details.querySelector(':scope > .hhl-policy__body');
    if (!body || reduced() || typeof body.animate !== 'function') {
      details.open = opening;
      details.hhlOpening = undefined;
      return;
    }
    if (body.hhlSlide) body.hhlSlide.cancel();
    if (opening) details.open = true;
    var full = body.scrollHeight;
    var duration = Math.min(560, 240 + full * 0.08);
    body.hhlSlide = body.animate(
      opening ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }] : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: duration, easing: HHL.ease }
    );
    body.hhlSlide.onfinish = function () {
      body.hhlSlide = null;
      details.hhlOpening = undefined;
      if (!opening) details.open = false;
    };
  };

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('.hhl-policy__toggle');
    if (!toggle) return;
    var details = toggle.closest('details');
    if (!details) return;
    event.preventDefault();
    // Mid-slide, a second press reverses it
    var opening = details.hhlOpening === undefined ? !details.open : !details.hhlOpening;
    details.hhlOpening = opening;
    slide(details, opening);
  });

  // A link to a policy opens its full text (on arrival, and when followed on the page)
  var openFromHash = function () {
    var id = decodeURIComponent((location.hash || '').slice(1).split('?')[0]);
    if (!/^hhl-policy-/.test(id)) return;
    var policy = document.getElementById(id);
    var details = policy && policy.querySelector('details.hhl-policy__full');
    if (details && !details.open) details.open = true;
  };
  openFromHash();
  window.addEventListener('hashchange', openFromHash);
})();

/**
 * Policy center (sections/hhl-policies.liquid): the search box narrows the policies to those mentioning what's typed
 * (title, short version and full text), groups with nothing left step aside, and the list marks the ones that match;
 * the list also marks the policy being read as the page scrolls.
 */
(function () {
  var center = document.querySelector('[data-hhl-policies]');
  if (!center) return;

  var input = center.querySelector('[data-hhl-policies-search]');
  var empty = center.querySelector('[data-hhl-policies-empty]');
  var items = Array.prototype.slice.call(center.querySelectorAll('.hhl-policies__item'));
  var links = Array.prototype.slice.call(center.querySelectorAll('[data-hhl-policy-link]'));
  var linkFor = function (key) {
    return links.filter(function (link) { return link.getAttribute('data-hhl-policy-link') === key; })[0];
  };

  // Each policy's words, read once (lower case, for matching)
  var words = items.map(function (item) { return item.textContent.toLowerCase().replace(/\s+/g, ' '); });

  var filter = function () {
    var terms = input.value.toLowerCase().trim().split(/\s+/).filter(Boolean);
    var shown = 0;
    items.forEach(function (item, index) {
      var match = terms.every(function (term) { return words[index].indexOf(term) !== -1; });
      item.classList.toggle('is-filtered', !match);
      var policy = item.querySelector('[data-hhl-policy]');
      var link = policy && linkFor(policy.getAttribute('data-hhl-policy'));
      if (link) link.classList.toggle('is-filtered', !match);
      if (match) shown++;
    });
    center.querySelectorAll('[data-hhl-policies-group]').forEach(function (group) {
      group.classList.toggle('is-filtered', !group.querySelector('.hhl-policies__item:not(.is-filtered)'));
    });
    empty.hidden = shown > 0;
  };

  var timer = null;
  input.addEventListener('input', function () {
    clearTimeout(timer);
    timer = setTimeout(filter, 120);
  });

  // Phones: the "Jump to" bar opens and shuts the list under it; picking a policy (or tapping elsewhere) shuts it.
  // While the list is open, the bar stops following the screen and stays where it is on the page, so the page
  // scrolls as usual under a finger anywhere (the list included) and the rest of the list scrolls into view.
  // Shut again, it goes back to following the screen, easing in if it had scrolled out of sight.
  var nav = center.querySelector('[data-hhl-policies-nav]');
  var jump = center.querySelector('[data-hhl-policies-jump]');
  var list = center.querySelector('[data-hhl-policies-list]');
  var currentLabel = center.querySelector('[data-hhl-policies-current]');
  var hold = function () {
    if (getComputedStyle(nav).position !== 'sticky') return;
    var stuck = nav.getBoundingClientRect().top;
    nav.style.position = 'relative';
    nav.style.top = '0px';
    nav.style.top = (stuck - nav.getBoundingClientRect().top) + 'px';
  };
  var release = function () {
    if (!nav.style.position) return;
    var hidden = nav.getBoundingClientRect().bottom < 0;
    nav.style.position = '';
    nav.style.top = '';
    if (hidden && !HHL.mq.reducedMotion.matches && typeof nav.animate === 'function') {
      nav.animate([{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'none' }], { duration: 240, easing: HHL.ease });
    }
  };
  var setOpen = function (open) {
    if (open) {
      if (list) nav.style.setProperty('--hhl-list-height', list.scrollHeight + 24 + 'px');
      hold();
    } else {
      release();
    }
    nav.classList.toggle('is-open', open);
    jump.setAttribute('aria-expanded', String(open));
  };
  if (jump) {
    jump.addEventListener('click', function () { setOpen(!nav.classList.contains('is-open')); });
    nav.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-hhl-policy-link]')) setOpen(false);
    });
    document.addEventListener('click', function (event) {
      if (nav.classList.contains('is-open') && !nav.contains(event.target)) setOpen(false);
    });
  }

  // The list marks the policy in view (the one nearest the top third of the window)
  if ('IntersectionObserver' in window) {
    var visible = {};
    var mark = function () {
      var current = null;
      items.forEach(function (item) {
        var policy = item.querySelector('[data-hhl-policy]');
        if (!current && policy && visible[policy.id]) current = policy.getAttribute('data-hhl-policy');
      });
      links.forEach(function (link) { link.classList.toggle('is-current', link.getAttribute('data-hhl-policy-link') === current); });
      // Phones: the "Jump to" bar names the policy being read
      var active = current && linkFor(current);
      if (currentLabel) currentLabel.textContent = active ? active.textContent.trim() : 'All policies';
    };
    var watch = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { visible[entry.target.id] = entry.isIntersecting; });
      mark();
    }, { rootMargin: '-20% 0px -60% 0px' });
    items.forEach(function (item) {
      var policy = item.querySelector('[data-hhl-policy]');
      if (policy) watch.observe(policy);
    });
  }
})();

/**
 * Policy sections (snippets/hhl-policy.liquid): no wall of text. A policy's full text is cut into its own sections
 * (by its headings; or, where it has none, its "SECTION 1 - ..." capital-letter lines, or its "Label: ..." list
 * items), each a card numbered within its policy, with its own address (#refund-3) and a link button that copies
 * it: its title (a tap opens or shuts the card; cards start shut), then the section's own text, tidied (empty lines, pasted-in styling and a label repeating the title
 * dropped), never reworded. A policy that has no sections, or carries a script (Shopify's privacy choices form), is
 * left as it is.
 */
(function () {
  var slug = function (text) {
    return (text || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  };
  var clean = function (text) { return (text || '').replace(/\s+/g, ' ').trim(); };

  // The element holding the text's blocks (some policies wrap everything in one or more <div>s)
  var rootOf = function (inner) {
    var root = inner;
    while (root.children.length === 1 && root.firstElementChild.tagName === 'DIV') root = root.firstElementChild;
    return root;
  };

  // "ACCURACY OF BILLING AND ACCOUNT INFORMATION" -> "Accuracy of Billing and Account Information" (short joining
  // words lower case, abbreviations like US and VAT kept)
  var SMALL = /^(a|an|and|as|at|but|by|for|in|of|on|or|per|the|to|via|with)$/;
  var ABBR = /^(us|usa|vat|apo|fpo|dpo|ups|usps|dhl|lcd|ips|faq|id|eu|uk|diy)$/;
  var titleCase = function (text) {
    return text.toLowerCase().split(' ').map(function (word, index) {
      if (index > 0 && SMALL.test(word)) return word;
      return word.split(/([\/-])/).map(function (part) {
        var bare = part.replace(/[^a-z]/g, '');
        if (ABBR.test(bare)) return part.toUpperCase();
        return part.replace(/^([^a-z]*)([a-z])/, function (m, lead, letter) { return lead + letter.toUpperCase(); });
      }).join('');
    }).join(' ');
  };

  // Up to about 60 characters, ending on a whole word
  var wordCut = function (text) {
    if (text.length <= 60) return text;
    var cut = text.slice(0, 60);
    return cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:]+$/, '') + '…';
  };

  var isCapsLine = function (el) {
    if (el.tagName !== 'P') return false;
    // A short note in brackets after it doesn't count ("SECTION 5 - PRODUCTS OR SERVICES (if applicable)")
    var text = clean(el.textContent).replace(/\s*\([^)]*\)$/, '');
    return text.length > 3 && text.length <= 90 && /[A-Z]/.test(text) && text === text.toUpperCase() && !/^-+$/.test(text);
  };

  // [{ title, nodes: [...] }], the text before the first heading as "Overview"
  var split = function (root) {
    var kids = Array.prototype.slice.call(root.children);
    var levels = kids.filter(function (el) { return /^H[1-6]$/.test(el.tagName) && clean(el.textContent); })
      .map(function (el) { return +el.tagName.charAt(1); });
    var isHead;
    if (levels.length >= 2) {
      var top = Math.min.apply(null, levels);
      isHead = function (el) { return el.tagName === 'H' + top && !!clean(el.textContent); };
    } else if (kids.filter(isCapsLine).length >= 2) {
      isHead = isCapsLine;
    } else {
      // "Label: text" list items (each item its own section)
      var list = kids.filter(function (el) { return el.tagName === 'UL' || el.tagName === 'OL'; })[0];
      var items = list ? Array.prototype.slice.call(list.children) : [];
      var labeled = items.filter(function (li) { return /^[^:]{3,60}:/.test(clean(li.textContent)); });
      if (labeled.length < 2) return [];
      var sections = [];
      var before = kids.slice(0, kids.indexOf(list));
      var after = kids.slice(kids.indexOf(list) + 1);
      if (before.some(function (el) { return clean(el.textContent); })) sections.push({ title: 'Overview', nodes: before });
      items.forEach(function (li) {
        var text = clean(li.textContent);
        var colon = text.indexOf(':');
        var p = document.createElement('div');
        while (li.firstChild) p.appendChild(li.firstChild);
        sections.push(colon > 0 ? { title: text.slice(0, colon), nodes: [p], labeled: true } : { title: wordCut(text), key: text.slice(0, 50), nodes: [p] });
      });
      if (after.some(function (el) { return clean(el.textContent); })) {
        var rest = clean(after.map(function (el) { return el.textContent; }).join(' '));
        sections.push({ title: /question|contact|help/i.test(rest) ? 'Questions' : 'More Information', key: 'More', nodes: after });
      }
      return sections;
    }
    var out = [], current = { title: 'Overview', nodes: [] };
    kids.forEach(function (el) {
      if (isHead(el)) {
        if (current.nodes.some(function (n) { return clean(n.textContent); }) || current.title !== 'Overview') out.push(current);
        current = { title: clean(el.textContent), nodes: [], head: el };
      } else if (!/^-+$/.test(clean(el.textContent)) || el.tagName !== 'P') {
        current.nodes.push(el);
      }
    });
    out.push(current);
    var kept = [], carry = [];
    out.forEach(function (section) {
      var full = section.nodes.some(function (n) { return clean(n.textContent) || n.querySelector && n.querySelector('img, table'); });
      if (!full) {
        if (section.head) {
          var line = document.createElement('p');
          var bold = document.createElement('strong');
          bold.textContent = section.title;
          line.appendChild(bold);
          carry.push(line);
        }
        return;
      }
      if (carry.length) { section.nodes = carry.concat(section.nodes); carry = []; }
      kept.push(section);
    });
    if (carry.length && kept.length) kept[kept.length - 1].nodes = kept[kept.length - 1].nodes.concat(carry);
    return kept;
  };

  // The section's text, as written but tidier: no empty lines or stray line breaks, no sizes or colors pasted in from
  // elsewhere (bold, italic and underline stay), and no "Label:" at its start that only repeats the card's title
  var tidy = function (text, section) {
    // Pasted-in sizes, fonts, colors and alignment go; emphasis stays (bold, italic, underline are there for a reason)
    text.querySelectorAll('[style]').forEach(function (el) {
      var keep = ['font-weight', 'font-style', 'text-decoration', 'text-decoration-line'].map(function (name) {
        var value = el.style.getPropertyValue(name);
        return value ? name + ': ' + value : '';
      }).filter(Boolean).join('; ');
      if (keep) el.setAttribute('style', keep); else el.removeAttribute('style');
    });
    text.querySelectorAll('font').forEach(function (el) { el.replaceWith.apply(el, Array.prototype.slice.call(el.childNodes)); });
    // Empty paragraphs only: an empty-looking <span>&nbsp;</span> inside a line is the space between two words
    text.querySelectorAll('p, div, li').forEach(function (el) {
      if (!clean(el.textContent.replace(/\u00a0/g, ' ')) && !el.querySelector('img, table, iframe, video')) el.remove();
    });
    if (section.labeled) {
      var walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT);
      var label = section.title.length;
      var node;
      while (label > 0 && (node = walker.nextNode())) {
        if (label === section.title.length) node.nodeValue = node.nodeValue.replace(/^\s+/, '');
        var take = Math.min(label, node.nodeValue.length);
        node.nodeValue = node.nodeValue.slice(take);
        label -= take;
      }
      while (node && !node.nodeValue.trim()) node = walker.nextNode();
      if (node) node.nodeValue = node.nodeValue.replace(/^\s*:\s*/, '');
      text.querySelectorAll('strong, b').forEach(function (el) {
        if (clean(el.textContent)) return;
        if (el.textContent) el.replaceWith(document.createTextNode(' ')); else el.remove();
      });
    }
    var first = text.firstElementChild;
    while (first && first.tagName === 'BR') { first.remove(); first = text.firstElementChild; }
  };

  // Sections start shut: a tap on the title row opens (or shuts) one, its text easing open
  var setSection = function (card, open, animate) {
    var body = card.querySelector('.hhl-policy-sec__body');
    var toggle = card.querySelector('.hhl-policy-sec__toggle');
    if (!body || !toggle) return;
    if (card.classList.contains('is-open') === open && !body.hhlSlide) return;
    card.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (body.hhlSlide) body.hhlSlide.cancel();
    if (open) body.removeAttribute('hidden');
    var still = HHL.mq.reducedMotion.matches;
    if (!animate || still || typeof body.animate !== 'function') {
      body.hhlSlide = null;
      if (!open) body.setAttribute('hidden', 'until-found');
      return;
    }
    var full = body.scrollHeight;
    body.hhlSlide = body.animate(
      open ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }] : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: Math.min(480, 220 + full * 0.08), easing: HHL.ease }
    );
    body.hhlSlide.onfinish = function () {
      body.hhlSlide = null;
      if (!card.classList.contains('is-open')) body.setAttribute('hidden', 'until-found');
    };
  };

  document.addEventListener('click', function (event) {
    var head = event.target.closest && event.target.closest('.hhl-policy-sec__head');
    if (!head || event.target.closest('[data-hhl-policy-copy]')) return;
    var sel = window.getSelection();
    if (sel && !sel.isCollapsed && head.contains(sel.anchorNode)) return;
    var card = head.closest('.hhl-policy-sec');
    setSection(card, !card.classList.contains('is-open'), true);
  });

  var build = function (policy) {
    var inner = policy.querySelector('.hhl-policy__body-inner');
    if (!inner || inner.hhlSectioned || inner.querySelector('script, form, iframe')) return;
    inner.hhlSectioned = true;
    var root = rootOf(inner);
    var sections = split(root);
    if (sections.length < 2) return;
    var policyKey = (policy.id || '').replace(/^hhl-policy-/, '');
    var list = document.createElement('div');
    list.className = 'hhl-policy-secs';
    sections.forEach(function (section, index) {
      var card = document.createElement('section');
      card.className = 'hhl-policy-sec';
      var key = slug(section.key || section.title);
      var number = index + 1;
      card.setAttribute('data-hhl-policy-section', key);
      // Numbered per policy, and each one has its own address (#refund-3) to point a customer at
      if (policyKey) card.id = policyKey + '-' + number;
      var head = document.createElement('div');
      head.className = 'hhl-policy-sec__head';
      var num = document.createElement('span');
      num.className = 'hhl-policy-sec__num';
      num.textContent = number;
      var title = document.createElement('h4');
      title.className = 'hhl-policy-sec__title';
      // The card has its own number: the title's ("SECTION 4 - ...", "1. ...") goes. All caps reads as title case.
      var label = section.title.replace(/^SECTION\s+\d+\s*[-–:]\s*/i, '').replace(/^\d+[.)]\s+/, '');
      // (a note in brackets after the capitals keeps its own case: "Products or Services (if applicable)")
      var note = (/\s*\([^)]*\)$/.exec(label) || [''])[0];
      var core = label.slice(0, label.length - note.length);
      if (core === core.toUpperCase()) label = titleCase(core) + note;
      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'hhl-policy-sec__toggle';
      toggle.setAttribute('aria-expanded', 'false');
      var name = document.createElement('span');
      name.className = 'hhl-policy-sec__name';
      name.textContent = label;
      toggle.appendChild(name);
      toggle.insertAdjacentHTML('beforeend', '<svg class="hhl-policy-sec__chevron" viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="m1 1.5 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>');
      title.appendChild(toggle);
      var body = document.createElement('div');
      body.className = 'hhl-policy-sec__body';
      body.setAttribute('hidden', 'until-found');
      if (card.id) {
        body.id = card.id + '-text';
        toggle.setAttribute('aria-controls', body.id);
      }
      var text = document.createElement('div');
      text.className = 'hhl-policy-sec__text rte';
      section.nodes.forEach(function (node) { text.appendChild(node); });
      tidy(text, section);
      body.appendChild(text);
      // The browser's find landing in a shut section opens it
      body.addEventListener('beforematch', function () { setSection(card, true, false); });
      head.appendChild(num);
      head.appendChild(title);
      if (card.id) {
        var copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'hhl-policy-copy';
        copy.setAttribute('data-hhl-policy-copy', '');
        copy.setAttribute('aria-label', 'Copy link to section ' + number + ': ' + title.textContent);
        copy.setAttribute('data-hhl-copy-label', copy.getAttribute('aria-label'));
        copy.title = 'Copy link to this section';
        copy.innerHTML = '<svg class="hhl-policy-copy__link" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M10 13.5a4 4 0 0 0 6 .4l2.6-2.6a4 4 0 0 0-5.7-5.7l-1.4 1.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 10.5a4 4 0 0 0-6-.4l-2.6 2.6a4 4 0 0 0 5.7 5.7l1.4-1.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><svg class="hhl-policy-copy__done" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5.5 12.5 4 4 9-9" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="hhl-policy-copy__tip" aria-hidden="true">Link copied</span>';
        head.appendChild(copy);
      }
      card.appendChild(head);
      card.appendChild(body);
      list.appendChild(card);
    });
    // What's left of the original (headings, rules, empty lines) goes; the sections take its place
    root.innerHTML = '';
    root.appendChild(list);
    policy.classList.add('has-sections');
    var count = policy.querySelector('[data-hhl-policy-count]');
    if (count) count.textContent = '(' + sections.length + ' sections)';
  };

  // ---- Highlights. Select text in a section and a Highlight button appears under it; a highlight can be taken off
  // again (tap it: Remove highlight). The link buttons carry them: a section's link its own, a policy's link all of
  // its sections'. In the address after "?": hl = section.start.length for each (character offsets in the section's
  // text), and c = a short check of the highlighted words, so a link made before the policy's wording changed opens
  // its section but marks nothing rather than the wrong words. Several highlights, in several sections, can travel in
  // one link.
  var MARK = 'hhl-policy-mark';
  var textBox = function (card) { return card.querySelector('.hhl-policy-sec__text'); };
  var check = function (str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0).toString(36);
  };
  // Characters in a section's text before a point in it
  var offsetOf = function (box, node, offset) {
    var range = document.createRange();
    range.setStart(box, 0);
    range.setEnd(node, offset);
    return range.toString().length;
  };
  // The section's highlights as [start, end] pairs, in order
  var rangesOf = function (box) {
    var out = [], seen = {};
    box.querySelectorAll('mark.' + MARK).forEach(function (mark) {
      var id = mark.getAttribute('data-hhl-mark');
      if (seen[id]) return;
      seen[id] = true;
      var parts = box.querySelectorAll('mark[data-hhl-mark="' + id + '"]');
      var last = parts[parts.length - 1];
      out.push([offsetOf(box, mark, 0), offsetOf(box, last, last.childNodes.length)]);
    });
    return out.sort(function (a, b) { return a[0] - b[0]; });
  };
  var unwrap = function (box) {
    box.querySelectorAll('mark.' + MARK).forEach(function (mark) {
      while (mark.firstChild) mark.parentNode.insertBefore(mark.firstChild, mark);
      mark.remove();
    });
    box.normalize();
  };
  var markCount = 0;
  // Wraps [start, end) of the section's text in marks (one per stretch of text it covers), all one highlight
  var wrap = function (box, start, end) {
    var id = String(++markCount);
    var walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
    var pos = 0, node, parts = [];
    while ((node = walker.nextNode()) && pos < end) {
      var len = node.nodeValue.length;
      var a = Math.max(start - pos, 0), b = Math.min(end - pos, len);
      if (a < b) parts.push([node, a, b]);
      pos += len;
    }
    parts.forEach(function (part) {
      var piece = part[0];
      if (part[2] < piece.nodeValue.length) piece.splitText(part[2]);
      if (part[1] > 0) piece = piece.splitText(part[1]);
      if (!piece.nodeValue.trim()) return;
      var mark = document.createElement('mark');
      mark.className = MARK;
      mark.setAttribute('data-hhl-mark', id);
      piece.parentNode.insertBefore(mark, piece);
      mark.appendChild(piece);
    });
  };
  // Sets a section's highlights (overlapping or touching ones merge into one)
  var setRanges = function (box, ranges) {
    ranges = ranges.slice().sort(function (a, b) { return a[0] - b[0]; });
    var merged = [];
    ranges.forEach(function (r) {
      var prev = merged[merged.length - 1];
      if (prev && r[0] <= prev[1]) prev[1] = Math.max(prev[1], r[1]);
      else merged.push([r[0], r[1]]);
    });
    unwrap(box);
    merged.forEach(function (r) { wrap(box, r[0], r[1]); });
  };

  // What a link carries: "?hl=...&c=..." for the highlights in these sections ('' when there are none)
  var linkQuery = function (cards) {
    var items = [], words = '';
    cards.forEach(function (card) {
      var box = textBox(card);
      var n = card.querySelector('.hhl-policy-sec__num');
      if (!box || !n) return;
      var text = box.textContent;
      rangesOf(box).forEach(function (r) {
        items.push(n.textContent + '.' + r[0] + '.' + (r[1] - r[0]));
        words += text.slice(r[0], r[1]);
      });
    });
    return items.length ? '?hl=' + items.join(',') + '&c=' + check(words) : '';
  };

  // A link's highlights put back (all, or none if the check doesn't match the words now there); the first one
  var restore = function (policy, query) {
    var params = new URLSearchParams(query);
    var list = (params.get('hl') || '').split(',').filter(Boolean);
    var key = (policy.id || '').replace(/^hhl-policy-/, '');
    if (!list.length || !key) return null;
    var plan = [], words = '';
    for (var i = 0; i < list.length; i++) {
      var bits = list[i].split('.').map(Number);
      var card = document.getElementById(key + '-' + bits[0]);
      var box = card && textBox(card);
      if (!box || !(bits[2] > 0) || bits[1] < 0 || bits[1] + bits[2] > box.textContent.length) return null;
      plan.push([box, bits[1], bits[1] + bits[2]]);
      words += box.textContent.slice(bits[1], bits[1] + bits[2]);
    }
    if (check(words) !== params.get('c')) return null;
    var byBox = new Map();
    plan.forEach(function (item) {
      if (!byBox.has(item[0])) byBox.set(item[0], rangesOf(item[0]));
      byBox.get(item[0]).push([item[1], item[2]]);
    });
    byBox.forEach(function (ranges, box) {
      setRanges(box, ranges);
      setSection(box.closest('.hhl-policy-sec'), true, false);
    });
    return policy.querySelector('mark.' + MARK);
  };

  // The floating button: Highlight (under a selection in a section) or Remove highlight (under a tapped highlight)
  var pop = document.createElement('button');
  pop.type = 'button';
  pop.className = 'hhl-mark-pop';
  pop.hidden = true;
  var popFor = null;
  var showPop = function (rect, label, action) {
    pop.textContent = label;
    popFor = action;
    if (!pop.isConnected) document.body.appendChild(pop);
    pop.hidden = false;
    var width = pop.offsetWidth;
    var left = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8);
    // Under the selection (on phones the system's own copy menu sits above it)
    var top = Math.min(rect.bottom + 10, window.innerHeight - pop.offsetHeight - 8);
    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
    requestAnimationFrame(function () { pop.classList.add('is-shown'); });
  };
  var hidePop = function () {
    if (pop.hidden) return;
    popFor = null;
    pop.classList.remove('is-shown');
    clearTimeout(pop.hhlHide);
    pop.hhlHide = setTimeout(function () { if (!pop.classList.contains('is-shown')) pop.hidden = true; }, 200);
  };
  // Keeps the selection when the button is pressed
  pop.addEventListener('pointerdown', function (event) { event.preventDefault(); });
  pop.addEventListener('mousedown', function (event) { event.preventDefault(); });
  pop.addEventListener('click', function () {
    var action = popFor;
    hidePop();
    if (action) action();
  });

  var selectedIn = function () {
    var sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
    var range = sel.getRangeAt(0);
    var el = function (node) { return node.nodeType === 1 ? node : node.parentElement; };
    var box = el(range.startContainer) && el(range.startContainer).closest('.hhl-policy-sec__text');
    if (!box || !box.contains(range.endContainer) || !range.toString().trim()) return null;
    return { box: box, range: range };
  };
  var pending = 0;
  document.addEventListener('selectionchange', function () {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(function () {
      var hit = selectedIn();
      if (!hit) { if (popFor && popFor.select) hidePop(); return; }
      var action = function () {
        var box = hit.box, range = hit.range, text = box.textContent;
        var start = offsetOf(box, range.startContainer, range.startOffset);
        var end = offsetOf(box, range.endContainer, range.endOffset);
        while (start < end && /\s/.test(text.charAt(start))) start++;
        while (end > start && /\s/.test(text.charAt(end - 1))) end--;
        if (end <= start) return;
        var ranges = rangesOf(box);
        ranges.push([start, end]);
        setRanges(box, ranges);
        window.getSelection().removeAllRanges();
      };
      action.select = true;
      showPop(hit.range.getBoundingClientRect(), 'Highlight', action);
    });
  });

  // A tap on a highlight offers to take it off (a tap, not the start of a new selection)
  document.addEventListener('click', function (event) {
    var mark = event.target.closest && event.target.closest('mark.' + MARK);
    if (!mark) {
      if (popFor && !popFor.select && event.target !== pop) hidePop();
      return;
    }
    var sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    var box = mark.closest('.hhl-policy-sec__text');
    var id = mark.getAttribute('data-hhl-mark');
    var parts = box.querySelectorAll('mark[data-hhl-mark="' + id + '"]');
    showPop(mark.getBoundingClientRect(), 'Remove highlight', function () {
      parts.forEach(function (part) { part.classList.add('is-leaving'); });
      setTimeout(function () {
        var keep = rangesOf(box).filter(function (r) { return r[0] !== offsetOf(box, parts[0], 0); });
        setRanges(box, keep);
      }, 200);
    });
  });
  window.addEventListener('scroll', function () { if (popFor && !popFor.select) hidePop(); }, { passive: true });

  // A link to a policy or a section (#refund-3) opens the policy, brings the section into view and marks it briefly;
  // its highlights, if it carries any, are put back and the first is brought into view instead
  var openFromHash = function () {
    var raw = (location.hash || '').slice(1);
    var cut = raw.indexOf('?');
    var id = decodeURIComponent(cut < 0 ? raw : raw.slice(0, cut));
    var query = cut < 0 ? '' : raw.slice(cut + 1);
    var target = id && document.getElementById(id);
    if (!target) return;
    var isSection = target.classList.contains('hhl-policy-sec');
    var policy = isSection ? target.closest('.hhl-policy') : (target.classList.contains('hhl-policy') ? target : null);
    if (!policy) return;
    var first = query ? restore(policy, query) : null;
    if (!isSection && !first) return;
    var full = policy.querySelector('details.hhl-policy__full');
    if (full) full.open = true;
    var card = isSection ? target : first.closest('.hhl-policy-sec');
    setSection(card, true, false);
    requestAnimationFrame(function () {
      if (first) first.scrollIntoView({ block: 'center' });
      else card.scrollIntoView({ block: 'start' });
      card.classList.remove('is-linked');
      void card.offsetWidth;
      card.classList.add('is-linked');
      clearTimeout(card.hhlLinked);
      card.hhlLinked = setTimeout(function () { card.classList.remove('is-linked'); }, 2400);
    });
  };

  var copyText = function (text) {
    var legacy = function () { return new Promise(function (resolve, reject) {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(area);
      area.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      area.remove();
      if (ok) resolve(); else reject();
    }); };
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text).catch(legacy);
    return legacy();
  };

  // The link button (on each policy, and each of its sections) copies its address; it turns into a check with
  // "Link copied" for a moment
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-hhl-policy-copy]');
    if (!button) return;
    var card = button.closest('.hhl-policy-sec') || button.closest('.hhl-policy');
    if (!card || !card.id) return;
    var cards = card.classList.contains('hhl-policy-sec') ? [card] : Array.prototype.slice.call(card.querySelectorAll('.hhl-policy-sec'));
    var query = linkQuery(cards);
    // ?policy=refund&section=13 lets a link preview (Discord, iMessage) name the policy; the # part opens it here
    var params = new URLSearchParams(location.search);
    params.delete('policy');
    params.delete('section');
    var policyEl = card.closest('.hhl-policy');
    if (policyEl && policyEl.getAttribute('data-hhl-policy')) params.set('policy', policyEl.getAttribute('data-hhl-policy'));
    var num = card.classList.contains('hhl-policy-sec') && card.querySelector('.hhl-policy-sec__num');
    if (num) params.set('section', num.textContent);
    var search = params.toString();
    var url = location.origin + location.pathname + (search ? '?' + search : '') + '#' + card.id + query;
    var tip = button.querySelector('.hhl-policy-copy__tip');
    if (tip) tip.textContent = query ? 'Link with highlights copied' : 'Link copied';
    copyText(url).then(function () {
      button.classList.add('is-copied');
      button.setAttribute('aria-label', 'Link copied');
      clearTimeout(button.hhlCopied);
      button.hhlCopied = setTimeout(function () {
        button.classList.remove('is-copied');
        button.setAttribute('aria-label', button.getAttribute('data-hhl-copy-label'));
      }, 1800);
    }, function () {
      location.hash = card.id;
    });
  });

  var all = function () { document.querySelectorAll('.hhl-policy').forEach(build); openFromHash(); };
  all();
  document.addEventListener('shopify:section:load', all);
  window.addEventListener('hashchange', openFromHash);
})();

/**
 * Home links on the home page (owner 2026-10-19): a plain click on any link to the home page (the logo, Home in the
 * menu and the tab bar, the footer) while already on it closes any open sheet or drawer and glides back to the top
 * instead of reloading; keyboard focus goes to the top of the page. Ctrl, cmd, shift and middle clicks open as usual,
 * and so do links with a #section. Every sheet closes when another announces itself (hhl:sheet-open), so this
 * announces 'home'.
 */
(function () {
  var trim = function (path) { return path.replace(/\/+$/, '') || '/'; };

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target instanceof Element && event.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    var url;
    try { url = new URL(link.href, location.href); } catch (error) { return; }
    var home = trim(HHL.root());
    if (url.origin !== location.origin || url.hash || trim(url.pathname) !== home || trim(location.pathname) !== home) return;

    event.preventDefault();
    document.dispatchEvent(new CustomEvent('hhl:sheet-open', {detail: 'home'}));
    window.scrollTo({top: 0, behavior: HHL.mq.reducedMotion.matches ? 'auto' : 'smooth'});

    var main = document.getElementById('main');
    if (main) {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main.focus({preventScroll: true});
    }
  }, true);
})();

/* ==========================================================================
   Choose every option (snippets/product-form.liquid, snippets/product-sticky-form.liquid, custom.css "Choose every
   option"; owner 2026-10-20: no T-shirt in the cart without a size). On a product with several choices, the options
   start unchosen (the page marks the variant picker data-hhl-choose, and data-hhl-picked lists the options chosen
   already). Until every option is picked, the add buttons (under the options and in the sticky bar) say "Choose a
   size", and pressing one, or a Buy it now button, adds nothing: a short "Choose a size" eases in by the option and
   the page scrolls to it. A pick counts from the options, the sticky bar's menus or a click on the choice already
   marked. The theme re-renders the picker and buttons on each change; what's picked is kept here per form and put
   back each time.
   ========================================================================== */
(function () {
  var forms = {}; // form id -> {need: [positions], picked: [positions]}

  var pickerOf = function (id) { return document.querySelector('variant-picker[form-id="' + id + '"]'); };
  var missing = function (s) { return s.need.filter(function (p) { return s.picked.indexOf(p) === -1; }); };
  var textFor = function (id, position) {
    var picker = pickerOf(id);
    var option = picker && picker.querySelector('[data-hhl-option="' + position + '"]');
    return (option && option.getAttribute('data-hhl-choose-text')) || 'Choose an option';
  };
  var buttonsOf = function (id) {
    return Array.prototype.slice.call(document.querySelectorAll('#' + CSS.escape(id) + ' .product-form__add-button, .product-form__add-button[form="' + id + '"]'));
  };

  // Puts the form's state on the page: the picker's marks, the dropdowns' words, the add buttons' label
  var apply = function (id) {
    var s = forms[id];
    var picker = pickerOf(id);
    if (!s || !picker) return;
    var left = missing(s);
    picker.setAttribute('data-hhl-picked', s.picked.join(' '));
    picker.toggleAttribute('data-hhl-choose', left.length > 0);
    // Dropdowns (here and in the sticky bar): "Choose a size" until picked, then the picked choice
    s.need.forEach(function (position) {
      document.querySelectorAll('[form="' + id + '"][data-option-position="' + position + '"]').forEach(function (control) {
        var wrap = control.closest('.select-wrapper');
        var shown = wrap && wrap.querySelector('.select__selected-value');
        if (!shown) return;
        if (left.indexOf(position) !== -1) {
          if (!shown.hasAttribute('data-hhl-choosing')) shown.setAttribute('data-hhl-was', shown.innerHTML);
          shown.setAttribute('data-hhl-choosing', '');
          HHL.setText(shown, textFor(id, position));
        } else if (shown.hasAttribute('data-hhl-choosing') || shown.textContent.trim() === textFor(id, position)) {
          shown.removeAttribute('data-hhl-choosing');
          var chosen = wrap.querySelector('[role="option"][aria-selected="true"]');
          if (chosen) HHL.setText(shown, chosen.textContent.trim()); else shown.innerHTML = shown.getAttribute('data-hhl-was') || '';
        }
      });
    });
    buttonsOf(id).forEach(function (button) {
      if (left.length) {
        if (!button.hasAttribute('data-hhl-choosing')) button.setAttribute('data-hhl-was', button.innerHTML);
        button.setAttribute('data-hhl-choosing', '');
        HHL.setText(button, textFor(id, left[0]));
      } else if (button.hasAttribute('data-hhl-choosing')) {
        button.removeAttribute('data-hhl-choosing');
        button.innerHTML = button.getAttribute('data-hhl-was') || button.innerHTML;
      }
    });
    if (!left.length) {
      picker.querySelectorAll('[data-hhl-choose-prompt].is-shown').forEach(function (p) { p.classList.remove('is-shown'); });
    }
  };

  var setUp = function (picker) {
    var id = picker.getAttribute('form-id');
    if (!id || forms[id] || !picker.hasAttribute('data-hhl-choose')) return;
    var need = [];
    picker.querySelectorAll('[data-hhl-option]').forEach(function (option) {
      if (option.querySelector('[data-hhl-choose-prompt]')) need.push(option.getAttribute('data-hhl-option'));
    });
    if (!need.length) return;
    forms[id] = {need: need, picked: (picker.getAttribute('data-hhl-picked') || '').split(' ').filter(Boolean)};
    var form = document.getElementById(id);
    // The theme redraws the picker and buttons after each change (this listener runs after its own)
    if (form) form.addEventListener('product:rerender', function () { apply(id); });
    apply(id);
  };

  var pick = function (id, position) {
    var s = forms[id];
    if (!s || s.picked.indexOf(position) !== -1 || s.need.indexOf(position) === -1) return;
    s.picked.push(position);
    var picker = pickerOf(id);
    var prompt = picker && picker.querySelector('[data-hhl-option="' + position + '"] [data-hhl-choose-prompt]');
    if (prompt) prompt.classList.remove('is-shown');
    apply(id);
  };

  // "Choose a size" by each option still to pick, and the first one scrolled into view
  var ask = function (id) {
    var s = forms[id];
    var picker = pickerOf(id);
    if (!s || !picker) return;
    var first = null;
    missing(s).forEach(function (position) {
      var option = picker.querySelector('[data-hhl-option="' + position + '"]');
      var prompt = option && option.querySelector('[data-hhl-choose-prompt]');
      if (!prompt) return;
      HHL.setText(prompt, option.getAttribute('data-hhl-choose-text'));
      prompt.classList.remove('is-shown');
      prompt.getBoundingClientRect();
      prompt.classList.add('is-shown');
      if (!first) first = option;
    });
    if (first) {
      var box = first.getBoundingClientRect();
      if (box.top < 80 || box.bottom > window.innerHeight - 80) {
        first.scrollIntoView({block: 'center', behavior: HHL.mq.reducedMotion.matches ? 'auto' : 'smooth'});
      }
    }
  };

  // (A product form has an input named "id", so form.id is that input: the form's own id is its attribute)
  var formIdOf = function (element) {
    var form = element.form || element.closest('form');
    return element.getAttribute('form') || (form && form.getAttribute('id')) || '';
  };

  // A pick: an option's input or menu changing, or a click on the choice already marked (no change event then)
  document.addEventListener('change', function (event) {
    var control = event.target;
    if (!(control instanceof Element) || !control.hasAttribute('data-option-position')) return;
    pick(formIdOf(control), control.getAttribute('data-option-position'));
  }, true);

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var target = event.target;

    // Add to cart or Buy it now while something is still to pick: nothing is added
    var buy = target.closest('.product-form__add-button[data-hhl-choosing], .shopify-payment-button');
    if (buy) {
      var buyForm = formIdOf(buy.closest('.shopify-payment-button') ? buy.closest('form') || buy : buy);
      if (forms[buyForm] && missing(forms[buyForm]).length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        ask(buyForm);
        return;
      }
    }

    var control = null;
    var label = target.closest('label[for]');
    if (label) control = document.getElementById(label.getAttribute('for'));
    if (!control) {
      var item = target.closest('[data-hhl-chip-value], [role="option"], .hhl-chips__pick');
      var wrap = item && item.closest('[data-hhl-option], .select-wrapper');
      control = wrap && wrap.querySelector('[data-option-position]');
    }
    if (control && control.hasAttribute('data-option-position')) pick(formIdOf(control), control.getAttribute('data-option-position'));
  }, true);

  document.addEventListener('submit', function (event) {
    var id = event.target instanceof Element ? event.target.getAttribute('id') : '';
    if (forms[id] && missing(forms[id]).length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      ask(id);
    }
  }, true);

  // Pickers on the page now and any added later (the quick view's)
  document.querySelectorAll('variant-picker[data-hhl-choose]').forEach(setUp);
  new MutationObserver(function (records) {
    records.forEach(function (record) {
      record.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches('variant-picker[data-hhl-choose]')) setUp(node);
        else if (node.querySelector) node.querySelectorAll('variant-picker[data-hhl-choose]').forEach(setUp);
      });
    });
  }).observe(document.body, {childList: true, subtree: true});
})();

/* ==========================================================================
   Gift card amounts (snippets/hhl-gift-amounts.liquid, custom.css "Gift card amounts"; owner 2026-10-20): a few
   amounts as buttons and "Other amount" for the rest. A typed amount snaps to the nearest $5; one outside the card's
   range is named in the note (eased). The amount picks its variant: in the gift page's form (the hidden id), in the
   product page's own Amount menu (out of sight; the theme follows as usual), or in the quick add panel.
   ========================================================================== */
(function () {
  var chosenIn = {}; // product page: the amount chosen, by form, to mark again on the picker the theme redraws

  var setNote = function (box, text) {
    var note = box.querySelector('[data-hhl-gc-note]');
    if (!note) return;
    if (text) HHL.setText(note, text);
    box.classList.toggle('has-note', !!text);
  };

  var choose = function (box, amount) {
    var map = {};
    try { map = JSON.parse(box.getAttribute('data-map')) || {}; } catch (e) {}
    var id = map[String(amount)];
    if (!id) return false;
    box.querySelectorAll('.hhl-gc-amounts__preset').forEach(function (preset) {
      preset.setAttribute('aria-pressed', String(preset.getAttribute('data-amount') === String(amount)));
    });
    var mode = box.getAttribute('data-mode');
    if (mode === 'form') {
      var field = box.querySelector('[data-hhl-gc-id]');
      if (field) field.value = id;
    } else if (mode === 'picker') {
      // The theme redraws the picker after a change: what was chosen is put back on the new one (below)
      var form = box.closest('variant-picker');
      if (form) chosenIn[form.getAttribute('form-id')] = {amount: String(amount), typed: (box.querySelector('[data-hhl-gc-other]') || {}).value || ''};
      // The theme's own menu (out of sight): its choice, clicked, so the variant, price and button follow
      var option = box.closest('.product-form__option-selector');
      var name = '$' + Number(amount).toFixed(2);
      var item = option && Array.prototype.filter.call(option.querySelectorAll('[role="option"]'), function (el) {
        return el.textContent.trim() === name;
      })[0];
      if (item) item.click();
    } else if (mode === 'quick') {
      // The quick add panel chooses an option button; this one stands in for the amount
      var panel = box.closest('quick-variants');
      if (panel && panel._select) {
        var stand = document.createElement('button');
        stand.dataset.variantId = id;
        stand.dataset.name = '$' + amount;
        stand.dataset.price = HHL.money(amount * 100);
        stand.dataset.available = 'true';
        panel._select(stand);
      }
    }
    return true;
  };

  // Store analytics (hhl_gift_amount_chosen, listed above hhlTrack): where the amount was chosen, by the box's mode
  var PLACES = {form: 'gift_page', picker: 'product_page', quick: 'quick_add'};
  var track = function (box, amount, how, inRange) {
    hhlTrack('hhl_gift_amount_chosen', {
      amount: Number(amount),
      how: how,
      in_range: inRange,
      place: PLACES[box.getAttribute('data-mode')] || 'other'
    });
  };

  var typed = function (box, input) {
    var value = parseFloat(input.value);
    if (isNaN(value)) { setNote(box, ''); return; }
    var min = parseFloat(box.getAttribute('data-min'));
    var max = parseFloat(box.getAttribute('data-max'));
    if (value < min || value > max) {
      box.classList.add('is-wrong');
      setNote(box, 'Gift cards go from $' + min + ' to $' + max + '.');
      track(box, value, 'typed', false);
      return;
    }
    box.classList.remove('is-wrong');
    var snapped = Math.min(max, Math.max(min, Math.round(value / 5) * 5));
    if (snapped !== value) {
      input.value = snapped;
      setNote(box, 'Rounded to $' + snapped + ' (gift cards come in $5 steps).');
    } else {
      setNote(box, '');
    }
    if (choose(box, snapped)) track(box, snapped, 'typed', true);
  };

  document.addEventListener('click', function (event) {
    var preset = event.target instanceof Element && event.target.closest('.hhl-gc-amounts__preset');
    if (!preset) return;
    var box = preset.closest('[data-hhl-gc-amounts]');
    var other = box.querySelector('[data-hhl-gc-other]');
    if (other) other.value = '';
    box.classList.remove('is-wrong');
    setNote(box, '');
    if (choose(box, preset.getAttribute('data-amount'))) track(box, preset.getAttribute('data-amount'), 'button', true);
  });
  document.addEventListener('change', function (event) {
    if (!(event.target instanceof Element) || !event.target.matches('[data-hhl-gc-other]')) return;
    typed(event.target.closest('[data-hhl-gc-amounts]'), event.target);
  });
  // The product page's picker, drawn again by the theme after a change: its button (or typed amount) shows again
  new MutationObserver(function () {
    document.querySelectorAll('[data-hhl-gc-amounts][data-mode="picker"]').forEach(function (box) {
      var form = box.closest('variant-picker');
      var was = form && chosenIn[form.getAttribute('form-id')];
      if (!was) return;
      box.querySelectorAll('.hhl-gc-amounts__preset').forEach(function (preset) {
        var on = !was.typed && preset.getAttribute('data-amount') === was.amount;
        if ((preset.getAttribute('aria-pressed') === 'true') !== on) preset.setAttribute('aria-pressed', String(on));
      });
      var other = box.querySelector('[data-hhl-gc-other]');
      if (other && was.typed && other.value !== was.amount) other.value = was.amount;
    });
  }).observe(document.body, {childList: true, subtree: true});

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || !(event.target instanceof Element) || !event.target.matches('[data-hhl-gc-other]')) return;
    event.preventDefault(); // Enter sets the amount; it doesn't send the form
    typed(event.target.closest('[data-hhl-gc-amounts]'), event.target);
  });
})();

/* ==========================================================================
   Notify me from a tile (snippets/product-item.liquid, snippets/quick-variants.liquid; owner 2026-10-22): a sold-out
   tile's "Notify me" opens the product at that version with #notify, and here the back-in-stock form opens as soon
   as Doran's app has drawn its button (the same form as tapping it). Gives up quietly after a few seconds.
   ========================================================================== */
(function () {
  if (location.hash !== '#notify' || !document.querySelector('[data-dr="back-in-stock-inline-button"]')) return;
  var done = false;
  var open = function () {
    var button = document.querySelector('.product-form .dr-back-in-stock-inline-button') || document.querySelector('.dr-back-in-stock-inline-button');
    if (done || !button) return false;
    done = true;
    watch.disconnect();
    button.scrollIntoView({block: 'center', behavior: HHL.mq.reducedMotion.matches ? 'auto' : 'smooth'});
    button.click();
    history.replaceState(null, '', location.pathname + location.search);
    return true;
  };
  var watch = new MutationObserver(open);
  if (!open()) {
    watch.observe(document.body, {childList: true, subtree: true});
    setTimeout(function () { watch.disconnect(); }, 8000);
  }
})();

/* ==========================================================================
   Sold-out fold (snippets/product-form.liquid, custom.css "Sold-out fold"): the "Show 6 sold-out colors" row eases
   open and shut (it is a <details>, so without this script it still opens). One motion, its content wrapped as one
   piece: opening, the room opens and then the choices fade in; closing plays the same motion backwards (they fade
   out, then the room folds), and a tap in the middle turns it around from where it is. is-open follows the tap at
   once (the words and chevron); open stays set until the closing has finished.
   ========================================================================== */
(function () {
  var sync = function (details) { details.classList.toggle('is-open', details.open); };
  document.querySelectorAll('.hhl-soldout-stack').forEach(sync);
  // The theme redraws the picker after a choice: the new folds take their state from their open attribute
  new MutationObserver(function () {
    document.querySelectorAll('.hhl-soldout-stack:not(.is-open)[open], .hhl-soldout-stack.is-open:not([open])').forEach(function (details) {
      if (!details.hhlEasing) sync(details);
    });
  }).observe(document.body, {childList: true, subtree: true});

  document.addEventListener('click', function (event) {
    var summary = event.target instanceof Element && event.target.closest('.hhl-soldout-stack__toggle');
    if (!summary) return;
    var details = summary.parentElement;
    var body = details.querySelector('.hhl-soldout-stack__body');
    event.preventDefault();
    var opening = !details.classList.contains('is-open');
    details.classList.toggle('is-open', opening);

    if (!body || HHL.mq.reducedMotion.matches || typeof body.animate !== 'function') {
      details.open = opening;
      return;
    }
    // Mid-way: the same motion turns around from where it is
    if (details.hhlEasing) {
      details.hhlEasing.reverse();
      return;
    }
    if (opening) details.open = true;
    var full = body.getBoundingClientRect().height;
    // Each half eases on its own (the room, then the fade), so played backwards the fold eases shut as smoothly
    var run = details.hhlEasing = body.animate([
      {height: '0px', opacity: 0, easing: HHL.ease},
      {height: full + 'px', opacity: 0, offset: .55, easing: HHL.ease},
      {height: full + 'px', opacity: 1}
    ], {duration: 540 * HHL.motion(), fill: 'both'});
    if (!opening) run.reverse();
    run.onfinish = function () {
      details.hhlEasing = null;
      if (!details.classList.contains('is-open')) details.open = false;
      run.cancel(); // back to its own height (or hidden, closed)
    };
  });
})();

/* ==========================================================================
   Cart page goes to the side cart (layout/theme.liquid sends /cart back to the page it came from with #cart; owner
   2026-10-22: the cart page isn't used). #cart opens the side cart (the drawer, or the docked cart's highlight on
   wide screens, as the cart icon does) once it is ready, then leaves the address so a refresh doesn't reopen it. Any
   other link to the cart page (in apps or page text) opens the side cart the same way; the header's cart icons
   already do.
   ========================================================================== */
(function () {
  var open = function () {
    var drawer = document.getElementById('mini-cart');
    if (!drawer) return;
    var go = function () { requestAnimationFrame(function () { HHL.openCart(); }); };
    if (window.customElements && customElements.whenDefined) customElements.whenDefined('cart-drawer').then(go); else go();
  };
  if (location.hash === '#cart') {
    history.replaceState(history.state, '', location.pathname + location.search);
    open();
  }

  var cartPath = new URL(HHL.cartUrl(), location.origin).pathname;
  document.addEventListener('click', function (event) {
    var link = event.target instanceof Element && event.target.closest('a[href]');
    if (!link || link.hasAttribute('aria-controls') || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    var to;
    try { to = new URL(link.getAttribute('href'), location.href); } catch (e) { return; }
    if (to.origin !== location.origin || to.pathname !== cartPath || !document.getElementById('mini-cart')) return;
    event.preventDefault();
    open();
  });
})();

/* ==========================================================================
   Builder prices in the shopper's currency (owner 2026-10-25: build prices "should follow the user. Convert the
   currencies appropriately like with any other item"). A builder's "from" price can be in the shop's currency (the
   Builder directory's from_price, a block's Starting price: snippets/explore-builders.liquid), which Liquid can't
   convert. In another currency the page converts it as Shopify converts a price, times its rate to the currency's
   smallest unit (4.99 USD is 7.09 CAD), and shows the lower of that and the products' own prices.
   ========================================================================== */
(function () {
  var fill = function (scope) {
    var cur = window.Shopify && Shopify.currency;
    var rate = cur ? Number(cur.rate) : NaN;
    if (!(rate > 0) || !isFinite(rate)) return;
    var unit = 1;
    try {
      unit = new Intl.NumberFormat('en', {style: 'currency', currency: HHL.currency()}).resolvedOptions().maximumFractionDigits === 0 ? 100 : 1;
    } catch (error) {}
    (scope || document).querySelectorAll('[data-hhl-from-price]').forEach(function (el) {
      var shop = parseInt(el.getAttribute('data-hhl-shop-cents'), 10);
      var local = parseInt(el.getAttribute('data-hhl-local-cents'), 10);
      if (isNaN(shop)) return;
      var converted = Math.round((shop * rate) / unit) * unit;
      var amount = HHL.money(isNaN(local) ? converted : Math.min(converted, local));
      // A builder tile's price (snippets/hhl-builder-grid-tile.liquid) is a tile's price line, not a sentence
      HHL.setText(el, el.hasAttribute('data-hhl-from-plain') ? 'From ' + amount : ' From ' + amount + '.');
    });
  };

  fill();
  document.addEventListener('shopify:section:load', function (event) { fill(event.target); });
  // A collection opened in place brings its builder tile with it
  document.addEventListener('hhl:page-swapped', function (event) { fill(event.target); });
})();

/* ==========================================================================
   Builder tile in a collection grid (owner 2026-10-29; snippets/hhl-builder-grid-tile.liquid): opening it sends
   hhl_builder_tile_opened with the builder product's handle and the collection's.
   ========================================================================== */
(function () {
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-hhl-builder-tile] a[href]');
    if (!link) return;
    var tile = link.closest('[data-hhl-builder-tile]');
    var path = window.location.pathname.split('/collections/')[1] || '';
    hhlTrack('hhl_builder_tile_opened', {builder: tile.getAttribute('data-hhl-builder-tile'), collection: path.split('/')[0]});
  });
})();

/**
 * Collection hero "Shop all … parts" (sections/hhl-collection-hero.liquid): scrolls down to the product grid on the same
 * page (#facet-main, whose .anchor margin clears the sticky header). It doesn't change the address: an in-page #link
 * fires popstate, which the search module's Back/Forward handler answers with a full reload back at the top. Without
 * this script the link still leads to the collection.
 */
(function () {
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-hhl-scroll-to]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = document.querySelector(link.getAttribute('data-hhl-scroll-to'));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({block: 'start', behavior: HHL.mq.reducedMotion.matches ? 'auto' : 'smooth'});
  });
})();
